import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogFooter, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { handleUppercaseInput, initializeAutoUppercase } from "@/lib/utils";
import DTCManagementSection from "@/components/DTCManagementSection";
import WorkHoldManager from "@/components/WorkHoldManager";
import TimeTracker from "@/components/TimeTracker";
import JobCardStatusWorkflow from "@/components/JobCardStatusWorkflow";
import DatabaseBackupSettings from "@/components/DatabaseBackupSettings";
import AddVehicleDataForm from "@/components/forms/AddVehicleDataForm";
import SimpleAddPartForm from "@/components/forms/SimpleAddPartForm";
import MasterPartForm from "@/components/forms/MasterPartForm";
import VehicleCascadeDropdowns from "@/components/VehicleCascadeDropdowns";
import { 
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package, 
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3, Printer, 
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown, CalendarClock, Building2
} from "lucide-react";
import { BACKEND_URL, API, apiFetch } from "@/lib/api";
import SupplierPayPODialog from "@/components/SupplierPayPODialog";
import SupplierAgingReport from "@/components/SupplierAgingReport";import GarageExpenseStatement from "@/components/GarageExpenseStatement";import { AuthContext, useAuth, AuthProvider } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import { toLocalISODate } from "@/lib/dateUtils";
import { exportRowsToCSV } from "@/lib/csvExport";
import { AccountingSection, ReportsSection } from "@/pages/AccountingSection";
import { LedgerStatement } from "@/pages/AccountingSection";
import { InvoicesSection } from "@/pages/Invoices";
import { QuotationsSection } from "@/pages/Quotations";
import { JobCardsTestSection } from "@/pages/JobCards";
import { PurchaseOrdersSection } from "@/pages/PurchaseOrders";
import { LeaveAndToolsSection } from "@/pages/LeaveAndTools";
import { AppointmentsSection } from "@/pages/Appointments";
import { AddServiceForm } from "@/pages/shared/ItemForms";
import { PlatformGaragesSection } from "@/pages/PlatformGarages";


// Authentication Context
// Authentication Components
const LoginForm = () => {
  const [loginFormData, setLoginFormData] = useState({
    garage_code: '',
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    if (sessionStorage.getItem('session_expired')) {
      sessionStorage.removeItem('session_expired');
      setSessionExpired(true);
      toast.error('Your session expired, please sign in again');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(loginFormData.username, loginFormData.password, loginFormData.garage_code);
    
    if (result.success) {
      toast.success('Login successful!');
    } else {
      toast.error(result.message);
    }
    
    setIsLoading(false);
  };

  if (showRegister) {
    return <RegisterForm onBackToLogin={() => setShowRegister(false)} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="Garage Logo" 
              className="h-16 w-16 mb-4"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Garage System
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please sign in to your account
          </p>
        </div>
        {sessionExpired && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 text-center"
            data-testid="session-expired-message"
          >
            Your session expired, please sign in again
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="garage_code">Garage Code</Label>
              <Input
                id="garage_code"
                type="text"
                data-testid="login-garage-code-input"
                value={loginFormData.garage_code}
                onChange={(e) => setLoginFormData({...loginFormData, garage_code: e.target.value})}
                placeholder="e.g. MAIN"
                className="mt-1 uppercase"
              />
              <p className="mt-1 text-xs text-gray-500">Leave blank only for the sales-demo login.</p>
            </div>
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                data-testid="login-username-input"
                value={loginFormData.username}
                onChange={(e) => setLoginFormData({...loginFormData, username: e.target.value})}
                required
                placeholder="Enter your username"
                className="mt-1 no-uppercase"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={loginFormData.password}
                onChange={(e) => setLoginFormData({...loginFormData, password: e.target.value})}
                required
                placeholder="Enter your password"
                className="mt-1"
              />
            </div>
          </div>

          <div className="space-y-4">
            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
            
            {/* Account creation moved to Settings section */}
          </div>

          {/* Admin info removed for security */}
        </form>
      </div>
    </div>
  );
};

const RegisterForm = ({ onBackToLogin }) => {
  const [registerFormData, setRegisterFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    role: 'normal_user'
  });
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (registerFormData.password !== registerFormData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    
    const result = await register({
      username: registerFormData.username,
      email: registerFormData.email,
      full_name: registerFormData.full_name,
      password: registerFormData.password,
      role: registerFormData.role
    });
    
    if (result.success) {
      toast.success('Registration successful! Please login.');
      onBackToLogin();
    } else {
      toast.error(result.message);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="Garage Logo" 
              className="h-16 w-16 mb-4"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join Garage System
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                type="text"
                value={registerFormData.full_name}
                onChange={(e) => setRegisterFormData({...registerFormData, full_name: e.target.value})}
                required
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={registerFormData.username}
                onChange={(e) => setRegisterFormData({...registerFormData, username: e.target.value})}
                required
                placeholder="Choose a username"
                className="no-uppercase"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={registerFormData.email}
                onChange={(e) => setRegisterFormData({...registerFormData, email: e.target.value})}
                required
                placeholder="Enter your email"
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select 
                value={registerFormData.role} 
                onValueChange={(value) => setRegisterFormData({...registerFormData, role: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal_user">Technician</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                  <SelectItem value="basic_user">Basic User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={registerFormData.password}
                onChange={(e) => setRegisterFormData({...registerFormData, password: e.target.value})}
                required
                placeholder="Create a password"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={registerFormData.confirmPassword}
                onChange={(e) => setRegisterFormData({...registerFormData, confirmPassword: e.target.value})}
                required
                placeholder="Confirm your password"
              />
            </div>
          </div>

          <div className="space-y-4">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={onBackToLogin}
              className="w-full"
            >
              Back to Login
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Settings Section Component
const SettingsSection = () => {
  const { user, hasPermission, register } = useAuth();
  const [showCreateUser, setShowCreateUser] = useState(false);

  // Only show to management and super_admin
  if (!hasPermission(['management', 'super_admin'])) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <Shield className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h3>
          <p className="text-gray-600">You don't have permission to access Settings.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage system settings and user accounts</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Management Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <UserPlus className="h-5 w-5 mr-2 text-blue-600" />
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600 text-sm">
              Create new user accounts for technicians, management, or other staff members.
            </p>
            
            {!showCreateUser ? (
              <Button 
                onClick={() => setShowCreateUser(true)}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create New User Account
              </Button>
            ) : (
              <CreateUserForm 
                onSuccess={() => setShowCreateUser(false)} 
                onCancel={() => setShowCreateUser(false)} 
              />
            )}
          </CardContent>
        </Card>

        {/* System Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2 text-gray-600" />
              System Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current User:</span>
                <span className="font-medium">{user?.username}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Role:</span>
                <span className="font-medium capitalize">{user?.role?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Full Name:</span>
                <span className="font-medium">{user?.full_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{user?.email}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {user?.role === 'super_admin' && (
          <div className="lg:col-span-2">
            <DatabaseBackupSettings />
          </div>
        )}

        <div className="lg:col-span-2">
          <GSTSettingsCard />
        </div>
      </div>
    </div>
  );
};

// GST Settings Card — garage's own GSTIN/state used as the seller side of every invoice
const GSTSettingsCard = () => {
  const [gst, setGst] = useState({ gstin: '', business_name: '', phone: '', email: '', state_name: '', state_code: '', gst_enabled_default: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch(`${API}/settings/gst`);
        if (res.ok) {
          const data = await res.json();
          setGst({
            gstin: data.gstin || '',
            business_name: data.business_name || '',
            phone: data.phone || '',
            email: data.email || '',
            state_name: data.state_name || '',
            state_code: data.state_code || '',
            gst_enabled_default: data.gst_enabled_default !== false
          });
        }
      } catch (e) {
        console.error('Failed to load GST settings', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/settings/gst`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gst)
      });
      if (!res.ok) throw new Error('Failed to save');
      toast.success('GST settings saved');
    } catch (e) {
      toast.error('Failed to save GST settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="gst-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileText className="h-5 w-5 mr-2 text-orange-600" />
          GST Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-600 text-sm">
          Your garage's GST registration details — used as the seller side on every invoice, and to decide CGST+SGST (same state) vs IGST (different state).
        </p>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gst-gstin">Garage GSTIN</Label>
                <Input id="gst-gstin" data-testid="gst-settings-gstin-input" value={gst.gstin}
                  onChange={(e) => setGst({ ...gst, gstin: e.target.value })} placeholder="32XXXXXXXXXXXZX" />
              </div>
              <div>
                <Label htmlFor="gst-business-name">Business Name</Label>
                <Input id="gst-business-name" data-testid="gst-settings-business-name-input" value={gst.business_name}
                  onChange={(e) => setGst({ ...gst, business_name: e.target.value })} placeholder="Garage" />
              </div>
              <div>
                <Label htmlFor="gst-state-name">State</Label>
                <Input id="gst-state-name" data-testid="gst-settings-state-input" value={gst.state_name}
                  onChange={(e) => setGst({ ...gst, state_name: e.target.value })} placeholder="Kerala" />
              </div>
              <div>
                <Label htmlFor="gst-state-code">State Code</Label>
                <Input id="gst-state-code" data-testid="gst-settings-state-code-input" value={gst.state_code}
                  onChange={(e) => setGst({ ...gst, state_code: e.target.value })} placeholder="32" maxLength={2} />
              </div>
              <div>
                <Label htmlFor="gst-phone">Garage Phone (shown on job cards)</Label>
                <Input id="gst-phone" data-testid="gst-settings-phone-input" value={gst.phone}
                  onChange={(e) => setGst({ ...gst, phone: e.target.value })} placeholder="+91 90000 00000" />
              </div>
              <div>
                <Label htmlFor="gst-email">Garage Email (shown on job cards)</Label>
                <Input id="gst-email" data-testid="gst-settings-email-input" value={gst.email}
                  onChange={(e) => setGst({ ...gst, email: e.target.value })} placeholder="contact@yourgarage.com" />
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-4">
              <div>
                <Label htmlFor="gst-enabled-default">Apply GST by default on new invoices</Label>
                <p className="text-xs text-gray-500">Can still be toggled off per-invoice.</p>
              </div>
              <Switch id="gst-enabled-default" data-testid="gst-settings-enabled-switch"
                checked={gst.gst_enabled_default}
                onCheckedChange={(v) => setGst({ ...gst, gst_enabled_default: v })} />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="gst-settings-save-button">
              {saving ? 'Saving...' : 'Save GST Settings'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Create User Form Component  
const CreateUserForm = ({ onSuccess, onCancel }) => {
  const { register: authRegister } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Create User Form using React Hook Form
  const { register, handleSubmit, control, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      username: '',
      email: '',
      full_name: '',
      password: '',
      confirmPassword: '',
      role: 'normal_user'
    }
  });

  // Watch password for confirmation validation
  const watchPassword = watch("password");

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setIsLoading(true);
    
    const result = await authRegister({
      username: data.username,
      email: data.email,
      full_name: data.full_name,
      password: data.password,
      role: data.role
    });

    setIsLoading(false);

    if (result.success) {
      toast.success('User account created successfully!');
      reset(); // Reset form using React Hook Form
      onSuccess();
    } else {
      toast.error(result.message || 'Failed to create user account');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label htmlFor="username">Username *</Label>
          <Input
            id="username"
            type="text"
            {...register("username", { 
              required: "Username is required",
              minLength: { value: 3, message: "Username must be at least 3 characters" },
              pattern: { value: /^[a-zA-Z0-9_]+$/, message: "Username can only contain letters, numbers, and underscores" }
            })}
            className={`no-uppercase ${errors.username ? 'border-red-500' : ''}`}
            placeholder="Enter username"
          />
          {errors.username && (
            <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            type="text"
            {...register("full_name", { 
              required: "Full name is required",
              minLength: { value: 2, message: "Full name must be at least 2 characters" }
            })}
            className={errors.full_name ? 'border-red-500' : ''}
            placeholder="Enter full name"
          />
          {errors.full_name && (
            <p className="text-red-500 text-sm mt-1">{errors.full_name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            {...register("email", { 
              required: "Email is required",
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Please enter a valid email address" }
            })}
            className={errors.email ? 'border-red-500' : ''}
            placeholder="Enter email address"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="role">Role *</Label>
          <Controller
            control={control}
            name="role"
            rules={{ required: "Role is required" }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className={errors.role ? 'border-red-500' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal_user">Normal User/Technician</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="basic_user">Basic User</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {errors.role && (
            <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="password">Password *</Label>
          <Input
            id="password"
            type="password"
            {...register("password", { 
              required: "Password is required",
              minLength: { value: 6, message: "Password must be at least 6 characters" },
              pattern: { 
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message: "Password must contain at least one uppercase letter, one lowercase letter, and one number"
              }
            })}
            className={`no-uppercase ${errors.password ? 'border-red-500' : ''}`}
            placeholder="Enter secure password"
          />
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="confirmPassword">Confirm Password *</Label>
          <Input
            id="confirmPassword"
            type="password"
            {...register("confirmPassword", { 
              required: "Please confirm your password",
              validate: value => value === watchPassword || "Passwords don't match"
            })}
            className={`no-uppercase ${errors.confirmPassword ? 'border-red-500' : ''}`}
            placeholder="Confirm password"
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>
      </div>

      <div className="flex space-x-2 pt-4">
        <Button type="submit" disabled={isLoading} className="flex-1 bg-green-600 hover:bg-green-700">
          {isLoading ? 'Creating...' : 'Create Account'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
      </div>
    </form>
  );
};

// User Management Section (for super_admin only)
const UserManagementSection = () => {
  const { hasPermission, register } = useAuth();
  const [users, setUsers] = useState([]);
  const [employeeMap, setEmployeeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  if (!hasPermission(['super_admin'])) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <Shield className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Super Admin Only</h3>
          <p className="text-gray-600">This section is restricted to Super Administrators only.</p>
        </Card>
      </div>
    );
  }

  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeMap = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      const map = {};
      (response.data || []).forEach((emp) => { map[emp.id] = emp.name; });
      setEmployeeMap(map);
    } catch (error) {
      console.error('Error fetching employees for linkage:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchEmployeeMap();
  }, []);

  // Handle user creation
  const handleCreateUser = () => {
    setIsCreateUserModalOpen(true);
  };

  const handleUserCreated = () => {
    setIsCreateUserModalOpen(false);
    fetchUsers();
  };

  // Handle user editing
  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleUserUpdated = () => {
    setIsEditUserModalOpen(false);
    setEditingUser(null);
    fetchUsers();
  };

  // Handle user deletion
  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success('User deleted successfully!');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  // Handle role change
  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success('User role updated successfully!');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  // Handle account status toggle
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}/status`, { is_active: !currentStatus });
      toast.success(`User account ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-800';
      case 'management': return 'bg-blue-100 text-blue-800';
      case 'normal_user': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'management': return 'Management';
      case 'normal_user': return 'Normal User';
      case 'basic_user': return 'Basic User';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage all user accounts and permissions</p>
        </div>
        <Button onClick={handleCreateUser} className="bg-green-600 hover:bg-green-700">
          <UserPlus className="h-4 w-4 mr-2" />
          Create New User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Super Admins</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'super_admin').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Settings className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Management</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'management').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Normal Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'normal_user').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search users by name, username, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="no-uppercase"
              />
            </div>
            <div className="w-48">
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                  <SelectItem value="normal_user">Normal User</SelectItem>
                  <SelectItem value="basic_user">Basic User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Linked Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">@{user.username}</div>
                        {user.email && <div className="text-sm text-gray-500">{user.email}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Select 
                        value={user.role} 
                        onValueChange={(value) => handleRoleChange(user.id, value)}
                        disabled={user.role === 'super_admin' && users.filter(u => u.role === 'super_admin').length === 1}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal_user">Normal User</SelectItem>
                          <SelectItem value="management">Management</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                          <SelectItem value="basic_user">Basic User</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.employee_id && employeeMap[user.employee_id] ? (
                        <Badge className="bg-purple-100 text-purple-800" data-testid={`user-linked-employee-${user.id}`}>
                          {employeeMap[user.employee_id]}
                        </Badge>
                      ) : (
                        <span className="text-xs text-gray-400" data-testid={`user-not-linked-${user.id}`}>Not linked</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleStatus(user.id, user.is_active)}
                        className={user.is_active 
                          ? "text-green-600 border-green-600 hover:bg-green-50" 
                          : "text-red-600 border-red-600 hover:bg-red-50"
                        }
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                          disabled={user.role === 'super_admin' && users.filter(u => u.role === 'super_admin').length === 1}
                        >
                          <Trash className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No users found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {filteredUsers.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      )}

      {/* Create User Modal */}
      <Dialog open={isCreateUserModalOpen} onOpenChange={setIsCreateUserModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user account to the system with appropriate role and permissions.
            </DialogDescription>
          </DialogHeader>
          <CreateUserForm onSuccess={handleUserCreated} onCancel={() => setIsCreateUserModalOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={isEditUserModalOpen} onOpenChange={setIsEditUserModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user account details and permissions.
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <EditUserForm 
              user={editingUser} 
              onSuccess={handleUserUpdated} 
              onCancel={() => setIsEditUserModalOpen(false)} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edit User Form Component
const EditUserForm = ({ user, onSuccess, onCancel }) => {
  const [editUserFormData, setEditUserFormData] = useState({
    username: user.username || '',
    email: user.email || '',
    full_name: user.full_name || '',
    role: user.role || 'normal_user',
    is_active: user.is_active !== false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.put(`${API}/users/${user.id}`, {
        username: editUserFormData.username,
        email: editUserFormData.email,
        full_name: editUserFormData.full_name,
        role: editUserFormData.role,
        is_active: editUserFormData.is_active
      });

      toast.success('User updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Edit User</h2>
        <p className="text-gray-600">Update user information and permissions</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={editUserFormData.username}
              onChange={(e) => setEditUserFormData({...editUserFormData, username: e.target.value})}
              required
              className="no-uppercase"
            />
          </div>

          <div>
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              type="text"
              value={editUserFormData.full_name}
              onChange={(e) => setEditUserFormData({...editUserFormData, full_name: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={editUserFormData.email}
              onChange={(e) => setEditUserFormData({...editUserFormData, email: e.target.value})}
            />
          </div>

          <div>
            <Label htmlFor="role">Role</Label>
            <Select value={editUserFormData.role} onValueChange={(value) => setEditUserFormData({...editUserFormData, role: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal_user">Normal User/Technician</SelectItem>
                <SelectItem value="management">Management</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="basic_user">Basic User</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={editUserFormData.is_active}
                onChange={(e) => setEditUserFormData({...editUserFormData, is_active: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <Label htmlFor="is_active">Account Active</Label>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Inactive users cannot log in to the system
            </p>
          </div>
        </div>

        <div className="flex space-x-2 pt-4">
          <Button type="submit" disabled={isLoading} className="flex-1 bg-blue-600 hover:bg-blue-700">
            {isLoading ? 'Updating...' : 'Update User'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

const MainWindow = () => {
  const { user, logout, hasPermission } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [garageProfile, setGarageProfile] = useState(null);

  useEffect(() => {
    axios.get(`${API}/garage/profile`).then((res) => setGarageProfile(res.data)).catch(() => {});
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Set up global refresh functions for job card creation
  useEffect(() => {
    window.refreshVehicles = fetchVehicles;
    window.refreshCustomers = fetchCustomers;
    
    return () => {
      delete window.refreshVehicles;
      delete window.refreshCustomers;
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVehicles(), fetchCustomers(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleVehicleAdded = (newVehicle = null) => {
    fetchVehicles();
    fetchStats();
    setIsAddModalOpen(false);
    // If newVehicle data is passed, we could potentially use it for optimization
    // but for now, we'll stick with the refetch approach for consistency
  };

  const filteredVehicles = vehicles;

  // Role-based navigation items
  const getAllSidebarItems = () => [
    { id: 'dashboard', label: 'Dashboard', icon: Home, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    { id: 'jobcards-test', label: 'Job Cards', icon: Clipboard, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    { id: 'issues', label: 'Vehicle Problems', icon: AlertTriangle, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    { id: 'dtc-management', label: 'DTC Codes', icon: Code, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    { id: 'parts', label: 'Parts & Inventory', icon: Package, roles: ['basic_user', 'management', 'super_admin'] },
    { id: 'suppliers', label: 'Suppliers', icon: Truck, roles: ['management', 'super_admin'] },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: CreditCard, roles: ['management', 'super_admin'] },
    { id: 'vehicle-specs', label: 'Specifications', icon: Database, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    { id: 'services', label: 'Services', icon: Cog, roles: ['basic_user', 'management', 'super_admin'] },
    { id: 'customers', label: 'Customers', icon: Users, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    // Management and Super Admin only
    { id: 'vehicles', label: 'Vehicles', icon: Car, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    { id: 'garage', label: 'Garage', icon: Wrench, roles: ['management', 'super_admin'] },
    { id: 'employees', label: 'Employees', icon: Users, roles: ['management', 'super_admin'] },
    { id: 'leave-tools', label: 'Leave & Tools', icon: CalendarClock, roles: ['basic_user', 'normal_user', 'management', 'super_admin'] },
    { id: 'salary', label: 'Salary', icon: DollarSign, roles: ['management', 'super_admin'] },
    { id: 'invoices', label: 'Invoices', icon: FileText, roles: ['management', 'super_admin'] },
    { id: 'reports', label: 'Reports', icon: BarChart3, roles: ['management', 'super_admin'] },
    { id: 'accounting', label: 'Accounting', icon: Calculator, roles: ['management', 'super_admin'] },
    { id: 'quotations', label: 'Quotations', icon: FileText, roles: ['management', 'super_admin'] },
    { id: 'appointments', label: 'Appointments', icon: Calendar, roles: ['management', 'super_admin'] },
    { id: 'settings', label: 'Settings', icon: Settings, roles: ['management', 'super_admin'] },
    // Super Admin only
    { id: 'users', label: 'User Management', icon: Shield, roles: ['super_admin'] },
    { id: 'platform-garages', label: 'Garages', icon: Building2, roles: ['super_admin'] },
  ];

  // Early return with loading state if user is not ready
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const sidebarItems = getAllSidebarItems()
    .filter(item => item && item.roles && item.roles.includes(user?.role || 'normal_user'))
    .filter(item => item.id !== 'platform-garages' || user?.is_platform_owner)
    .filter(item => item.id && item.label && item.icon); // Additional safety check

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/logo.png" 
            alt="Garage Logo" 
            className="h-20 w-20 mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Garage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`bg-white shadow-lg transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div className="p-4">
          <div className="flex items-center space-x-3 mb-8">
            <div className="bg-white p-2 rounded-lg shadow-md">
              <img 
                src="/logo.png" 
                alt="Garage Logo" 
                className="h-8 w-8"
              />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="text-xl font-bold text-gray-900" data-testid="app-brand-name">
                  {user?.is_demo_account ? 'Demo Garage' : (garageProfile?.name || 'Garage')}
                </h1>
                <p className="text-xs text-gray-600">
                  {user?.is_demo_account ? 'Sales Demo' : (garageProfile?.garage_code ? `Code: ${garageProfile.garage_code}` : 'Management System')}
                </p>
              </div>
            )}
          </div>

          <nav className="space-y-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedSection(item.id)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                  selectedSection === item.id 
                    ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <item.icon className="h-5 w-5" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Header Bar */}
        <header className="bg-white border-b border-gray-200 shadow-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100"
                data-testid="sidebar-toggle-button"
              >
                <Menu className="h-5 w-5 text-gray-600" />
              </button>
              {user?.is_demo_account && (
                <span
                  className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold tracking-wide"
                  data-testid="demo-mode-banner"
                >
                  DEMO MODE — sample data only
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                <div className="text-xs text-gray-500 capitalize">
                  {user.role === 'super_admin' ? 'Super Admin' : 
                   user.role === 'management' ? 'Management' : 
                   user.role === 'basic_user' ? 'Basic User' : 'Technician'}
                </div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="text-gray-600 hover:text-red-600"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Main Dashboard Content */}
        <main className="flex-1 p-6">
          {selectedSection === 'dashboard' && (
            <>
              {/* Quick Stats Row */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
                  <Card data-testid="total-vehicles-card" className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-2xl font-bold">{stats?.total_vehicles || 0}</div>
                          <div className="text-sm opacity-90">Total Vehicles</div>
                        </div>
                        <Car className="h-8 w-8 opacity-80" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  {Object.entries(stats?.status_counts || {}).map(([status, count]) => {
                    const config = statusConfig[status] || { label: "Unknown", icon: Package, color: "bg-gray-100" };
                    return (
                      <Card key={status} className="bg-white hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xl font-semibold text-gray-900">{count}</div>
                              <div className="text-xs text-gray-600">{config.label}</div>
                            </div>
                            <config.icon className="h-6 w-6 text-gray-400" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Quick Action Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <Plus className="h-8 w-8" />
                      <div>
                        <div className="text-lg font-semibold">New Vehicle</div>
                        <div className="text-sm opacity-90">Add a new vehicle</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <Calendar className="h-8 w-8" />
                      <div>
                        <div className="text-lg font-semibold">Schedule Service</div>
                        <div className="text-sm opacity-90">Book appointment</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <Users className="h-8 w-8" />
                      <div>
                        <div className="text-lg font-semibold">Customers</div>
                        <div className="text-sm opacity-90">Manage customers</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <BarChart3 className="h-8 w-8" />
                      <div>
                        <div className="text-lg font-semibold">Reports</div>
                        <div className="text-sm opacity-90">View analytics</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Current Vehicles Section */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold text-gray-900">Current Vehicles</h2>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm">
                      <Archive className="h-4 w-4 mr-2" />
                      View All
                    </Button>
                  </div>
                </div>
                
                {filteredVehicles.length === 0 ? (
                  <Card className="bg-white">
                    <CardContent className="p-8 text-center">
                      <Car className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-gray-900 mb-2">
                        No vehicles in garage
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Get started by adding your first vehicle to track.
                      </p>
                      <Button 
                        data-testid="add-first-vehicle-btn" 
                        onClick={() => setIsAddModalOpen(true)} 
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Vehicle
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVehicles.map((vehicle) => (
                      <VehicleCard key={vehicle.id} vehicle={vehicle} onUpdate={handleVehicleAdded} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Vehicle Problems Section */}
          {selectedSection === 'issues' && (
            <VehicleIssuesSection vehicles={vehicles} onUpdate={handleVehicleAdded} />
          )}

          {/* DTC Management Section */}
          {selectedSection === 'dtc-management' && (
            <DTCManagementSection />
          )}

          {/* Services Section */}
          {selectedSection === 'services' && (
            <ServicesSection onUpdate={handleVehicleAdded} />
          )}

          {/* Garage Section */}
          {selectedSection === 'garage' && (
            <GarageSection onUpdate={handleVehicleAdded} />
          )}

          {/* Employees Section */}
          {selectedSection === 'employees' && (
            <EmployeesSection onUpdate={handleVehicleAdded} />
          )}

          {/* Leave & Tools Section */}
          {selectedSection === 'leave-tools' && (
            <LeaveAndToolsSection />
          )}

          {/* Salary Section */}
          {selectedSection === 'salary' && (
            <SalarySection onUpdate={handleVehicleAdded} />
          )}

          {/* Job Cards Test Section */}
          {/* Job Cards Test Section */}
          {selectedSection === 'jobcards-test' && (
            <JobCardsTestSection onUpdate={handleVehicleAdded} />
          )}

          {/* Parts Section */}
          {selectedSection === 'parts' && user?.role !== 'normal_user' && (
            <PartsInventorySection onUpdate={handleVehicleAdded} />
          )}

          {/* Suppliers Section */}
          {selectedSection === 'suppliers' && (
            <SuppliersSection onUpdate={handleVehicleAdded} />
          )}

          {/* Purchase Orders Section */}
          {selectedSection === 'purchase-orders' && (
            <PurchaseOrdersSection onUpdate={handleVehicleAdded} />
          )}

          {/* Parts Section (legacy support) */}
          {selectedSection === 'inventory' && user?.role !== 'normal_user' && (
            <PartsInventorySection onUpdate={handleVehicleAdded} />
          )}

          {/* Vehicle Specifications Section */}
          {selectedSection === 'vehicle-specs' && (
            <VehicleSpecificationsSection onUpdate={handleVehicleAdded} />
          )}

          {/* Appointments Section */}
          {selectedSection === 'appointments' && (
            <AppointmentsSection onUpdate={handleVehicleAdded} />
          )}

          {/* Quotations Section */}
          {selectedSection === 'quotations' && (
            <QuotationsSection onUpdate={handleVehicleAdded} />
          )}

          {/* Invoices Section */}
          {selectedSection === 'invoices' && (
            <InvoicesSection onUpdate={handleVehicleAdded} />
          )}

          {/* Accounting Section */}
          {selectedSection === 'accounting' && (
            <AccountingSection onUpdate={handleVehicleAdded} />
          )}

          {/* Reports Section */}
          {selectedSection === 'reports' && (
            <ReportsSection />
          )}

          {/* Customers Section */}
          {selectedSection === 'customers' && (
            <CustomersSection onUpdate={handleVehicleAdded} />
          )}

          {/* Vehicles Section */}
          {selectedSection === 'vehicles' && (
            <VehiclesSection onUpdate={handleVehicleAdded} />
          )}

          {/* Settings Section */}
          {selectedSection === 'settings' && (
            <SettingsSection />
          )}

          {/* User Management Section */}
          {selectedSection === 'users' && (
            <UserManagementSection />
          )}

          {/* Platform Garages Section */}
          {selectedSection === 'platform-garages' && (
            <PlatformGaragesSection />
          )}

          {/* Placeholder sections for other menu items */}
          {!['dashboard', 'issues', 'dtc-management', 'services', 'garage', 'employees', 'leave-tools', 'salary', 'jobcards-test', 'parts', 'suppliers', 'purchase-orders', 'vehicle-specs', 'invoices', 'customers', 'vehicles', 'reports', 'accounting', 'quotations', 'appointments', 'settings', 'users', 'platform-garages'].includes(selectedSection) && (
            <div className="flex items-center justify-center h-96">
              <Card className="p-8 text-center">
                <div className="text-gray-400 mb-4">
                  {sidebarItems.find(item => item.id === selectedSection)?.icon && 
                    React.createElement(sidebarItems.find(item => item.id === selectedSection).icon, {className: "h-16 w-16 mx-auto"})
                  }
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {sidebarItems.find(item => item.id === selectedSection)?.label || 'Section'}
                </h3>
                <p className="text-gray-600 mb-4">This section is coming soon!</p>
                <Button variant="outline" onClick={() => setSelectedSection('dashboard')}>
                  Back to Dashboard
                </Button>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const VehicleIssuesSection = ({ vehicles, onUpdate }) => {
  const [enhancedIssues, setEnhancedIssues] = useState([]);
  const [dtcCodes, setDtcCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddIssueModalOpen, setIsAddIssueModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);

  // Fetch enhanced issues
  const fetchEnhancedIssues = async () => {
    try {
      const response = await axios.get(`${API}/vehicle-issues`);
      setEnhancedIssues(response.data);
    } catch (error) {
      console.error("Error fetching enhanced issues:", error);
      setEnhancedIssues([]);
    }
  };

  // Fetch DTC codes
  const fetchDtcCodes = async () => {
    try {
      const response = await axios.get(`${API}/dtc-codes`);
      setDtcCodes(response.data);
    } catch (error) {
      console.error("Error fetching DTC codes:", error);
      setDtcCodes([]);
    }
  };

  // Handle issue deletion
  const handleDeleteIssue = async (issueId, issueTitle) => {
    if (window.confirm(`Are you sure you want to delete the issue "${issueTitle}"?\n\nThis action cannot be undone.`)) {
      try {
        const response = await axios.delete(`${API}/vehicle-issues/${issueId}`);
        if (response.status === 200) {
          await fetchEnhancedIssues(); // Refresh the list
        }
      } catch (error) {
        console.error('Error deleting issue:', error);
        alert('Failed to delete issue. Please try again.');
      }
    }
  };

  // Handle edit modal opening
  const handleEditIssue = (issue) => {
    setEditingIssue(issue);
    setIsEditModalOpen(true);
  };

  // Handle successful issue update
  const handleIssueUpdated = () => {
    setIsEditModalOpen(false);
    setEditingIssue(null);
    fetchEnhancedIssues(); // Refresh the list
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEnhancedIssues(), fetchDtcCodes()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Filter issues
  const filteredIssues = enhancedIssues.filter(issue => {
    const matchesSeverity = filterSeverity === 'all' || issue.severity === filterSeverity;
    const matchesStatus = filterStatus === 'all' || issue.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || issue.category === filterCategory;
    const matchesSearch = searchTerm === '' || 
      issue.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.symptoms?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.dtc_codes?.some(dtc => dtc.code?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSeverity && matchesStatus && matchesCategory && matchesSearch;
  });

  // Statistics
  const issueStats = {
    total: enhancedIssues.length,
    solved: enhancedIssues.filter(i => i.is_solved).length,
    open: enhancedIssues.filter(i => !i.is_solved).length,
    critical: enhancedIssues.filter(i => i.severity === 'critical').length,
    high: enhancedIssues.filter(i => i.severity === 'high').length,
    totalCost: enhancedIssues.reduce((sum, i) => sum + (i.total_cost || 0), 0)
  };

  const severityConfig = {
    low: { color: "bg-gray-100 text-gray-800", label: "Low" },
    medium: { color: "bg-blue-100 text-blue-800", label: "Medium" },
    high: { color: "bg-orange-100 text-orange-800", label: "High" },
    critical: { color: "bg-red-100 text-red-800", label: "Critical" }
  };

  const issueStatusConfig = {
    open: { color: "bg-red-100 text-red-800", icon: AlertCircle, label: "Open" },
    "in-progress": { color: "bg-yellow-100 text-yellow-800", icon: Clock, label: "In Progress" },
    resolved: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Resolved" },
    deferred: { color: "bg-gray-100 text-gray-800", icon: Clock, label: "Deferred" }
  };

  const categoryIcons = {
    engine: Wrench,
    transmission: Settings,
    electrical: Zap,
    brakes: AlertCircle,
    suspension: Car,
    general: Package
  };

  const getVehicleInfo = (issueOrVehicleId) => {
    // Backwards compatible: accept either a string vehicleId or the full issue object
    const isIssueObj = issueOrVehicleId && typeof issueOrVehicleId === 'object';
    const vehicleId = isIssueObj ? issueOrVehicleId.vehicle_id : issueOrVehicleId;
    const issue = isIssueObj ? issueOrVehicleId : null;

    const vehicle = vehicleId ? vehicles.find(v => v.id === vehicleId) : null;
    if (vehicle) {
      return `${vehicle.make || ''} ${vehicle.model || ''} (${vehicle.license_plate || 'No Plate'})`.trim();
    }
    // Fallback: use issue's own embedded vehicle fields (added in Enhanced Add Issue Form)
    if (issue) {
      const make = issue.vehicle_make || '';
      const model = issue.vehicle_model || '';
      const series = issue.vehicle_series || '';
      const year = issue.vehicle_year || '';
      const combined = [year, make, model, series].filter(Boolean).join(' ').trim();
      if (combined) return combined;
    }
    return 'Unknown Vehicle';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading enhanced vehicle issues...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            🔧 Enhanced Vehicle Problems Database
          </h2>
          <p className="text-gray-600">
            Comprehensive diagnostic tracking with DTC codes, photos, and solution database
          </p>
        </div>
        <Button 
          onClick={() => setIsAddIssueModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Problem
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{issueStats.total}</div>
                <div className="text-sm opacity-90">Total Problems</div>
              </div>
              <AlertTriangle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{issueStats.solved}</div>
                <div className="text-sm opacity-90">Solved</div>
              </div>
              <CheckCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{issueStats.open}</div>
                <div className="text-sm opacity-90">Open Problems</div>
              </div>
              <AlertCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{issueStats.critical}</div>
                <div className="text-sm opacity-90">Critical</div>
              </div>
              <AlertTriangle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{dtcCodes.length}</div>
                <div className="text-sm opacity-90">DTC Codes</div>
              </div>
              <FileText className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{issueStats.totalCost.toFixed(0)}</div>
                <div className="text-sm opacity-90">Total Cost</div>
              </div>
              <Package className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Search Problems</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by title, DTC code, symptoms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 no-uppercase"
                />
              </div>
            </div>

            <div>
              <Label>Status Filter</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="deferred">Deferred</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Severity Filter</Label>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Category Filter</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="engine">Engine</SelectItem>
                  <SelectItem value="transmission">Transmission</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="brakes">Brakes</SelectItem>
                  <SelectItem value="suspension">Suspension</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Problems List */}
      <div className="space-y-4">
        {filteredIssues.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Problems Found</h3>
              <p className="text-gray-600">
                {enhancedIssues.length === 0 
                  ? "No vehicle issues have been recorded yet."
                  : "No issues match your current search and filter criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredIssues.map((issue) => {
            const severityInfo = severityConfig[issue.severity] || { color: "bg-gray-100 text-gray-800", label: "Unknown" };
            const statusInfo = issueStatusConfig[issue.status] || { color: "bg-gray-100 text-gray-800", label: "Unknown" };
            const CategoryIcon = categoryIcons[issue.category?.toLowerCase()] || Package;

            return (
              <Card key={issue.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <CategoryIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{issue.title}</h3>
                        <p className="text-sm text-gray-600">{issue.category} Problem • {getVehicleInfo(issue)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Badge className={`${severityInfo.color} text-xs`}>
                        {severityInfo.label}
                      </Badge>
                      <Badge className={`${statusInfo.color} text-xs`}>
                        {statusInfo.label}
                      </Badge>
                      {issue.is_solved ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          🟢 Solved
                        </Badge>
                      ) : issue.status === 'in-progress' ? (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">
                          🟡 Working on Solution
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 text-xs">
                          🔴 Unsolved
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Problem Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Symptoms</p>
                      <p className="text-sm text-gray-600">{issue.symptoms || 'No symptoms recorded'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">DTC Codes</p>
                      <div className="flex flex-wrap gap-1">
                        {issue.dtc_codes?.slice(0, 3).map((dtc, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {dtc.code}
                          </Badge>
                        ))}
                        {issue.dtc_codes?.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{issue.dtc_codes.length - 3} more
                          </Badge>
                        )}
                        {(!issue.dtc_codes || issue.dtc_codes.length === 0) && (
                          <span className="text-xs text-gray-500">No DTC codes</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Cost</p>
                      <div className="text-sm text-gray-600">
                        {issue.total_cost ? `₹${issue.total_cost.toFixed(2)}` : 
                         issue.estimated_cost ? `Est: ₹${issue.estimated_cost.toFixed(2)}` : 'No cost data'}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-700 mb-4">{issue.description}</p>

                  {/* Solution Info — show for both 'solved' (green) and 'in-progress' (amber) */}
                  {(issue.is_solved || issue.status === 'in-progress') && issue.solution_description && (
                    <div className={`${issue.is_solved ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-3 mb-4`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircle className={`h-4 w-4 ${issue.is_solved ? 'text-green-600' : 'text-amber-600'}`} />
                        <h4 className={`font-medium ${issue.is_solved ? 'text-green-900' : 'text-amber-900'}`}>
                          {issue.is_solved ? 'Solution Applied' : 'Work-in-Progress Notes'}
                        </h4>
                      </div>
                      <p className={`${issue.is_solved ? 'text-green-800' : 'text-amber-800'} text-sm`}>{issue.solution_description}</p>
                      {issue.remedy_used && (
                        <p className={`${issue.is_solved ? 'text-green-700' : 'text-amber-700'} text-xs mt-1`}>
                          Remedy: {issue.remedy_used}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedIssue(issue);
                        setIsViewModalOpen(true);
                      }}
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditIssue(issue)}
                      className="text-green-600 border-green-600 hover:bg-green-50"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteIssue(issue.id, issue.title)}
                      className="text-red-600 border-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                    
                    {issue.scanning_report_filename && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          window.open(`${API}/vehicle-issues/${issue.id}/download-report`, '_blank');
                        }}
                        className="text-purple-600 border-purple-600 hover:bg-purple-50"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Report
                      </Button>
                    )}

                    {issue.photos && issue.photos.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-orange-600 border-orange-600 hover:bg-orange-50"
                      >
                        <Camera className="h-4 w-4 mr-1" />
                        {issue.photos.length} Photos
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* View Problem Modal */}
      {selectedIssue && (
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Problem Details: {selectedIssue.title}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Problem Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium">Vehicle:</p>
                      <p>{getVehicleInfo(selectedIssue)}</p>
                    </div>
                    <div>
                      <p className="font-medium">Status:</p>
                      <Badge className={issueStatusConfig[selectedIssue.status]?.color || "bg-gray-100"}>
                        {selectedIssue.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="font-medium">Severity:</p>
                      <Badge className={severityConfig[selectedIssue.severity]?.color || "bg-gray-100"}>
                        {selectedIssue.severity}
                      </Badge>
                    </div>
                    <div>
                      <p className="font-medium">Category:</p>
                      <p>{selectedIssue.category}</p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <p className="font-medium mb-2">Description:</p>
                    <p className="text-gray-700">{selectedIssue.description}</p>
                  </div>
                  
                  {selectedIssue.symptoms && (
                    <div className="mt-4">
                      <p className="font-medium mb-2">Symptoms:</p>
                      <p className="text-gray-700">{selectedIssue.symptoms}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* DTC Codes */}
              {selectedIssue.dtc_codes && selectedIssue.dtc_codes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Diagnostic Trouble Codes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedIssue.dtc_codes.map((dtc, index) => (
                        <div key={dtc.code || `dtc-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="font-medium">{dtc.code}</span>
                            <p className="text-sm text-gray-600">{dtc.description}</p>
                          </div>
                          <Badge variant="outline">{dtc.system}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Solution (if solved) */}
              {selectedIssue.is_solved && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg text-green-700">Solution Applied</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedIssue.solution_description && (
                        <div>
                          <p className="font-medium mb-2">Solution Description:</p>
                          <p className="text-gray-700">{selectedIssue.solution_description}</p>
                        </div>
                      )}
                      
                      {selectedIssue.remedy_used && (
                        <div>
                          <p className="font-medium mb-2">Remedy Used:</p>
                          <p className="text-gray-700">{selectedIssue.remedy_used}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {selectedIssue.labor_hours && (
                          <div>
                            <p className="font-medium">Labor Hours:</p>
                            <p>{selectedIssue.labor_hours}</p>
                          </div>
                        )}
                        
                        {selectedIssue.total_cost && (
                          <div>
                            <p className="font-medium">Total Cost:</p>
                            <p>₹{selectedIssue.total_cost.toFixed(2)}</p>
                          </div>
                        )}
                        
                        {selectedIssue.solved_by && (
                          <div>
                            <p className="font-medium">Solved By:</p>
                            <p>{selectedIssue.solved_by}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Scanning Report */}
            {selectedIssue.scanning_report_filename && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Diagnostic Scanning Report</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 bg-blue-50 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">{selectedIssue.scanning_report_filename}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                      onClick={() => window.open(`${API}/vehicle-issues/${selectedIssue.id}/download-report`, '_blank')}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Photos */}
            {((selectedIssue.photos && selectedIssue.photos.length > 0) || selectedIssue.photo_filenames) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Vehicle Photos</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedIssue.photos && selectedIssue.photos.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedIssue.photos.map((photo, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden bg-gray-50">
                          <img
                            src={`${API}/vehicle-issues/${selectedIssue.id}/photos/${index}`}
                            alt={photo.filename}
                            className="w-full h-32 object-cover cursor-pointer hover:opacity-90"
                            onClick={() => window.open(`${API}/vehicle-issues/${selectedIssue.id}/photos/${index}`, '_blank')}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="p-2 flex items-center justify-between">
                            <span className="text-xs text-gray-600 truncate">{photo.filename}</span>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-green-600" onClick={() => window.open(`${API}/vehicle-issues/${selectedIssue.id}/photos/${index}`, '_blank')}>
                              <Download className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedIssue.photo_filenames.split(',').filter(f => f.trim()).map((filename, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <Camera className="h-5 w-5 text-green-600" />
                            <span className="text-sm font-medium text-green-800">{filename.trim()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Videos */}
            {((selectedIssue.videos && selectedIssue.videos.length > 0) || selectedIssue.video_filenames) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Vehicle Videos</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedIssue.videos && selectedIssue.videos.length > 0 ? (
                    <div className="space-y-3">
                      {selectedIssue.videos.map((video, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden bg-purple-50">
                          <video controls className="w-full max-h-64" preload="metadata">
                            <source src={`${API}/vehicle-issues/${selectedIssue.id}/videos/${index}`} type="video/mp4" />
                            Your browser does not support video playback.
                          </video>
                          <div className="p-3 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-purple-800">{video.filename}</p>
                              <p className="text-xs text-gray-500">
                                {video.size_bytes ? `${(video.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}
                                {video.uploaded_at ? ` · ${new Date(video.uploaded_at).toLocaleDateString()}` : ''}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300"
                              onClick={() => window.open(`${API}/vehicle-issues/${selectedIssue.id}/videos/${index}`, '_blank')}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : selectedIssue.video_filenames ? (
                    <div className="space-y-2">
                      {selectedIssue.video_filenames.split(',').filter(f => f.trim()).map((filename, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-purple-600" />
                            <span className="text-sm font-medium text-purple-800">{filename.trim()}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300"
                            onClick={() => window.open(`${API}/vehicle-issues/${selectedIssue.id}/videos/${idx}`, '_blank')}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Problem Modal */}
      <Dialog open={isAddIssueModalOpen} onOpenChange={setIsAddIssueModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Enhanced Vehicle Problem</DialogTitle>
            <DialogDescription>
              Record a detailed vehicle problem with diagnostic codes, symptoms, and technical specifications.
            </DialogDescription>
          </DialogHeader>
          
          <EnhancedAddIssueForm
            vehicles={vehicles}
            dtcCodes={dtcCodes}
            onIssueAdded={() => {
              setIsAddIssueModalOpen(false);
              fetchEnhancedIssues();
            }}
            onCancel={() => setIsAddIssueModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Problem Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Vehicle Problem</DialogTitle>
            <DialogDescription>
              Modify vehicle problem details, update diagnostic codes, and track resolution progress.
            </DialogDescription>
          </DialogHeader>
          
          {editingIssue && (
            <EditIssueForm
              issue={editingIssue}
              vehicles={vehicles}
              onIssueUpdated={handleIssueUpdated}
              onCancel={() => setIsEditModalOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Job Card View Component
const GarageSection = ({ onUpdate }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [showExpenseStatement, setShowExpenseStatement] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isEditExpenseModalOpen, setIsEditExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Load expenses from backend API
  const loadExpenses = async () => {
    try {
      console.log('Loading expenses from backend API...');
      const response = await apiFetch(`${backendUrl}/api/expenses`);
      
      if (response.ok) {
        const expensesData = await response.json();
        console.log('Expenses loaded from backend:', expensesData);
        setExpenses(expensesData || []);
        
        // Save to localStorage as backup
        localStorage.setItem('garage_expenses', JSON.stringify(expensesData));
      } else {
        console.error('Failed to load expenses from backend:', response.status);
        loadExpensesFromLocalStorage();
      }
    } catch (error) {
      console.error('Error loading expenses from backend:', error);
      loadExpensesFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  // Fallback to localStorage if backend fails
  const loadExpensesFromLocalStorage = () => {
    try {
      const storedExpenses = localStorage.getItem('garage_expenses');
      if (storedExpenses) {
        const expenseData = JSON.parse(storedExpenses);
        console.log('Using localStorage fallback for expenses');
        setExpenses(expenseData || []);
      } else {
        console.log('No expenses found, initializing with empty array');
        setExpenses([]);
      }
    } catch (error) {
      console.error('Error loading expenses from localStorage:', error);
      setExpenses([]);
    }
  };

  // Load expenses when component mounts
  useEffect(() => {
    loadExpenses();
  }, []);

  // Save expenses to localStorage whenever expenses change
  useEffect(() => {
    if (expenses.length > 0) {
      localStorage.setItem('garage_expenses', JSON.stringify(expenses));
    }
  }, [expenses]);

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'No date specified';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date format';
      }
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Date error';
    }
  };

  // Helper function to safely display field values
  const safeDisplay = (value, fallback = 'Not specified') => {
    return value && value.trim() !== '' ? value : fallback;
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || expense.status === filterStatus;
    
    // Date range filter
    let matchesDateRange = true;
    if (filterDateRange !== 'all') {
      try {
        const expenseDate = new Date(expense.date);
        // Check if date is valid before proceeding
        if (!isNaN(expenseDate.getTime())) {
          const today = new Date();
          const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          
          switch (filterDateRange) {
            case 'today':
              matchesDateRange = expenseDate.toDateString() === today.toDateString();
              break;
            case 'week':
              matchesDateRange = expenseDate >= sevenDaysAgo;
              break;
            case 'month':
              matchesDateRange = expenseDate >= thirtyDaysAgo;
              break;
            default:
              matchesDateRange = true;
          }
        } else {
          // If date is invalid, exclude from date-based filters but include for 'all'
          matchesDateRange = false;
        }
      } catch (error) {
        console.error('Error processing expense date for filtering:', error);
        matchesDateRange = false;
      }
    }
    
    const matchesSearch = searchTerm === '' || 
      expense.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.expense_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesStatus && matchesDateRange && matchesSearch;
  });

  // Statistics
  const expenseStats = {
    total: expenses.length,
    paid: expenses.filter(e => e.status === 'paid').length,
    pending: expenses.filter(e => e.status === 'pending').length,
    totalAmount: expenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    paidAmount: expenses.filter(e => e.status === 'paid').reduce((sum, e) => sum + (e.amount || 0), 0),
    pendingAmount: expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0),
    monthlyExpenses: expenses.filter(e => {
      try {
        const expenseDate = new Date(e.date);
        // Only include expenses with valid dates
        if (isNaN(expenseDate.getTime())) return false;
        
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        return expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear;
      } catch (error) {
        console.error('Error processing date for monthly expenses:', error);
        return false;
      }
    }).reduce((sum, e) => sum + (e.amount || 0), 0)
  };

  const categoryConfig = {
    "tools": { icon: Wrench, label: "Tools", color: "bg-blue-100 text-blue-800" },
    "parts": { icon: Package, label: "Parts", color: "bg-blue-100 text-blue-800" },
    "operational": { icon: Settings, label: "Operational", color: "bg-cyan-100 text-cyan-800" },
    "employee": { icon: Users, label: "Employee / Salary", color: "bg-pink-100 text-pink-800" },
    "supplies": { icon: Package, label: "Supplies", color: "bg-teal-100 text-teal-800" },
    "equipment": { icon: Cog, label: "Equipment", color: "bg-purple-100 text-purple-800" },
    "office_supplies": { icon: FileText, label: "Office Supplies", color: "bg-green-100 text-green-800" },
    "utilities": { icon: Zap, label: "Utilities", color: "bg-yellow-100 text-yellow-800" },
    "maintenance": { icon: Settings, label: "Maintenance", color: "bg-orange-100 text-orange-800" },
    "software": { icon: Settings2, label: "Software", color: "bg-indigo-100 text-indigo-800" },
    "rent": { icon: Home, label: "Rent", color: "bg-red-100 text-red-800" },
    "other": { icon: Package, label: "Other", color: "bg-gray-100 text-gray-800" }
  };

  const expenseStatusConfig = {
    "paid": { color: "bg-green-100 text-green-800", label: "Paid" },
    "completed": { color: "bg-green-100 text-green-800", label: "Paid" },
    "pending": { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
    "cancelled": { color: "bg-red-100 text-red-800", label: "Cancelled" }
  };

  const handleAddExpense = () => {
    setIsAddExpenseModalOpen(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setIsEditExpenseModalOpen(true);
  };

  const handleExpenseAdded = async (newExpense) => {
    // The AddExpenseForm already created the expense via
    // POST /api/accounting/transactions (which writes to accounting_transactions
    // AND processes the cash/bank/credit payment). All we need to do here is
    // refresh the list. Posting again to /api/expenses created a duplicate row
    // in the legacy `db.expenses` collection, which then showed up twice in the
    // merged GET /api/expenses list. (Cash was only deducted once because only
    // the first endpoint processes payments.)
    try {
      await loadExpenses();
    } finally {
      setIsAddExpenseModalOpen(false);
      if (onUpdate) onUpdate();
    }
  };

  const handleExpenseUpdated = async (updatedExpense) => {
    try {
      console.log('Updating expense via API...');
      
      const response = await apiFetch(`${backendUrl}/api/expenses/${updatedExpense.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedExpense),
      });

      if (response.ok) {
        const updatedExpenseData = await response.json();
        console.log('Expense updated successfully:', updatedExpenseData);
        
        // Reload expenses from backend to get updated list
        await loadExpenses();
        setIsEditExpenseModalOpen(false);
        setEditingExpense(null);
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json();
        console.error('Failed to update expense:', errorData);
        alert('Failed to update expense. Please try again.');
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      
      // Fallback to localStorage update if backend fails
      const updatedExpenses = expenses.map(e => 
        e.id === updatedExpense.id ? updatedExpense : e
      );
      setExpenses(updatedExpenses);
      localStorage.setItem('garage_expenses', JSON.stringify(updatedExpenses));
      setIsEditExpenseModalOpen(false);
      setEditingExpense(null);
      if (onUpdate) onUpdate();
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense record?")) return;
    
    try {
      console.log('Deleting expense via API...');
      
      const response = await apiFetch(`${backendUrl}/api/expenses/${expenseId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        console.log('Expense deleted successfully');
        
        // Reload expenses from backend to get updated list
        await loadExpenses();
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json();
        console.error('Failed to delete expense:', errorData);
        alert('Failed to delete expense. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      
      // Fallback to localStorage delete if backend fails
      const updatedExpenses = expenses.filter(e => e.id !== expenseId);
      setExpenses(updatedExpenses);
      localStorage.setItem('garage_expenses', JSON.stringify(updatedExpenses));
      if (onUpdate) onUpdate();
    }
  };

  const handleStatusChange = (expenseId, newStatus) => {
    setExpenses(expenses.map(e => 
      e.id === expenseId ? { ...e, status: newStatus } : e
    ));
    if (onUpdate) onUpdate();
  };

  return (
    <div>
      {/* Garage Expenses Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Garage Expenses</h2>
          <p className="text-gray-600">Track and manage garage operational expenses, tool purchases, and equipment costs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowExpenseStatement(true)}
            variant="outline"
            data-testid="expense-statement-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Full Statement
          </Button>
          <Button 
            onClick={handleAddExpense}
            className="bg-green-600 hover:bg-green-700"
            data-testid="add-expense-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{expenseStats.total}</div>
                <div className="text-sm opacity-90">Total Expenses</div>
              </div>
              <FileText className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{expenseStats.paid}</div>
                <div className="text-sm opacity-90">Paid</div>
              </div>
              <CheckCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{expenseStats.pending}</div>
                <div className="text-sm opacity-90">Pending</div>
              </div>
              <Clock className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{(expenseStats.totalAmount || 0).toLocaleString('en-IN')}</div>
                <div className="text-sm opacity-90">Total Amount</div>
              </div>
              <DollarSign className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{(expenseStats.paidAmount || 0).toLocaleString('en-IN')}</div>
                <div className="text-sm opacity-90">Paid Amount</div>
              </div>
              <CheckCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{(expenseStats.pendingAmount || 0).toLocaleString('en-IN')}</div>
                <div className="text-sm opacity-90">Pending Amount</div>
              </div>
              <Clock className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{(expenseStats.monthlyExpenses || 0).toLocaleString('en-IN')}</div>
                <div className="text-sm opacity-90">This Month</div>
              </div>
              <Calendar className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label>Search Expenses</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by title, vendor, expense ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 no-uppercase"
                />
              </div>
            </div>

            <div>
              <Label>Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="tools">Tools</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="office_supplies">Office Supplies</SelectItem>
                  <SelectItem value="utilities">Utilities</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="rent">Rent</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date Range</Label>
              <Select value={filterDateRange} onValueChange={setFilterDateRange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expenses List */}
      <div className="space-y-4">
        {filteredExpenses.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Wrench className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Expenses Found</h3>
              <p className="text-gray-600">
                {expenses.length === 0 
                  ? "No garage expenses recorded yet."
                  : "No expenses match your current filters. Try adjusting your search criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredExpenses.map((expense) => {
            const categoryInfo = categoryConfig[expense.category] || { icon: Package, label: "Unknown", color: "bg-gray-100 text-gray-800" };
            const statusInfo = expenseStatusConfig[expense.status] || { color: "bg-gray-100 text-gray-800", label: "Unknown" };
            
            return (
              <Card key={expense.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <categoryInfo.icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-gray-900">{expense.title}</h3>
                            <span className="text-sm text-gray-500">({expense.expense_id})</span>
                          </div>
                          <p className="text-sm text-gray-600">{expense.description}</p>
                        </div>
                        <Badge className={`${categoryInfo.color} text-xs`}>
                          {categoryInfo.label}
                        </Badge>
                        <Badge className={`${statusInfo.color} text-xs`}>
                          {statusInfo.label}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Amount</p>
                          <p className="text-lg font-semibold text-green-600">₹{(expense.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Date</p>
                          <p className="text-sm text-gray-600">{formatDate(expense.date)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Vendor</p>
                          <p className="text-sm text-gray-600">{safeDisplay(expense.vendor, 'Unknown vendor')}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Payment Method</p>
                          <p className="text-sm text-gray-600">{safeDisplay(expense.payment_method, 'Unknown method')}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Receipt #</p>
                          <p className="text-sm text-gray-600">{safeDisplay(expense.receipt_number, 'No receipt number')}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Created by: {expense.created_by}</span>
                        <span>Date: {formatDate(expense.created_at)}</span>
                        {expense.notes && expense.notes.length > 0 && <span>Notes: {expense.notes.substring(0, 50)}...</span>}
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      {expense.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusChange(expense.id, 'paid')}
                          className="text-green-600 border-green-600 hover:bg-green-50"
                        >
                          Mark Paid
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditExpense(expense)}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        Edit
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteExpense(expense.id)}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Summary */}
      {filteredExpenses.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600">
          Showing {filteredExpenses.length} of {expenses.length} expenses
        </div>
      )}

      {/* Add Expense Modal */}
      <Dialog open={isAddExpenseModalOpen} onOpenChange={setIsAddExpenseModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <AddExpenseForm onExpenseAdded={handleExpenseAdded} />
        </DialogContent>
      </Dialog>

      {/* Edit Expense Modal */}
      <Dialog open={isEditExpenseModalOpen} onOpenChange={setIsEditExpenseModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {editingExpense && (
            <EditExpenseForm 
              expense={editingExpense} 
              onExpenseUpdated={handleExpenseUpdated} 
            />
          )}
        </DialogContent>
      </Dialog>

      <GarageExpenseStatement
        open={showExpenseStatement}
        onOpenChange={setShowExpenseStatement}
        expenses={expenses}
      />
    </div>
  );
};

const EmployeesSection = ({ onUpdate }) => {
  const [employees, setEmployees] = useState([]);
  const [loginMap, setLoginMap] = useState({});
  const [loginDialogEmployee, setLoginDialogEmployee] = useState(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Fallback to localStorage if backend fails
  const loadEmployeesFromLocalStorage = useCallback(() => {
    try {
      const storedEmployees = localStorage.getItem('garage_employees');
      if (storedEmployees) {
        const employeeData = JSON.parse(storedEmployees);
        console.log('Using localStorage fallback for employees');
        setEmployees(employeeData || []);
      } else {
        console.log('No employees found, initializing with empty array');
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error loading employees from localStorage:', error);
      setEmployees([]);
    }
  }, []);

  // Load employees from backend API
  const loadEmployees = useCallback(async () => {
    try {
      console.log('Loading employees from backend API...');
      const response = await apiFetch(`${backendUrl}/api/employees`);
      
      if (response.ok) {
        const employeesData = await response.json();
        console.log('Employees loaded from backend:', employeesData);
        setEmployees(employeesData || []);
        
        // Save to localStorage as backup
        localStorage.setItem('garage_employees', JSON.stringify(employeesData));
      } else {
        console.error('Failed to load employees from backend:', response.status);
        loadEmployeesFromLocalStorage();
      }
    } catch (error) {
      console.error('Error loading employees from backend:', error);
      loadEmployeesFromLocalStorage();
    }
  }, [backendUrl, loadEmployeesFromLocalStorage]);

  // Load employees when component mounts
  useEffect(() => {
    loadEmployees();
    axios.get(`${backendUrl}/api/employees/login-map`)
      .then((res) => setLoginMap(res.data || {}))
      .catch((error) => console.error('Error loading employee login map:', error));
  }, [loadEmployees, backendUrl]);
  
  const [loading, setLoading] = useState(false);
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [isEditEmployeeModalOpen, setIsEditEmployeeModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Timesheet / monthly work log
  const [timesheetEmployee, setTimesheetEmployee] = useState(null);
  const [timesheetMonth, setTimesheetMonth] = useState(() => toLocalISODate(new Date()).slice(0, 7));
  const [timesheetData, setTimesheetData] = useState(null);
  const [timesheetLoading, setTimesheetLoading] = useState(false);

  const fetchTimesheet = useCallback(async (employeeId, month) => {
    setTimesheetLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/api/employees/${employeeId}/timesheet?month=${month}`);
      setTimesheetData(res.data);
    } catch (e) {
      console.error('Error loading timesheet:', e);
      toast.error('Failed to load timesheet');
      setTimesheetData(null);
    } finally {
      setTimesheetLoading(false);
    }
  }, [backendUrl]);

  const printTimesheet = () => {
    if (!timesheetData || !timesheetEmployee) return;
    const fmtTime = (iso) => {
      if (!iso) return '-';
      const d = new Date(iso);
      return isNaN(d.getTime()) ? '-' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    const escapeHtml = (str) => (str || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const rows = timesheetData.entries.map(en => `
      <tr>
        <td>${en.date}</td>
        <td>${en.job_number || ''}</td>
        <td>${[en.customer_name, en.vehicle].filter(Boolean).join(' · ')}</td>
        <td>${fmtTime(en.start_time)}</td>
        <td>${fmtTime(en.end_time)}</td>
        <td style="text-align:right">${(en.worked_minutes / 60).toFixed(2)}h</td>
        <td style="text-align:right">${(en.billable_minutes / 60).toFixed(2)}h</td>
        <td>${escapeHtml(en.notes)}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Timesheet - ${timesheetEmployee.name} - ${timesheetData.period}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        p { margin: 2px 0; color: #555; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f3f4f6; }
        .totals { display: flex; gap: 24px; margin-top: 16px; font-size: 13px; }
      </style></head>
      <body>
        <h1>Timesheet — ${timesheetEmployee.name}</h1>
        <p>Position: ${timesheetEmployee.position || '-'} | Period: ${timesheetData.period}</p>
        <div class="totals">
          <div><strong>Billable Hours:</strong> ${timesheetData.totals.billable_hours}h</div>
          <div><strong>Worked Hours:</strong> ${timesheetData.totals.worked_hours}h</div>
          <div><strong>Jobs:</strong> ${timesheetData.totals.jobs_count}</div>
          <div><strong>Days Worked:</strong> ${timesheetData.totals.days_count}</div>
        </div>
        <table>
          <thead><tr><th>Date</th><th>Job</th><th>Customer / Vehicle</th><th>Start</th><th>End</th><th>Worked</th><th>Billable</th><th>Notes</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="8">No work logged in this period.</td></tr>'}</tbody>
        </table>
      </body></html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Please allow popups to print the timesheet'); return; }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  const downloadTimesheetCSV = () => {
    if (!timesheetData || !timesheetEmployee) return;
    const fmtTime = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    const escapeCsv = (val) => {
      const str = (val === null || val === undefined) ? '' : String(val);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = ['Date', 'Job Number', 'Customer', 'Vehicle', 'Start', 'End', 'Worked (hrs)', 'Billable (hrs)', 'Activity', 'Notes'];
    const rows = timesheetData.entries.map((en) => [
      en.date, en.job_number || '', en.customer_name || '', en.vehicle || '',
      fmtTime(en.start_time), fmtTime(en.end_time),
      (en.worked_minutes / 60).toFixed(2), (en.billable_minutes / 60).toFixed(2),
      en.activity_type || '', en.notes || ''
    ]);
    const csvLines = [header, ...rows].map((row) => row.map(escapeCsv).join(','));
    csvLines.push('');
    csvLines.push(`Totals:,,,,,,${timesheetData.totals.worked_hours},${timesheetData.totals.billable_hours}`);
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `timesheet_${timesheetEmployee.name.replace(/\s+/g, '_')}_${timesheetData.period}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Timesheet CSV downloaded');
  };

  const openTimesheet = (employee) => {
    const m = toLocalISODate(new Date()).slice(0, 7);
    setTimesheetEmployee(employee);
    setTimesheetMonth(m);
    setTimesheetData(null);
    fetchTimesheet(employee.id, m);
  };

  // Filter employees for display
  const filteredEmployees = employees.filter(employee => {
    if (!employee || !employee.name) return false;
    
    const matchesDepartment = filterDepartment === 'all' || employee.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || employee.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      (employee.name && employee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employee.employee_id && employee.employee_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employee.position && employee.position.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesDepartment && matchesStatus && matchesSearch;
  });

  // Statistics based on all employees
  const employeeStats = {
    total: employees.length,
    active: employees.filter(e => e && e.status === 'active').length,
    service: employees.filter(e => e && e.department === 'service').length,
    frontOffice: employees.filter(e => e && e.department === 'front_office').length,
    parts: employees.filter(e => e && e.department === 'parts').length,
    admin: employees.filter(e => e && e.department === 'admin').length,
    totalSalaryBudget: employees.reduce((sum, e) => sum + (e && e.salary ? e.salary : 0), 0)
  };

  const departmentConfig = {
    service: { icon: Wrench, label: "Service", color: "bg-blue-100 text-blue-800" },
    front_office: { icon: Users, label: "Front Office", color: "bg-green-100 text-green-800" },
    parts: { icon: Package, label: "Parts", color: "bg-yellow-100 text-yellow-800" },
    admin: { icon: FileText, label: "Admin", color: "bg-purple-100 text-purple-800" }
  };

  const handleAddEmployee = () => {
    setIsAddEmployeeModalOpen(true);
  };

  const handleEmployeeAdded = async (createdEmployee) => {
    // Employee was already created by the form, just reload the list
    console.log('Employee added, reloading list:', createdEmployee);
    await loadEmployees();
    setIsAddEmployeeModalOpen(false);
    if (onUpdate) onUpdate();
  };

  const handleEmployeeUpdated = async (updatedEmployee) => {
    try {
      console.log('Updating employee via API...');
      
      const response = await apiFetch(`${backendUrl}/api/employees/${updatedEmployee.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedEmployee),
      });

      if (response.ok) {
        const updatedEmployeeData = await response.json();
        console.log('Employee updated successfully:', updatedEmployeeData);
        
        // Reload employees from backend to get updated list
        await loadEmployees();
        setIsEditEmployeeModalOpen(false);
        setEditingEmployee(null);
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json();
        console.error('Failed to update employee:', errorData);
        alert('Failed to update employee. Please try again.');
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      
      // Fallback to localStorage update if backend fails
      const updatedEmployees = employees.map(e => 
        e.id === updatedEmployee.id ? updatedEmployee : e
      );
      setEmployees(updatedEmployees);
      localStorage.setItem('garage_employees', JSON.stringify(updatedEmployees));
      setIsEditEmployeeModalOpen(false);
      setEditingEmployee(null);
      if (onUpdate) onUpdate();
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    
    try {
      await axios.delete(`${backendUrl}/api/employees/${employeeId}`);
      setEmployees(employees.filter(e => e.id !== employeeId));
      toast.success("Employee deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Failed to delete employee");
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setIsEditEmployeeModalOpen(true);
  };

  const handleToggleStatus = (employeeId) => {
    setEmployees(employees.map(e => 
      e.id === employeeId 
        ? { ...e, status: e.status === 'active' ? 'inactive' : 'active' }
        : e
    ));
    if (onUpdate) onUpdate();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Employee Management</h2>
          <p className="text-gray-600">Manage garage staff, payroll information, and employee records</p>
        </div>
        <Button 
          onClick={handleAddEmployee}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-employee-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900">{employeeStats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{employeeStats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Wrench className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Service Dept</p>
                <p className="text-2xl font-bold text-gray-900">{employeeStats.service}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Payroll</p>
                <p className="text-2xl font-bold text-gray-900">₹{employeeStats.totalSalaryBudget.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex gap-2">
          <Select value={filterDepartment} onValueChange={setFilterDepartment}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="front_office">Front Office</SelectItem>
              <SelectItem value="parts">Parts</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-64"
          />
        </div>
      </div>

      {/* Employee List */}
      <div className="space-y-4">
        {filteredEmployees.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Employees Found</h3>
              <p className="text-gray-600">
                {employees.length === 0 
                  ? "No employees found."
                  : "No employees match your current filters. Try adjusting your search criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredEmployees.map((employee) => {
            const departmentInfo = departmentConfig[employee.department] || { icon: Users, label: "Unknown", color: "bg-gray-100 text-gray-800" };
            const DepartmentIcon = departmentInfo.icon;
            
            return (
              <Card key={employee.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <DepartmentIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-gray-900">{employee.name || 'Unknown Employee'}</h3>
                            <span className="text-sm text-gray-500">({employee.employee_id || 'N/A'})</span>
                          </div>
                          <p className="text-sm text-gray-600">{employee.position || 'Unknown Position'}</p>
                          <Badge className={`text-xs ${departmentInfo.color} mt-1`}>
                            {departmentInfo.label}
                          </Badge>
                        </div>
                        <Badge 
                          className={`text-xs ${
                            employee.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {employee.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Contact</p>
                          <p className="text-sm text-gray-600">{employee.phone || employee.contact || 'Not provided'}</p>
                          <p className="text-sm text-gray-600">{employee.email || 'No email provided'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Salary (₹/month)</p>
                          <p className="text-sm text-gray-600">₹{(employee.salary || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Hourly Rate</p>
                          <p className="text-sm text-gray-600">₹{(employee.hourly_rate || 0).toFixed(2)}/hour</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Joining Date</p>
                          <p className="text-sm text-gray-600">
                            {employee.date_of_joining 
                              ? new Date(employee.date_of_joining).toLocaleDateString() 
                              : 'Not specified'}
                          </p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Skills:</p>
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(employee.skills) ? employee.skills.map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          )) : (
                            <Badge variant="outline" className="text-xs">
                              No skills listed
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500">
                        <div>
                          <span className="font-medium">PAN:</span> {employee.pan_number || 'Not provided'}
                        </div>
                        <div>
                          <span className="font-medium">Bank:</span> {employee.bank_account || 'Not provided'} {employee.bank_ifsc ? `(${employee.bank_ifsc})` : ''}
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleStatus(employee.id)}
                        className="text-xs"
                      >
                        {employee.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditEmployee(employee)}
                        className="text-xs"
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openTimesheet(employee)}
                        className="text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                        data-testid={`employee-timesheet-btn-${employee.id}`}
                      >
                        Timesheet
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLoginDialogEmployee(employee)}
                        className="text-xs text-purple-600 border-purple-300 hover:bg-purple-50"
                        data-testid={`employee-login-btn-${employee.id}`}
                      >
                        {loginMap[employee.id] ? `Login: ${loginMap[employee.id]}` : 'Create Login'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteEmployee(employee.id)}
                        className="text-xs"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Add Employee Modal */}
      <Dialog open={isAddEmployeeModalOpen} onOpenChange={setIsAddEmployeeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <AddEmployeeForm 
            key="add-employee-form" 
            onEmployeeAdded={handleEmployeeAdded} 
            onClose={() => setIsAddEmployeeModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={isEditEmployeeModalOpen} onOpenChange={setIsEditEmployeeModalOpen}>
        <DialogContent className="sm:max-w-md">
          {editingEmployee && (
            <EditEmployeeForm 
              employee={editingEmployee}
              onEmployeeUpdated={handleEmployeeUpdated}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Employee Timesheet / Monthly Work Modal */}
      <Dialog open={!!timesheetEmployee} onOpenChange={(o) => { if (!o) { setTimesheetEmployee(null); setTimesheetData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="employee-timesheet-dialog">
          <DialogHeader>
            <DialogTitle>Timesheet — {timesheetEmployee?.name}</DialogTitle>
            <DialogDescription>Monthly work log pulled from job cards (billable time excludes breaks).</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-4 flex-wrap justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Month</Label>
              <Input
                type="month"
                value={timesheetMonth}
                onChange={(e) => { setTimesheetMonth(e.target.value); if (timesheetEmployee) fetchTimesheet(timesheetEmployee.id, e.target.value); }}
                className="w-44"
                data-testid="timesheet-month-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTimesheetCSV} disabled={!timesheetData} data-testid="timesheet-download-csv-btn">
                <Download className="w-4 h-4 mr-2" /> Download CSV
              </Button>
              <Button variant="outline" size="sm" onClick={printTimesheet} disabled={!timesheetData} data-testid="timesheet-print-btn">
                <Printer className="w-4 h-4 mr-2" /> Print / Save PDF
              </Button>
            </div>
          </div>
          {timesheetLoading ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
          ) : !timesheetData ? (
            <p className="text-sm text-gray-500 py-8 text-center">No data.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border p-3 bg-blue-50"><div className="text-xs text-gray-600">Billable Hours</div><div className="text-xl font-bold text-blue-700" data-testid="timesheet-billable-hours">{timesheetData.totals.billable_hours}h</div></div>
                <div className="rounded-lg border p-3 bg-gray-50"><div className="text-xs text-gray-600">Worked Hours</div><div className="text-xl font-bold text-gray-700">{timesheetData.totals.worked_hours}h</div></div>
                <div className="rounded-lg border p-3 bg-green-50"><div className="text-xs text-gray-600">Jobs</div><div className="text-xl font-bold text-green-700">{timesheetData.totals.jobs_count}</div></div>
                <div className="rounded-lg border p-3 bg-amber-50"><div className="text-xs text-gray-600">Days Worked</div><div className="text-xl font-bold text-amber-700">{timesheetData.totals.days_count}</div></div>
              </div>
              {timesheetData.entries.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center" data-testid="timesheet-empty">No work logged in this period.</p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Job</th>
                        <th className="text-left px-3 py-2">Customer / Vehicle</th>
                        <th className="text-left px-3 py-2">Start</th>
                        <th className="text-left px-3 py-2">End</th>
                        <th className="text-right px-3 py-2">Worked</th>
                        <th className="text-right px-3 py-2">Billable</th>
                        <th className="text-left px-3 py-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timesheetData.entries.map((en, i) => (
                        <tr key={i} className="border-t" data-testid={`timesheet-row-${i}`}>
                          <td className="px-3 py-2 whitespace-nowrap">{en.date}</td>
                          <td className="px-3 py-2 font-medium">{en.job_number}</td>
                          <td className="px-3 py-2 text-gray-600">{en.customer_name || ''}{en.vehicle ? ` · ${en.vehicle}` : ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap" data-testid={`timesheet-start-${i}`}>{en.start_time ? new Date(en.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td className="px-3 py-2 whitespace-nowrap" data-testid={`timesheet-end-${i}`}>{en.end_time ? new Date(en.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td className="px-3 py-2 text-right">{(en.worked_minutes / 60).toFixed(2)}h</td>
                          <td className="px-3 py-2 text-right font-semibold">{(en.billable_minutes / 60).toFixed(2)}h</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-pre-wrap max-w-xs" data-testid={`timesheet-notes-${i}`}>{en.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Reset Employee Login Modal */}
      <Dialog open={!!loginDialogEmployee} onOpenChange={(o) => { if (!o) setLoginDialogEmployee(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="employee-login-dialog">
          {loginDialogEmployee && (
            <CreateEmployeeLoginForm
              employee={loginDialogEmployee}
              existingUsername={loginMap[loginDialogEmployee.id]}
              onSuccess={(username) => {
                setLoginMap((prev) => ({ ...prev, [loginDialogEmployee.id]: username }));
                setLoginDialogEmployee(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog removed - now using window.confirm */}
    </div>
  );
};

const CreateEmployeeLoginForm = ({ employee, existingUsername, onSuccess }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const [username, setUsername] = useState(existingUsername || employee.name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 15));
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('basic_user');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Username and password are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${backendUrl}/api/employees/${employee.id}/create-login`, {
        username: username.trim(),
        password: password.trim(),
        role
      });
      toast.success(res.data.message || "Login saved");
      onSuccess(username.trim());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save login");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{existingUsername ? 'Reset Login' : 'Create Login'} — {employee.name}</DialogTitle>
        <DialogDescription>
          {existingUsername
            ? 'Update this employee\'s username/password. They use these to log in and request leave/tools themselves.'
            : 'Give this employee their own login so they can request leave and tools themselves.'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="emp-login-username">Username</Label>
          <Input id="emp-login-username" className="no-uppercase" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="employee-login-username-input" />
        </div>
        <div>
          <Label htmlFor="emp-login-password">{existingUsername ? 'New Password' : 'Password'}</Label>
          <Input id="emp-login-password" type="text" className="no-uppercase" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Set a password" data-testid="employee-login-password-input" />
        </div>
        <div>
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="employee-login-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="basic_user">Basic User</SelectItem>
              <SelectItem value="normal_user">Normal User</SelectItem>
              <SelectItem value="management">Management</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={submitting} data-testid="save-employee-login-btn">
            {existingUsername ? 'Update Login' : 'Create Login'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

const ServicesSection = ({ onUpdate }) => {
  const { user } = useAuth();
  const isBasic = user?.role === 'basic_user';
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMake, setFilterMake] = useState('all');
  const [filterModel, setFilterModel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  
  // Vehicle data for filters
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);

  // MOVE FORM STATE TO PARENT TO SURVIVE RE-MOUNTING
  const [addServiceFormData, setAddServiceFormData] = useState({
    name: '',
    description: '',
    category: 'maintenance',
    duration: '',
    price: '',
    parts_required: [],
    skill_level: 'intermediate'
  });

  // Standard automotive services with Indian market pricing
  const standardServices = [
    {
      id: "service-001",
      name: "Basic Oil Change",
      description: "Engine oil and filter replacement service",
      category: "maintenance",
      duration: 30, // minutes
      labor_rate: 500.00,
      material_cost: 2500.00,
      total_price: 3000.00,
      status: "active",
      required_parts: ["Engine Oil (4L)", "Oil Filter"],
      skill_level: "basic",
      created_at: new Date().toISOString()
    },
    {
      id: "service-002", 
      name: "Complete Brake Service",
      description: "Brake pad replacement, rotor resurfacing, brake fluid flush",
      category: "repair",
      duration: 120,
      labor_rate: 1800.00,
      material_cost: 4500.00,
      total_price: 6300.00,
      status: "active",
      required_parts: ["Brake Pads", "Brake Fluid", "Cleaning Supplies"],
      skill_level: "intermediate",
      created_at: new Date().toISOString()
    },
    {
      id: "service-003",
      name: "AC System Diagnostic", 
      description: "Complete air conditioning system diagnosis and gas refill",
      category: "diagnostic",
      duration: 90,
      labor_rate: 1200.00,
      material_cost: 2800.00,
      total_price: 4000.00,
      status: "active",
      required_parts: ["AC Gas (R134a)", "UV Dye", "Leak Sealer"],
      skill_level: "advanced",
      created_at: new Date().toISOString()
    },
    {
      id: "service-004",
      name: "Engine Tune-Up",
      description: "Spark plugs, air filter, fuel system cleaning",
      category: "maintenance",
      duration: 150,
      labor_rate: 2250.00,
      material_cost: 3200.00,
      total_price: 5450.00,
      status: "active",
      required_parts: ["Spark Plugs (4)", "Air Filter", "Fuel System Cleaner"],
      skill_level: "intermediate",
      created_at: new Date().toISOString()
    },
    {
      id: "service-005",
      name: "Transmission Service",
      description: "Transmission fluid change and filter replacement",
      category: "maintenance", 
      duration: 75,
      labor_rate: 1350.00,
      material_cost: 2750.00,
      total_price: 4100.00,
      status: "active",
      required_parts: ["Transmission Fluid", "Transmission Filter", "Gasket"],
      skill_level: "advanced",
      created_at: new Date().toISOString()
    },
    {
      id: "service-006",
      name: "Battery & Electrical Check",
      description: "Battery testing, alternator check, starter inspection",
      category: "diagnostic",
      duration: 45,
      labor_rate: 675.00,
      material_cost: 150.00,
      total_price: 825.00,
      status: "active",
      required_parts: ["Battery Terminal Cleaner", "Multimeter Test"],
      skill_level: "basic",
      created_at: new Date().toISOString()
    }
  ];

  // Fetch services from backend
  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/services`);
      const backendServices = response.data;
      
      // Combine standard services with backend services (backend services take precedence)
      const allServices = [...standardServices];
      backendServices.forEach(backendService => {
        // Only add if not already in standard services
        if (!allServices.find(s => s.id === backendService.id)) {
          allServices.push(backendService);
        }
      });
      
      setServices(allServices);
    } catch (error) {
      console.error('Error fetching services:', error);
      // Fallback to standard services if API fails
      setServices(standardServices);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch vehicle makes for filters
  const fetchVehicleMakes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicle-data/makes`);
      const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
      setAvailableMakes(makes);
    } catch (error) {
      console.error('Error fetching vehicle makes:', error);
      setAvailableMakes([]);
    }
  }, []);

  // Fetch vehicle models for selected make
  const fetchVehicleModels = useCallback(async (make) => {
    if (!make || make === 'all') {
      setAvailableModels([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(make)}`);
      const models = (response.data.models || []).filter(m => m && m.trim() !== '');
      setAvailableModels(models);
    } catch (error) {
      console.error('Error fetching vehicle models:', error);
      setAvailableModels([]);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchVehicleMakes();
  }, [fetchServices, fetchVehicleMakes]);

  // Update available models when make filter changes
  useEffect(() => {
    if (filterMake === 'all') {
      setAvailableModels([]);
      setFilterModel('all');
    } else {
      fetchVehicleModels(filterMake);
      setFilterModel('all');
    }
  }, [filterMake, fetchVehicleModels]);

  // Filter services
  const filteredServices = (services || []).filter(service => {
    const matchesCategory = filterCategory === 'all' || service.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || service.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      (service.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.required_parts || []).some(part => (part || '').toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Vehicle compatibility filters
    const matchesMake = filterMake === 'all' || 
      !service.compatible_makes || 
      service.compatible_makes.length === 0 || 
      service.compatible_makes.includes(filterMake);
      
    const matchesModel = filterModel === 'all' || 
      !service.compatible_models || 
      service.compatible_models.length === 0 || 
      service.compatible_models.includes(filterModel);
    
    return matchesCategory && matchesStatus && matchesSearch && matchesMake && matchesModel;
  });

  // Statistics
  const serviceStats = {
    total: services.length,
    maintenance: services.filter(s => s.category === 'maintenance').length,
    repair: services.filter(s => s.category === 'repair').length,
    diagnostic: services.filter(s => s.category === 'diagnostic').length,
    active: services.filter(s => s.status === 'active').length,
    totalRevenue: services.reduce((sum, s) => sum + (s.total_price || 0), 0)
  };

  const categoryConfig = {
    "maintenance": { icon: Wrench, label: "Maintenance", color: "bg-green-100 text-green-800" },
    "repair": { icon: AlertTriangle, label: "Repair", color: "bg-red-100 text-red-800" },
    "diagnostic": { icon: Search, label: "Diagnostic", color: "bg-blue-100 text-blue-800" },
    "custom": { icon: Cog, label: "Custom", color: "bg-purple-100 text-purple-800" }
  };

  const skillLevelConfig = {
    "basic": { color: "bg-green-100 text-green-800", label: "Basic" },
    "intermediate": { color: "bg-yellow-100 text-yellow-800", label: "Intermediate" },
    "advanced": { color: "bg-red-100 text-red-800", label: "Advanced" }
  };

  const handleAddService = () => {
    setIsAddServiceModalOpen(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setIsEditServiceModalOpen(true);
  };

  const handleServiceAdded = useCallback((newService) => {
    // Service is already saved to backend, just refresh the list
    fetchServices();
    setIsAddServiceModalOpen(false);
    // Reset form state after successful addition
    setAddServiceFormData({
      name: '',
      description: '',
      category: 'maintenance',
      duration: '',
      price: '',
      parts_required: [],
      skill_level: 'intermediate'
    });
    if (onUpdate) onUpdate();
  }, [onUpdate, fetchServices]);

  const handleServiceUpdated = useCallback((updatedService) => {
    if (!updatedService || !updatedService.id) {
      console.error('handleServiceUpdated called without valid service data');
      return;
    }
    
    setServices(prevServices => prevServices.map(s => 
      s.id === updatedService.id ? updatedService : s
    ));
    setIsEditServiceModalOpen(false);
    setEditingService(null);
    if (onUpdate) onUpdate();
  }, [onUpdate]);

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service? This action cannot be undone.")) return;
    
    try {
      // Call backend API to delete service
      await axios.delete(`${API}/services/${serviceId}`);
      
      // Refresh services list from backend
      fetchServices();
      
      toast.success('Service deleted successfully!');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleToggleStatus = (serviceId) => {
    setServices(services.map(s => 
      s.id === serviceId 
        ? { ...s, status: s.status === 'active' ? 'inactive' : 'active' }
        : s
    ));
    if (onUpdate) onUpdate();
  };

  return (
    <div>
      {/* Services Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Services Management</h2>
          <p className="text-gray-600">Manage standard automotive services and create custom service packages</p>
        </div>
        <Button 
          onClick={handleAddService}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-service-btn"
          style={isBasic ? { display: 'none' } : undefined}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Custom Service
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{serviceStats.total}</div>
                <div className="text-sm opacity-90">Total Services</div>
              </div>
              <Cog className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{serviceStats.maintenance}</div>
                <div className="text-sm opacity-90">Maintenance</div>
              </div>
              <Wrench className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{serviceStats.repair}</div>
                <div className="text-sm opacity-90">Repair</div>
              </div>
              <AlertTriangle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{serviceStats.diagnostic}</div>
                <div className="text-sm opacity-90">Diagnostic</div>
              </div>
              <Search className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{serviceStats.active}</div>
                <div className="text-sm opacity-90">Active Services</div>
              </div>
              <CheckCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        {!isBasic && (
        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{(serviceStats.totalRevenue || 0).toFixed(0)}</div>
                <div className="text-sm opacity-90">Service Value</div>
              </div>
              <DollarSign className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div>
              <Label>Search Services</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search services, parts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 no-uppercase"
                />
              </div>
            </div>

            <div>
              <Label>Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="diagnostic">Diagnostic</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Vehicle Make</Label>
              <Select value={filterMake} onValueChange={setFilterMake}>
                <SelectTrigger>
                  <SelectValue placeholder="All Makes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Makes</SelectItem>
                  {availableMakes.map(make => (
                    <SelectItem key={make} value={make}>{make}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Vehicle Model</Label>
              <Select value={filterModel} onValueChange={setFilterModel} disabled={filterMake === 'all'}>
                <SelectTrigger>
                  <SelectValue placeholder="All Models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  {availableModels.map(model => (
                    <SelectItem key={model} value={model}>{model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services List */}
      <div className="space-y-4">
        {filteredServices.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Cog className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Services Found</h3>
              <p className="text-gray-600">
                {services.length === 0 
                  ? "No services available."
                  : "No services match your current filters. Try adjusting your search criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredServices.map((service) => {
            const categoryInfo = categoryConfig[service.category] || { icon: Cog, label: "Unknown", color: "bg-gray-100 text-gray-800" };
            const skillInfo = skillLevelConfig[service.skill_level] || { color: "bg-gray-100 text-gray-800", label: "Unknown" };
            
            return (
              <Card key={service.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <categoryInfo.icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                        </div>
                        <Badge className={`${categoryInfo.color} text-xs`}>
                          {categoryInfo.label}
                        </Badge>
                        <Badge className={`${skillInfo.color} text-xs`}>
                          {skillInfo.label}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Duration</p>
                          <p className="text-sm text-gray-600">{service.duration ? `${service.duration} hours` : 'Not specified'}</p>
                        </div>
                        {!isBasic && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Price</p>
                          <p className="text-lg font-semibold text-green-600">₹{service.price ? service.price.toFixed(2) : '0.00'}</p>
                        </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-700">Skill Level</p>
                          <p className="text-sm text-gray-600 capitalize">{service.skill_level}</p>
                        </div>
                      </div>

                      {service.description && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                          <p className="text-sm text-gray-600">{service.description}</p>
                        </div>
                      )}

                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Created: {new Date(service.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditService(service)}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                        style={isBasic ? { display: 'none' } : undefined}
                      >
                        Edit
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteService(service.id)}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                        style={isBasic ? { display: 'none' } : undefined}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Summary */}
      {filteredServices.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600">
          Showing {filteredServices.length} of {services.length} services
        </div>
      )}

      {/* Add Service Modal */}
      <Dialog open={isAddServiceModalOpen} onOpenChange={setIsAddServiceModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AddServiceForm 
            onServiceAdded={handleServiceAdded} 
            formData={addServiceFormData}
            setFormData={setAddServiceFormData}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Service Modal */}
      <Dialog open={isEditServiceModalOpen} onOpenChange={setIsEditServiceModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {editingService && (
            <EditServiceForm 
              service={editingService} 
              onServiceUpdated={handleServiceUpdated} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edit Customer Form Component
const EditCustomerForm = ({ customer, onCustomerUpdated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use React Hook Form for proper state management
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      name: customer.name || '',
      contact: customer.contact || customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      location: customer.location || '',
      gstin: customer.gstin || '',
      state_code: customer.state_code || '',
      state_name: customer.state_name || ''
    }
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      if (!data.name.trim()) {
        toast.error("Customer name is required");
        setIsSubmitting(false);
        return;
      }

      if (!data.contact.trim()) {
        toast.error("Contact information is required");
        setIsSubmitting(false);
        return;
      }

      const updateData = {
        name: data.name.trim(),
        contact: data.contact.trim(),
        email: data.email.trim(),
        address: data.address.trim(),
        location: data.location.trim(),
        gstin: (data.gstin || '').trim(),
        state_code: (data.state_code || '').trim(),
        state_name: (data.state_name || '').trim()
      };

      const response = await axios.put(`${API}/customers/${customer.id}`, updateData);
      
      toast.success('Customer updated successfully!');
      onCustomerUpdated(response.data);
    } catch (error) {
      console.error('Error updating customer:', error);
      
      if (error.response && error.response.data) {
        if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            const errorMessages = error.response.data.detail.map(err => 
              `${err.loc.join('.')}: ${err.msg}`
            ).join(', ');
            toast.error(`Validation error: ${errorMessages}`);
          } else {
            toast.error(`Error: ${error.response.data.detail}`);
          }
        } else {
          toast.error('Failed to update customer - server error');
        }
      } else {
        toast.error('Failed to update customer - network error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>Edit Customer</DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="name">Customer Name *</Label>
          <Input
            id="name"
            {...register("name", { required: "Customer name is required" })}
            placeholder="Full name"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
        </div>
        
        <div>
          <Label htmlFor="contact">Contact Number *</Label>
          <Input
            id="contact"
            {...register("contact", { required: "Contact information is required" })}
            placeholder="+91 XXXXX XXXXX"
          />
          {errors.contact && <p className="text-red-500 text-sm mt-1">{errors.contact.message}</p>}
        </div>
        
        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            placeholder="customer@email.com"
          />
        </div>
        
        <div>
          <Label htmlFor="address">Address</Label>
          <Textarea
            id="address"
            {...register("address")}
            placeholder="Street address, city, state"
            rows={2}
          />
        </div>
        
        <div>
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            {...register("location")}
            placeholder="City, State"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
          <div>
            <Label htmlFor="state_name">State (GST)</Label>
            <Input
              id="state_name"
              {...register("state_name")}
              placeholder="e.g. Kerala"
              data-testid="edit-customer-state-input"
            />
          </div>
          <div>
            <Label htmlFor="state_code">State Code</Label>
            <Input
              id="state_code"
              {...register("state_code")}
              placeholder="e.g. 32"
              maxLength={2}
              data-testid="edit-customer-state-code-input"
            />
          </div>
          <div>
            <Label htmlFor="gstin">Customer GSTIN (optional)</Label>
            <Input
              id="gstin"
              {...register("gstin")}
              placeholder="For B2B invoices"
              data-testid="edit-customer-gstin-input"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onCustomerUpdated(customer)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? 'Updating...' : 'Update Customer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Add Vehicle Form Component
const AddVehicleForm = ({ onVehicleAdded }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Cascading dropdown state
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');

  // Vehicle data from curated database
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  
  // Define default values using useMemo to prevent recreation
  const defaultValues = useMemo(() => ({
    make: '',
    model: '',
    series: '',
    year: '',
    license_plate: '',
    vin: '',
    engine_no: '',
    mileage: '',
    fuel_type: '',
    transmission: '',
    current_issue: ''
  }), []);
  
  // Use React Hook Form for proper state management
  const { register, handleSubmit, control, reset, formState: { errors }, setValue } = useForm({
    defaultValues
  });

  // Fetch vehicle makes from curated database
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setAvailableMakes(makes);
      } catch (error) {
        console.error('Error fetching vehicle makes:', error);
        setAvailableMakes([]);
      }
    };
    fetchMakes();
  }, []);

  // Fetch models when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedMake) {
        setAvailableModels([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedMake)}`);
        const models = (response.data.models || []).filter(m => m && m.trim() !== '');
        setAvailableModels(models);
      } catch (error) {
        console.error('Error fetching vehicle models:', error);
        setAvailableModels([]);
      }
    };
    fetchModels();
  }, [selectedMake]);

  // Fetch series when make+model changes
  useEffect(() => {
    const fetchSeries = async () => {
      if (!selectedMake || !selectedModel) {
        setAvailableSeries([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`);
        const series = (response.data.series || []).filter(s => s && s.trim() !== '');
        setAvailableSeries(series);
      } catch (error) {
        console.error('Error fetching vehicle series:', error);
        setAvailableSeries([]);
      }
    };
    fetchSeries();
  }, [selectedMake, selectedModel]);

  // Reset form function using useCallback
  const resetForm = useCallback(() => {
    reset(defaultValues);
    setSelectedMake('');
    setSelectedModel('');
    setSelectedSeries('');
  }, [reset, defaultValues]);

  const onSubmit = async (data) => {
    if (!selectedMake.trim() || !selectedModel.trim()) {
      toast.error("Please select vehicle make and model");
      return;
    }

    setIsSubmitting(true);

    try {
      // Use cascading dropdown values for make, model, series
      const vehicleData = {
        ...data,
        make: selectedMake.trim(),
        model: selectedModel.trim(),
        series: selectedSeries.trim() || null
      };
      
      // Remove empty strings and convert to proper types
      Object.keys(vehicleData).forEach(key => {
        if (vehicleData[key] === '') {
          vehicleData[key] = null;
        }
      });
      
      if (vehicleData.year) {
        vehicleData.year = parseInt(vehicleData.year);
      }
      if (vehicleData.mileage) {
        vehicleData.mileage = parseInt(vehicleData.mileage);
      }

      const response = await axios.post(`${API}/vehicles`, vehicleData);
      const newVehicle = response.data;
      
      toast.success('Vehicle added successfully!');
      onVehicleAdded(newVehicle);

      // Reset form to initial state
      resetForm();
    } catch (error) {
      console.error('Error adding vehicle:', error);
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Add Vehicle</DialogTitle>
        <DialogDescription>
          Add a new vehicle to the garage system
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Vehicle Information - Cascading Dropdowns */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">🚗 Vehicle Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* MAKE Dropdown */}
            <div>
              <Label htmlFor="make">MAKE *</Label>
              <Select
                value={selectedMake}
                onValueChange={(value) => {
                  setSelectedMake(value);
                  setValue('make', value);
                  // Reset model and series when make changes
                  setSelectedModel('');
                  setSelectedSeries('');
                  setValue('model', '');
                  setValue('series', '');
                }}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select vehicle MAKE" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableMakes.map((make) => (
                    <SelectItem key={make} value={make}>{make}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedMake && (
                <p className="text-red-500 text-sm mt-1">Vehicle make is required</p>
              )}
            </div>

            {/* MODEL Dropdown */}
            <div>
              <Label htmlFor="model">MODEL *</Label>
              <Select
                value={selectedModel}
                onValueChange={(value) => {
                  setSelectedModel(value);
                  setValue('model', value);
                  // Reset series when model changes
                  setSelectedSeries('');
                  setValue('series', '');
                }}
                disabled={!selectedMake}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue 
                    placeholder={
                      selectedMake 
                        ? `Select ${selectedMake} MODEL` 
                        : "Select MAKE first"
                    } 
                  />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableModels.length > 0 ? (
                    availableModels.map((model) => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))
                  ) : selectedMake ? (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      No predefined models for {selectedMake}
                    </div>
                  ) : null}
                  {selectedMake && selectedMake !== 'Other' && (
                    <SelectItem value="Other Model">Other {selectedMake} Model</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {selectedMake && (
                <div className="text-xs text-gray-500 mt-1">
                  {availableModels.length > 0 
                    ? `${availableModels.length} ${selectedMake} MODELs available`
                    : `Select ${selectedMake} MODEL`
                  }
                </div>
              )}
              {!selectedModel && selectedMake && (
                <p className="text-red-500 text-sm mt-1">Vehicle model is required</p>
              )}
            </div>

            {/* SERIES Dropdown */}
            <div>
              <Label htmlFor="series">SERIES</Label>
              <Select
                value={selectedSeries}
                onValueChange={(value) => {
                  setSelectedSeries(value);
                  setValue('series', value);
                }}
                disabled={!selectedMake || !selectedModel}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue 
                    placeholder={
                      !selectedMake ? "Select MAKE first" :
                      !selectedModel ? "Select MODEL first" :
                      `Select ${selectedModel} SERIES`
                    } 
                  />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableSeries.length > 0 ? (
                    availableSeries.map((series) => (
                      <SelectItem key={series} value={series}>
                        {series}
                      </SelectItem>
                    ))
                  ) : selectedMake && selectedModel ? (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      No predefined series for {selectedModel}
                    </div>
                  ) : null}
                  {selectedMake && selectedModel && selectedModel !== 'Other Model' && (
                    <SelectItem value="Other Series">Other {selectedModel} Series</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {selectedMake && selectedModel && (
                <div className="text-xs text-gray-500 mt-1">
                  {availableSeries.length > 0 
                    ? `${availableSeries.length} ${selectedModel} SERIES available`
                    : `Select ${selectedModel} SERIES`
                  }
                </div>
              )}
              {!selectedMake || !selectedModel ? (
                <p className="text-xs text-gray-500 mt-1">Optional: Select MAKE and MODEL first to see SERIES options</p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="year">Year</Label>
            <Input
              id="year"
              type="number"
              {...register('year')}
              placeholder="2024"
              min="1900"
              max="2030"
            />
          </div>
          <div>
            <Label htmlFor="license_plate">License Plate</Label>
            <Input
              id="license_plate"
              {...register('license_plate')}
              placeholder="ABC-1234"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="vin">VIN (Vehicle Identification Number)</Label>
          <Input
            id="vin"
            {...register('vin')}
            placeholder="17-character VIN"
            maxLength="17"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="engine_no">Engine Number</Label>
            <Input
              id="engine_no"
              {...register('engine_no')}
              placeholder="Engine number"
            />
          </div>
          <div>
            <Label htmlFor="mileage">Mileage (km)</Label>
            <Input
              id="mileage"
              type="number"
              {...register('mileage')}
              placeholder="100000"
              min="0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fuel_type">Fuel Type</Label>
            <Controller
              name="fuel_type"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select fuel type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="petrol">Petrol</SelectItem>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="cng">CNG</SelectItem>
                    <SelectItem value="electric">Electric</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <Label htmlFor="transmission">Transmission</Label>
            <Controller
              name="transmission"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select transmission" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="automatic">Automatic</SelectItem>
                    <SelectItem value="cvt">CVT</SelectItem>
                    <SelectItem value="amt">AMT</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="current_issue">Current Issue/Problem</Label>
          <Textarea
            id="current_issue"
            {...register('current_issue')}
            placeholder="Describe any current issues with the vehicle"
            rows="3"
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? "Adding Vehicle..." : "Add Vehicle"}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={resetForm}
            disabled={isSubmitting}
            className="flex-none"
          >
            Reset
          </Button>
        </div>
      </form>
    </div>
  );
};

// Update Vehicle Form Component
const UpdateVehicleForm = ({ vehicle, onVehicleUpdated, onCancel }) => {
  const [updateVehicleFormData, setUpdateVehicleFormData] = useState({
    make: vehicle.make || '',
    model: vehicle.model || '',
    series: vehicle.series || '',
    year: vehicle.year || '',
    license_plate: vehicle.license_plate || '',
    vin: vehicle.vin || '',
    engine_no: vehicle.engine_no || '',
    mileage: vehicle.mileage || '',
    fuel_type: vehicle.fuel_type || '',
    transmission: vehicle.transmission || '',
    current_issue: vehicle.current_issue || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.put(`${API}/vehicles/${vehicle.id}`, updateVehicleFormData);
      const updatedVehicle = response.data;
      
      toast.success('Vehicle updated successfully!');
      
      // Pass the updated vehicle data to the parent component
      onVehicleUpdated(updatedVehicle);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      toast.error('Failed to update vehicle');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Update Vehicle</DialogTitle>
        <DialogDescription>
          Update vehicle information and details
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="make">Make</Label>
            <Input
              id="make"
              value={updateVehicleFormData.make}
              onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, make: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="model">Model</Label>
            <Input
              id="model"
              value={updateVehicleFormData.model}
              onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, model: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="series">Series</Label>
            <Input
              id="series"
              value={updateVehicleFormData.series}
              onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, series: e.target.value})}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="year">Year</Label>
            <Input
              id="year"
              type="number"
              value={updateVehicleFormData.year}
              onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, year: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="license_plate">License Plate</Label>
            <Input
              id="license_plate"
              value={updateVehicleFormData.license_plate}
              onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, license_plate: e.target.value})}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="vin">VIN</Label>
          <Input
            id="vin"
            value={updateVehicleFormData.vin}
            onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, vin: e.target.value})}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="engine_no">Engine Number</Label>
            <Input
              id="engine_no"
              value={updateVehicleFormData.engine_no}
              onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, engine_no: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="mileage">Mileage (km)</Label>
            <Input
              id="mileage"
              type="number"
              value={updateVehicleFormData.mileage}
              onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, mileage: e.target.value})}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fuel_type">Fuel Type</Label>
            <Select value={updateVehicleFormData.fuel_type} onValueChange={(value) => setUpdateVehicleFormData({...updateVehicleFormData, fuel_type: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select fuel type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="petrol">Petrol</SelectItem>
                <SelectItem value="diesel">Diesel</SelectItem>
                <SelectItem value="electric">Electric</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="transmission">Transmission</Label>
            <Select value={updateVehicleFormData.transmission} onValueChange={(value) => setUpdateVehicleFormData({...updateVehicleFormData, transmission: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select transmission" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="automatic">Automatic</SelectItem>
                <SelectItem value="cvt">CVT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="current_issue">Current Problem</Label>
          <Textarea
            id="current_issue"
            value={updateVehicleFormData.current_issue}
            onChange={(e) => setUpdateVehicleFormData({...updateVehicleFormData, current_issue: e.target.value})}
            placeholder="Describe any current issues with the vehicle"
            rows="3"
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? "Updating..." : "Update Vehicle"}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

// Vehicle Card Component
const VehicleCard = ({ vehicle, onUpdate }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await axios.delete(`${API}/vehicles/${vehicle.id}`);
        toast.success('Vehicle deleted successfully!');
        onUpdate();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        toast.error('Failed to delete vehicle');
      }
    }
  };

  const handleEditComplete = () => {
    setIsEditModalOpen(false);
    onUpdate();
  };

  return (
    <>
      <Card className="hover:shadow-lg transition-shadow duration-200">
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h3>
              {vehicle.series && (
                <p className="text-gray-600 text-sm">{vehicle.series}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleEdit}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={handleDelete}>
                <Trash className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">License Plate:</span>
                <span className="ml-2 font-medium">{vehicle.license_plate || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">VIN:</span>
                <span className="ml-2 font-medium text-xs">{vehicle.vin || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">Mileage:</span>
                <span className="ml-2 font-medium">{vehicle.mileage ? `${vehicle.mileage} km` : 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">Fuel Type:</span>
                <span className="ml-2 font-medium capitalize">{vehicle.fuel_type || 'N/A'}</span>
              </div>
            </div>

            {vehicle.engine_no && (
              <div className="text-sm">
                <span className="text-gray-500">Engine No:</span>
                <span className="ml-2 font-medium">{vehicle.engine_no}</span>
              </div>
            )}

            {vehicle.transmission && (
              <div className="text-sm">
                <span className="text-gray-500">Transmission:</span>
                <span className="ml-2 font-medium capitalize">{vehicle.transmission}</span>
              </div>
            )}

            {vehicle.current_issue && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="font-medium text-red-900">Current Problem</span>
                </div>
                <p className="text-sm text-red-800 mt-1">{vehicle.current_issue}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Vehicle Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <UpdateVehicleForm 
            vehicle={vehicle}
            onVehicleUpdated={handleEditComplete}
            onCancel={() => setIsEditModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Add Customer Form Component
const AddCustomerForm = ({ onCustomerAdded }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use React Hook Form for proper state management
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      contact: '',
      email: '',
      address: '',
      location: '',
      vehicle_count: 0,
      notes: '',
      gstin: '',
      state_code: '',
      state_name: ''
    }
  });

  const onSubmit = async (data) => {
    if (!data.name.trim()) {
      toast.error("Please enter customer name");
      return;
    }

    setIsSubmitting(true);

    try {
      const customerData = {
        ...data,
        vehicle_count: parseInt(data.vehicle_count) || 0
      };

      const response = await axios.post(`${API}/customers`, customerData);
      const newCustomer = response.data;
      
      toast.success('Customer added successfully!');
      onCustomerAdded(newCustomer);

      // Reset form
      reset({
        name: '',
        contact: '',
        email: '',
        address: '',
        location: '',
        vehicle_count: 0,
        notes: '',
        gstin: '',
        state_code: '',
        state_name: ''
      });
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to add customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogDescription>
          Add a new customer to the system
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Customer Name *</Label>
            <Input
              id="name"
              {...register('name', { required: 'Customer name is required' })}
              placeholder="Full name"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="contact">Contact Number</Label>
            <Input
              id="contact"
              {...register('contact')}
              placeholder="Phone number"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="customer@email.com"
          />
        </div>

        <div>
          <Label htmlFor="address">Address</Label>
          <Textarea
            id="address"
            {...register('address')}
            placeholder="Full address"
            rows="2"
          />
        </div>

        <div>
          <Label htmlFor="location">Location/City</Label>
          <Input
            id="location"
            {...register('location')}
            placeholder="City or area"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
          <div>
            <Label htmlFor="state_name">State (GST)</Label>
            <Input
              id="state_name"
              {...register('state_name')}
              placeholder="e.g. Kerala"
              data-testid="add-customer-state-input"
            />
          </div>
          <div>
            <Label htmlFor="state_code">State Code</Label>
            <Input
              id="state_code"
              {...register('state_code')}
              placeholder="e.g. 32"
              maxLength={2}
              data-testid="add-customer-state-code-input"
            />
          </div>
          <div>
            <Label htmlFor="gstin">Customer GSTIN (optional)</Label>
            <Input
              id="gstin"
              {...register('gstin')}
              placeholder="For B2B invoices"
              data-testid="add-customer-gstin-input"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">Leave State blank to assume same state as the garage (CGST+SGST). Set a different state to charge IGST instead.</p>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            {...register('notes')}
            placeholder="Additional notes"
            rows="2"
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? "Adding Customer..." : "Add Customer"}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Add Expense Form Component - Enhanced with Payment Method Integration
const AddExpenseForm = ({ onExpenseAdded }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashBalance, setCashBalance] = useState(0);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const loadBankAccounts = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/bank-accounts`);
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  };
  
  // Use React Hook Form for proper state management
  const { register, handleSubmit, control, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      description: '',
      category: 'supplies',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      bank_account_id: '',
      credit_due_date: '',
      vendor_name: '',
      vendor_contact: '',
      vendor_address: '',
      receipt_number: '',
      notes: ''
    }
  });

  const paymentMethod = watch("payment_method");
  const amount = watch("amount");
  const category = watch("category");
  
  // Categories that typically require vendor information
  const vendorRequiredCategories = ['parts', 'supplies', 'equipment', 'maintenance'];
  
  // Calculate default due date (30 days from today)
  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  // Load bank accounts and cash balance on component mount
  useEffect(() => {
    const loadPaymentData = async () => {
      try {
        // Load bank accounts
        const bankResponse = await axios.get(`${backendUrl}/api/bank-accounts`);
        setBankAccounts(bankResponse.data || []);

        // Load cash balance
        const cashResponse = await axios.get(`${backendUrl}/api/cash/balance`);
        setCashBalance(cashResponse.data?.current_balance || 0);
      } catch (error) {
        console.error('Error loading payment data:', error);
      }
    };

    loadPaymentData();
  }, [backendUrl]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      if (!data.description.trim() || !data.amount) {
        toast.error("Please enter description and amount");
        setIsSubmitting(false);
        return;
      }

      const expenseAmount = parseFloat(data.amount);

      // Validate sufficient funds for cash/bank payments (skip for credit)
      if (data.payment_method === 'cash' && expenseAmount > cashBalance) {
        toast.error(`Insufficient cash balance. Available: ₹${cashBalance.toLocaleString()}`);
        setIsSubmitting(false);
        return;
      }

      if (data.payment_method === 'bank_account' && !data.bank_account_id) {
        toast.error("Please select a bank account");
        setIsSubmitting(false);
        return;
      }

      // Validate vendor information for certain categories
      if (vendorRequiredCategories.includes(data.category) && !data.vendor_name.trim()) {
        toast.error(`Vendor/Supplier name is required for ${data.category} expenses`);
        setIsSubmitting(false);
        return;
      }

      // Prepare expense data for backend
      const expenseData = {
        description: data.description.trim(),
        amount: expenseAmount,
        type: "expense",
        payment_method: data.payment_method,
        bank_account_id: data.payment_method === 'bank_account' ? data.bank_account_id : null,
        category: data.category,
        transaction_date: data.date,
        credit_due_date: data.payment_method === 'credit' ? (data.credit_due_date || null) : null,
        supplier_name: data.vendor_name?.trim() || null,
        notes: data.notes?.trim() || null,
        // Additional vendor details
        vendor_contact: data.vendor_contact?.trim() || null,
        vendor_address: data.vendor_address?.trim() || null,
        receipt_number: data.receipt_number?.trim() || null
      };

      console.log('Creating expense:', expenseData);

      // Create expense via accounting transaction endpoint
      const response = await axios.post(`${backendUrl}/api/accounting/transactions`, expenseData);
      
      toast.success(`Expense of ₹${expenseAmount.toLocaleString()} processed successfully!`);
      
      // Call the callback to refresh parent component
      if (onExpenseAdded) {
        onExpenseAdded(expenseData);
      }

      // Reset form
      reset({
        description: '',
        category: 'supplies',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        bank_account_id: '',
        credit_due_date: '',
        vendor_name: '',
        vendor_contact: '',
        vendor_address: '',
        receipt_number: '',
        notes: ''
      });

      // Refresh cash balance
      if (data.payment_method === 'cash') {
        const cashResponse = await axios.get(`${backendUrl}/api/cash/balance`);
        setCashBalance(cashResponse.data?.current_balance || 0);
      }

    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-1">
      <DialogHeader className="sticky top-0 bg-white z-10 pb-4 border-b">
        <DialogTitle>Add New Expense</DialogTitle>
        <DialogDescription>
          Record a new business expense with automatic payment processing
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-1">
        <div>
          <Label htmlFor="description">Description *</Label>
          <Input
            id="description"
            {...register("description", { required: "Description is required" })}
            placeholder="What was purchased/paid for"
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">Category</Label>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parts">Parts</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="operational">Operational</SelectItem>
                    <SelectItem value="supplies">Supplies</SelectItem>
                    <SelectItem value="office_supplies">Office Supplies</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="tools">Tools</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="utilities">Utilities</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <Label htmlFor="amount">Amount (₹) *</Label>
            <Input
              id="amount"
              type="number"
              {...register("amount", { 
                required: "Amount is required",
                min: { value: 0.01, message: "Amount must be greater than 0" }
              })}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              {...register("date")}
            />
          </div>
          <div>
            <Label htmlFor="payment_method">Payment Method *</Label>
            <Controller
              name="payment_method"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_account">Bank Account</SelectItem>
                    <SelectItem value="credit">Credit (Pay Later)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Payment Method Details */}
        <div className="p-3 bg-gray-50 rounded-lg">
          {paymentMethod === 'cash' ? (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Available Cash Balance:</span>
              <span className="font-semibold text-green-600">₹{cashBalance.toLocaleString()}</span>
            </div>
          ) : paymentMethod === 'bank_account' ? (
            <div>
              <Label htmlFor="bank_account_id">Select Bank Account *</Label>
              <Controller
                name="bank_account_id"
                control={control}
                rules={{ 
                  required: paymentMethod === 'bank_account' ? "Bank account selection is required" : false 
                }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex flex-col">
                            <span>{account.bank_name} - **** {account.account_number.slice(-4)}</span>
                            <span className="text-xs text-gray-500">Balance: ₹{account.current_balance?.toLocaleString()}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.bank_account_id && (
                <p className="text-red-500 text-sm mt-1">{errors.bank_account_id.message}</p>
              )}
            </div>
          ) : paymentMethod === 'credit' ? (
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-2 text-orange-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Credit Purchase - Payment Pending</span>
              </div>
              <p className="text-xs text-gray-600">This expense will be recorded but no balance will be deducted immediately</p>
              
              {/* Credit Due Date */}
              <div className="mt-3">
                <Label htmlFor="credit_due_date">Payment Due Date</Label>
                <Input
                  id="credit_due_date"
                  type="date"
                  {...register("credit_due_date")}
                  placeholder={getDefaultDueDate()}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default: 30 days from today ({getDefaultDueDate()})
                </p>
              </div>
            </div>
          ) : null}

          {/* Balance Warning */}
          {amount && paymentMethod === 'cash' && parseFloat(amount) > cashBalance && (
            <p className="text-red-500 text-sm mt-2">
              ⚠️ Insufficient cash balance! Available: ₹{cashBalance.toLocaleString()}
            </p>
          )}
        </div>

        {/* Enhanced Vendor/Supplier Section */}
        <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <Label className="text-base font-medium text-blue-900">Vendor/Supplier Information</Label>
            {vendorRequiredCategories.includes(category) && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Required for {category}</span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vendor_name">
                Vendor/Supplier Name {vendorRequiredCategories.includes(category) && '*'}
              </Label>
              <Input
                id="vendor_name"
                {...register("vendor_name", {
                  required: vendorRequiredCategories.includes(category) ? "Vendor name is required for this category" : false
                })}
                placeholder="Company or person name"
              />
              {errors.vendor_name && (
                <p className="text-red-500 text-sm mt-1">{errors.vendor_name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="vendor_contact">Contact Number</Label>
              <Input
                id="vendor_contact"
                {...register("vendor_contact")}
                placeholder="Phone number or email"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="vendor_address">Vendor Address (Optional)</Label>
            <Input
              id="vendor_address"
              {...register("vendor_address")}
              placeholder="Complete address"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="receipt_number">Receipt Number</Label>
          <Input
            id="receipt_number"
            {...register("receipt_number")}
            placeholder="Receipt or invoice number"
          />
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            {...register("notes")}
            placeholder="Additional notes"
            rows="2"
          />
        </div>

        <div className="sticky bottom-0 bg-white pt-4 border-t flex gap-2">
          <Button 
            type="submit" 
            disabled={isSubmitting || (paymentMethod === 'cash' && parseFloat(amount || 0) > cashBalance)}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? "Processing Expense..." : 
             paymentMethod === 'credit' ? "Record Credit Purchase" : "Add Expense"}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Add Employee Form Component
const AddEmployeeForm = ({ onEmployeeAdded, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // React Hook Form setup
  const { 
    register, 
    handleSubmit, 
    reset, 
    setValue, 
    watch,
    control,
    formState: { errors } 
  } = useForm({
    defaultValues: {
      name: '',
      position: 'technician',
      department: 'administration',
      contact: '',
      salary: ''
    }
  });

  // Watch form values for debugging
  const formValues = watch();

  const handleCancel = () => {
    // Reset form using React Hook Form
    reset();
    
    // Close modal
    if (onClose) {
      onClose();
    }
  };

  const onSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      console.log('Form data from React Hook Form:', formData);
      
      if (!formData.name || !formData.position) {
        toast.error("Please enter employee name and position");
        setIsSubmitting(false);
        return;
      }

      const employeeData = {
        name: formData.name.trim(),
        employee_id: `EMP${Date.now().toString().slice(-6)}`, // Generate unique employee ID
        position: formData.position,
        department: formData.department,
        contact: formData.contact.trim(),
        salary: parseFloat(formData.salary) || 0,
        is_active: true
      };

      console.log('Sending employee data to backend:', employeeData);

      const response = await apiFetch(`${backendUrl}/api/employees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(employeeData),
      });

      if (response.ok) {
        const createdEmployee = await response.json();
        console.log('Employee created successfully:', createdEmployee);
        
        toast.success('Employee added successfully!');
        
        // Reset form using React Hook Form
        reset();
        
        // Call parent handlers
        if (onEmployeeAdded) onEmployeeAdded(createdEmployee);
        if (onClose) onClose();
      } else {
        const errorData = await response.json();
        console.error('Failed to create employee:', errorData);
        
        if (errorData.detail && Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail.map(err => 
            `${err.loc?.join('.')}: ${err.msg}`
          ).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error('Failed to create employee. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      toast.error('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Add New Employee</DialogTitle>
        <DialogDescription>
          Add a new team member to your garage
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              {...register("name", { required: "Name is required" })}
              placeholder="Employee name"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="position">Position *</Label>
            <Controller
              name="position"
              control={control}
              rules={{ required: "Position is required" }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="senior_technician">Senior Technician</SelectItem>
                    <SelectItem value="junior_technician">Junior Technician</SelectItem>
                    <SelectItem value="senior_mechanic">Senior Mechanic</SelectItem>
                    <SelectItem value="junior_mechanic">Junior Mechanic</SelectItem>
                    <SelectItem value="service_advisor">Service Advisor</SelectItem>
                    <SelectItem value="parts_specialist">Parts Specialist</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="receptionist">Receptionist</SelectItem>
                    <SelectItem value="cleaner">Cleaner</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.position && (
              <p className="text-red-500 text-sm mt-1">{errors.position.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contact">Contact Number</Label>
            <Input
              id="contact"
              {...register("contact")}
              placeholder="Phone number"
            />
          </div>
          <div>
            <Label htmlFor="department">Department *</Label>
            <Controller
              name="department"
              control={control}
              rules={{ required: "Department is required" }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="administration">Administration</SelectItem>
                    <SelectItem value="management">Management</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="parts">Parts</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.department && (
              <p className="text-red-500 text-sm mt-1">{errors.department.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div>
            <Label htmlFor="salary">Salary (₹/month)</Label>
            <Input
              id="salary"
              {...register("salary")}
              type="text"
              placeholder="Monthly salary"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-4" style={{ position: 'relative', zIndex: 50 }}>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-green-600 hover:bg-green-700"
            style={{ zIndex: 51 }}
          >
            {isSubmitting ? "Adding Employee..." : "Add Employee"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={handleCancel}
            className="flex-1"
            style={{ zIndex: 51 }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

// Edit Expense Form Component
const EditExpenseForm = ({ expense, onExpenseUpdated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const [formData, setFormData] = useState({
    description: expense.description || '',
    category: expense.category || 'supplies',
    amount: expense.amount?.toString() || '',
    date: expense.date ? expense.date.split('T')[0] : new Date().toISOString().split('T')[0],
    payment_method: expense.payment_method === 'bank_transfer' ? 'bank_account' : (expense.payment_method || 'cash'),
    bank_account_id: expense.bank_account_id || '',
    vendor: expense.vendor || '',
    receipt_number: expense.receipt_number || '',
    notes: expense.notes || ''
  });

  useEffect(() => {
    const loadBankAccounts = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/bank-accounts`);
        setBankAccounts(response.data || []);
      } catch (error) {
        console.error('Error loading bank accounts:', error);
        setBankAccounts([]);
      }
    };
    loadBankAccounts();
  }, [backendUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.description.trim() || !formData.amount) {
        toast.error("Please enter description and amount");
        return;
      }

      if (formData.payment_method === 'bank_account' && !formData.bank_account_id) {
        toast.error("Please select a bank account");
        return;
      }

      const updateData = {
        description: formData.description.trim(),
        category: formData.category,
        amount: parseFloat(formData.amount),
        date: formData.date,
        payment_method: formData.payment_method,
        bank_account_id: formData.payment_method === 'bank_account' ? formData.bank_account_id : null,
        vendor: formData.vendor.trim(),
        receipt_number: formData.receipt_number.trim(),
        notes: formData.notes.trim()
      };

      onExpenseUpdated({ ...expense, ...updateData });
    } catch (error) {
      console.error('Error updating expense:', error);
      
      if (error.response && error.response.data) {
        if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            const errorMessages = error.response.data.detail.map(err => 
              `${err.loc.join('.')}: ${err.msg}`
            ).join(', ');
            toast.error(`Validation error: ${errorMessages}`);
          } else {
            toast.error(`Error: ${error.response.data.detail}`);
          }
        } else {
          toast.error('Failed to update expense - server error');
        }
      } else {
        toast.error('Failed to update expense - network error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Edit Expense</DialogTitle>
        <DialogDescription>
          Update expense information
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="description">Description *</Label>
          <Input
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="What was purchased/paid for"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">Category</Label>
            <Select name="category" value={formData.category} onValueChange={(value) => setFormData(prev => ({...prev, category: value}))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="supplies">Supplies</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="rent">Rent</SelectItem>
                <SelectItem value="utilities">Utilities</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="amount">Amount (₹) *</Label>
            <Input
              id="amount"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <Label htmlFor="payment_method">Payment Method</Label>
            <Select name="payment_method" value={formData.payment_method} onValueChange={(value) => setFormData(prev => ({...prev, payment_method: value}))}>
              <SelectTrigger data-testid="edit-expense-payment-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank_account">Bank Account</SelectItem>
                <SelectItem value="credit">Credit (Pay Later)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {formData.payment_method === 'bank_account' && (
          <div>
            <Label htmlFor="bank_account_id">Select Bank Account *</Label>
            <Select
              name="bank_account_id"
              value={formData.bank_account_id}
              onValueChange={(value) => setFormData(prev => ({...prev, bank_account_id: value}))}
            >
              <SelectTrigger className="mt-2" data-testid="edit-expense-bank-account-select">
                <SelectValue placeholder="Choose bank account" />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.bank_name} - **** {account.account_number?.slice(-4)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label htmlFor="vendor">Vendor/Supplier</Label>
          <Input
            id="vendor"
            name="vendor"
            value={formData.vendor}
            onChange={handleInputChange}
            placeholder="Company or person paid to"
          />
        </div>

        <div>
          <Label htmlFor="receipt_number">Receipt Number</Label>
          <Input
            id="receipt_number"
            name="receipt_number"
            value={formData.receipt_number}
            onChange={handleInputChange}
            placeholder="Receipt or invoice number"
          />
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Additional notes"
            rows="2"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onExpenseUpdated(expense)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? 'Updating...' : 'Update Expense'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Simple Working Add Part Form - Based on working Purchase Order pattern
const AddPartForm = ({ onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  // Simple React Hook Form implementation (following working pattern)
  const { 
    register, 
    handleSubmit, 
    control,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      part_name: '',
      part_number: '',
      brand: '',
      description: '',
      category: 'others',
      supplier: '',
      cost_price: '',
      selling_price: '',
      stock_quantity: '',
      minimum_stock: '',
      location: ''
    }
  });

  const categories = [
    { value: 'engine', label: 'Engine' },
    { value: 'transmission', label: 'Transmission' },
    { value: 'brakes', label: 'Brakes' },
    { value: 'suspension', label: 'Suspension' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'body', label: 'Body' },
    { value: 'interior', label: 'Interior' },
    { value: 'exhaust', label: 'Exhaust' },
    { value: 'cooling', label: 'Cooling' },
    { value: 'fuel_system', label: 'Fuel System' },
    { value: 'ac_system', label: 'AC System' },
    { value: 'filters', label: 'Filters' },
    { value: 'oils_fluids', label: 'Oils & Fluids' },
    { value: 'tires', label: 'Tires' },
    { value: 'others', label: 'Others' }
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      const payload = {
        ...data,
        cost_price: parseFloat(data.cost_price) || 0,
        selling_price: parseFloat(data.selling_price) || 0,
        stock_quantity: parseFloat(data.stock_quantity) || 0,
        minimum_stock: parseFloat(data.minimum_stock) || 0,
        category: data.category || 'others',
        // Add empty arrays for compatibility
        compatible_makes: [],
        compatible_models: [],
        compatible_series: [],
        compatible_years: []
      };

      console.log('Adding part:', payload);

      const response = await axios.post(`${backendUrl}/api/parts`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Part added successfully:', response.data);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      reset();
      
    } catch (error) {
      console.error('Error adding part:', error);
      
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Failed to add part. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Part Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="part_name">Part Name *</Label>
            <Input
              id="part_name"
              placeholder="e.g., Engine Oil Filter"
              {...register('part_name', {
                required: 'Part name is required',
                minLength: { value: 2, message: 'Part name must be at least 2 characters' }
              })}
              className={`mt-1 ${errors.part_name ? 'border-red-500' : ''}`}
            />
            {errors.part_name && (
              <p className="text-sm text-red-600 mt-1">{errors.part_name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="part_number">Part Number</Label>
            <Input
              id="part_number"
              placeholder="Manufacturer part number"
              {...register('part_number')}
              className="mt-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="brand">Brand</Label>
            <Input
              id="brand"
              placeholder="Brand name"
              {...register('brand')}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="category">Category *</Label>
            <Controller
              name="category"
              control={control}
              rules={{ required: 'Please select a category' }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className={`mt-1 ${errors.category ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.category && (
              <p className="text-sm text-red-600 mt-1">{errors.category.message}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Part description and specifications"
            {...register('description')}
            className="mt-1"
            rows="2"
          />
        </div>

        <div>
          <Label htmlFor="supplier">Supplier</Label>
          <Input
            id="supplier"
            placeholder="Supplier name"
            {...register('supplier')}
            className="mt-1"
          />
        </div>

        {/* Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cost_price">Cost Price (₹) *</Label>
            <Input
              id="cost_price"
              type="number"
              placeholder="0.00"
              min="0"
              step="0.01"
              {...register('cost_price', {
                required: 'Cost price is required',
                min: { value: 0, message: 'Cost price must be positive' }
              })}
              className={`mt-1 ${errors.cost_price ? 'border-red-500' : ''}`}
            />
            {errors.cost_price && (
              <p className="text-sm text-red-600 mt-1">{errors.cost_price.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="selling_price">Selling Price (₹) *</Label>
            <Input
              id="selling_price"
              type="number"
              placeholder="0.00"
              min="0"
              step="0.01"
              {...register('selling_price', {
                required: 'Selling price is required',
                min: { value: 0, message: 'Selling price must be positive' }
              })}
              className={`mt-1 ${errors.selling_price ? 'border-red-500' : ''}`}
            />
            {errors.selling_price && (
              <p className="text-sm text-red-600 mt-1">{errors.selling_price.message}</p>
            )}
          </div>
        </div>

        {/* Stock Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="stock_quantity">Stock Quantity *</Label>
            <Input
              id="stock_quantity"
              type="number"
              step="any"
              placeholder="0"
              min="0"
              {...register('stock_quantity', {
                required: 'Stock quantity is required',
                min: { value: 0, message: 'Stock quantity cannot be negative' }
              })}
              className={`mt-1 ${errors.stock_quantity ? 'border-red-500' : ''}`}
            />
            {errors.stock_quantity && (
              <p className="text-sm text-red-600 mt-1">{errors.stock_quantity.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="minimum_stock">Minimum Stock *</Label>
            <Input
              id="minimum_stock"
              type="number"
              step="any"
              placeholder="0"
              min="0"
              {...register('minimum_stock', {
                required: 'Minimum stock is required',
                min: { value: 0, message: 'Minimum stock cannot be negative' }
              })}
              className={`mt-1 ${errors.minimum_stock ? 'border-red-500' : ''}`}
            />
            {errors.minimum_stock && (
              <p className="text-sm text-red-600 mt-1">{errors.minimum_stock.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              placeholder="Shelf/bin location"
              {...register('location')}
              className="mt-1"
            />
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? "Adding Part..." : "Add Part"}
          </Button>
          <Button 
            type="button" 
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};
// Edit Part Form Component
const EditPartForm = ({ part, onSuccess, onCancel }) => {
  const [partFormData, setPartFormData] = useState({
    part_name: part.part_name || '',
    description: part.description || '',
    part_number: part.part_number || '',
    category: part.category || 'others',
    supplier: part.supplier || '',
    cost_price: part.cost_price || '',
    selling_price: part.selling_price || '',
    stock_quantity: part.stock_quantity || '',
    minimum_stock: part.minimum_stock || '',
    location: part.location || '',
    brand: part.brand || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Vehicle compatibility cascade (MAKE -> MODEL -> SERIES -> YEAR), mirrors Add Part form
  const [selectedMake, setSelectedMake] = useState(part.compatible_makes?.[0] || '');
  const [selectedModel, setSelectedModel] = useState(part.compatible_models?.[0] || '');
  const [selectedSeries, setSelectedSeries] = useState(part.compatible_series?.[0] || '');
  const [compatYear, setCompatYear] = useState(part.compatible_years?.[0] ? String(part.compatible_years[0]) : '');
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);

  useEffect(() => {
    axios.get(`${backendUrl}/api/vehicle-data/makes`)
      .then(res => setAvailableMakes((res.data.makes || []).filter(m => m && m.trim())))
      .catch(() => setAvailableMakes([]));
  }, [backendUrl]);

  useEffect(() => {
    if (!selectedMake) { setAvailableModels([]); return; }
    axios.get(`${backendUrl}/api/vehicle-data/models/${encodeURIComponent(selectedMake)}`)
      .then(res => setAvailableModels((res.data.models || []).filter(m => m && m.trim())))
      .catch(() => setAvailableModels([]));
  }, [selectedMake, backendUrl]);

  useEffect(() => {
    if (!selectedMake || !selectedModel) { setAvailableSeries([]); return; }
    axios.get(`${backendUrl}/api/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`)
      .then(res => setAvailableSeries((res.data.series || []).filter(s => s && s.trim())))
      .catch(() => setAvailableSeries([]));
  }, [selectedMake, selectedModel, backendUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!partFormData.part_name.trim()) {
        toast.error("Please enter part name");
        setIsSubmitting(false);
        return;
      }

      const partData = {
        part_name: partFormData.part_name.trim(),
        part_number: partFormData.part_number.trim(),
        description: partFormData.description.trim(),
        category: partFormData.category,
        supplier: partFormData.supplier.trim(),
        cost_price: parseFloat(partFormData.cost_price) || 0,
        selling_price: parseFloat(partFormData.selling_price) || 0,
        stock_quantity: parseFloat(partFormData.stock_quantity) || 0,
        minimum_stock: parseFloat(partFormData.minimum_stock) || 0,
        location: partFormData.location.trim(),
        brand: partFormData.brand.trim(),
        compatible_makes: selectedMake ? [selectedMake.trim()] : [],
        compatible_models: selectedModel ? [selectedModel.trim()] : [],
        compatible_series: selectedSeries ? [selectedSeries.trim()] : [],
        compatible_years: compatYear ? [parseInt(compatYear)] : []
      };

      console.log('Updating part data:', partData);

      const response = await apiFetch(`${backendUrl}/api/parts/${part.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(partData),
      });

      if (response.ok) {
        toast.success('Part updated successfully!');
        onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to update part');
      }
    } catch (error) {
      console.error('Error updating part:', error);
      toast.error('Failed to update part');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories = [
    { value: 'engine', label: 'Engine' },
    { value: 'transmission', label: 'Transmission' },
    { value: 'brakes', label: 'Brakes' },
    { value: 'suspension', label: 'Suspension' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'body', label: 'Body' },
    { value: 'interior', label: 'Interior' },
    { value: 'exhaust', label: 'Exhaust' },
    { value: 'cooling', label: 'Cooling' },
    { value: 'fuel_system', label: 'Fuel System' },
    { value: 'ac_system', label: 'AC System' },
    { value: 'filters', label: 'Filters' },
    { value: 'oils_fluids', label: 'Oils & Fluids' },
    { value: 'tires', label: 'Tires' },
    { value: 'others', label: 'Others' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Edit Part</h2>
        <p className="text-muted-foreground">Update part information</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="part_name">Part Name *</Label>
            <Input
              id="part_name"
              value={partFormData.part_name}
              onChange={(e) => setPartFormData((prev) => ({...prev, part_name: e.target.value}))}
              placeholder="e.g., Brake Pad Set"
              required
            />
          </div>
          <div>
            <Label htmlFor="part_number">Part Number</Label>
            <Input
              id="part_number"
              value={partFormData.part_number}
              onChange={(e) => setPartFormData({...partFormData, part_number: e.target.value})}
              placeholder="Manufacturer part number"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={partFormData.description}
            onChange={(e) => setPartFormData((prev) => ({...prev, description: e.target.value}))}
            placeholder="Part description and specifications"
            rows="2"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">Category</Label>
            <Select value={partFormData.category} onValueChange={(value) => setPartFormData({...partFormData, category: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="brand">Brand</Label>
            <Input
              id="brand"
              value={partFormData.brand}
              onChange={(e) => setPartFormData({...partFormData, brand: e.target.value})}
              placeholder="Brand name"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="supplier">Supplier</Label>
          <Input
            id="supplier"
            value={partFormData.supplier}
            onChange={(e) => setPartFormData({...partFormData, supplier: e.target.value})}
            placeholder="Supplier name"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cost_price">Cost Price (₹)</Label>
            <Input
              id="cost_price"
              type="number"
              value={partFormData.cost_price}
              onChange={(e) => setPartFormData({...partFormData, cost_price: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <Label htmlFor="selling_price">Selling Price (₹)</Label>
            <Input
              id="selling_price"
              type="number"
              value={partFormData.selling_price}
              onChange={(e) => setPartFormData({...partFormData, selling_price: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="stock_quantity">Stock Quantity</Label>
            <Input
              id="stock_quantity"
              type="number"
              step="any"
              value={partFormData.stock_quantity}
              onChange={(e) => setPartFormData({...partFormData, stock_quantity: e.target.value})}
              placeholder="0"
              min="0"
            />
          </div>
          <div>
            <Label htmlFor="minimum_stock">Minimum Stock</Label>
            <Input
              id="minimum_stock"
              type="number"
              step="any"
              value={partFormData.minimum_stock}
              onChange={(e) => setPartFormData({...partFormData, minimum_stock: e.target.value})}
              placeholder="0"
              min="0"
            />
          </div>
          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={partFormData.location}
              onChange={(e) => setPartFormData({...partFormData, location: e.target.value})}
              placeholder="Shelf/bin location"
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <h4 className="text-sm font-medium mb-1">Vehicle Compatibility (Optional)</h4>
          <p className="text-xs text-muted-foreground mb-3">Select compatible vehicle MAKE → MODEL → SERIES → YEAR for this part</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>MAKE</Label>
              <Select
                value={selectedMake}
                onValueChange={(value) => { setSelectedMake(value); setSelectedModel(''); setSelectedSeries(''); }}
              >
                <SelectTrigger data-testid="edit-part-make-select" className="mt-1 bg-white">
                  <SelectValue placeholder="Select MAKE" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {[...new Set([selectedMake, ...availableMakes].filter(Boolean))].map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>MODEL</Label>
              <Select
                value={selectedModel}
                onValueChange={(value) => { setSelectedModel(value); setSelectedSeries(''); }}
                disabled={!selectedMake}
              >
                <SelectTrigger data-testid="edit-part-model-select" className="mt-1 bg-white">
                  <SelectValue placeholder={selectedMake ? "Select MODEL" : "Select MAKE first"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {[...new Set([selectedModel, ...availableModels].filter(Boolean))].map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                  {selectedMake && <SelectItem value="Other Model">Other {selectedMake} Model</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>SERIES</Label>
              <Select
                value={selectedSeries}
                onValueChange={(value) => setSelectedSeries(value)}
                disabled={!selectedMake || !selectedModel}
              >
                <SelectTrigger data-testid="edit-part-series-select" className="mt-1 bg-white">
                  <SelectValue placeholder={!selectedMake ? "Select MAKE first" : !selectedModel ? "Select MODEL first" : "Select SERIES"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {[...new Set([selectedSeries, ...availableSeries].filter(Boolean))].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                  {selectedMake && selectedModel && <SelectItem value="Other Series">Other {selectedModel} Series</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="compat_year">YEAR</Label>
              <Input
                id="compat_year"
                type="number"
                min="1990"
                max="2030"
                value={compatYear}
                onChange={(e) => setCompatYear(e.target.value)}
                placeholder="e.g., 2020"
                data-testid="edit-part-year-input"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? "Updating Part..." : "Update Part"}
          </Button>
          <Button 
            type="button" 
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

// Invoice Item Row Component with Edit Functionality
const EditEmployeeForm = ({ employee, onEmployeeUpdated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: employee.name || '',
    position: employee.position || '',
    contact: employee.contact || employee.phone || '',
    email: employee.email || '',
    salary: employee.salary?.toString() || '',
    hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : new Date().toISOString().split('T')[0],
    skills: employee.skills || '',
    notes: employee.notes || ''
  });

  const handleEditEmployeeInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.name.trim() || !formData.position.trim()) {
        toast.error("Please enter employee name and position");
        return;
      }

      const updateData = {
        id: employee.id,  // Include ID for the API call
        name: formData.name.trim(),
        position: formData.position.trim(), 
        contact: formData.contact.trim(),
        email: formData.email.trim(),
        salary: parseFloat(formData.salary) || 0,
        date_joined: formData.hire_date,
        skills: formData.skills.trim(),
        notes: formData.notes.trim()
      };

      // Call the API to update employee
      await onEmployeeUpdated(updateData);
    } catch (error) {
      console.error('Error updating employee:', error);
      
      if (error.response && error.response.data) {
        if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            const errorMessages = error.response.data.detail.map(err => 
              `${err.loc.join('.')}: ${err.msg}`
            ).join(', ');
            toast.error(`Validation error: ${errorMessages}`);
          } else {
            toast.error(`Error: ${error.response.data.detail}`);
          }
        } else {
          toast.error('Failed to update employee - server error');
        }
      } else {
        toast.error('Failed to update employee - network error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Edit Employee</DialogTitle>
        <DialogDescription>
          Update employee information
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleEditEmployeeInputChange}
              placeholder="Employee name"
              required
            />
          </div>
          <div>
            <Label htmlFor="position">Position *</Label>
            <Input
              id="position"
              name="position"
              value={formData.position}
              onChange={handleEditEmployeeInputChange}
              placeholder="Job title"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contact">Contact Number</Label>
            <Input
              id="contact"
              name="contact"
              value={formData.contact}
              onChange={handleEditEmployeeInputChange}
              placeholder="Phone number"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleEditEmployeeInputChange}
              placeholder="employee@email.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="salary">Salary (₹/month)</Label>
            <Input
              id="salary"
              name="salary"
              type="number"
              value={formData.salary}
              onChange={handleEditEmployeeInputChange}
              placeholder="Monthly salary"
              min="0"
              step="100"
            />
          </div>
          <div>
            <Label htmlFor="hire_date">Hire Date</Label>
            <Input
              id="hire_date"
              name="hire_date"
              type="date"
              value={formData.hire_date}
              onChange={handleEditEmployeeInputChange}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="skills">Skills & Expertise</Label>
          <Textarea
            id="skills"
            name="skills"
            value={formData.skills}
            onChange={handleEditEmployeeInputChange}
            placeholder="Technical skills, certifications, areas of expertise"
            rows="2"
          />
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleEditEmployeeInputChange}
            placeholder="Additional notes about the employee"
            rows="2"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onEmployeeUpdated(employee)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? 'Updating...' : 'Update Employee'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Add Existing Items Form (unified for services and parts, usable in Invoice / Quotation / etc.)
const EditServiceForm = ({ service, onServiceUpdated }) => {
  const [editServiceFormData, setEditServiceFormData] = useState({
    name: service.name || '',
    description: service.description || '',
    category: service.category || 'maintenance',
    duration: service.duration?.toString() || '',
    price: service.price?.toString() || '',
    skill_level: service.skill_level || 'intermediate',
    sac_code: service.sac_code || '',
    gst_rate: service.gst_rate != null ? String(service.gst_rate) : '18'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const updateData = {
        ...editServiceFormData,
        duration: parseFloat(editServiceFormData.duration) || 0,
        price: parseFloat(editServiceFormData.price) || 0,
        gst_rate: parseFloat(editServiceFormData.gst_rate) || 0,
        sac_code: editServiceFormData.sac_code?.trim() || null
      };

      const response = await apiFetch(`${backendUrl}/api/services/${service.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update service');
      }
      const updatedService = await response.json();

      toast.success('Service updated successfully!');
      onServiceUpdated(updatedService);
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error(error.message || 'Failed to update service');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Edit Service</DialogTitle>
        <DialogDescription>
          Update service details and pricing
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Service Name</Label>
            <Input
              id="name"
              name="name"
              value={editServiceFormData.name}
              onChange={(e) => setEditServiceFormData({...editServiceFormData, name: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="edit-service-category">Category</Label>
            <Select value={editServiceFormData.category} onValueChange={(value) => setEditServiceFormData({...editServiceFormData, category: value})}>
              <SelectTrigger id="edit-service-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="repair">Repair</SelectItem>
                <SelectItem value="diagnostic">Diagnostic</SelectItem>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="bodywork">Body Work</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            name="description"
            value={editServiceFormData.description}
            onChange={(e) => setEditServiceFormData({...editServiceFormData, description: e.target.value})}
            rows="3"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="duration">Duration (hours)</Label>
            <Input
              id="duration"
              name="duration"
              type="number"
              value={editServiceFormData.duration}
              onChange={(e) => setEditServiceFormData({...editServiceFormData, duration: e.target.value})}
              min="0"
              step="0.5"
            />
          </div>
          <div>
            <Label htmlFor="price">Price (₹)</Label>
            <Input
              id="price"
              name="price"
              type="number"
              value={editServiceFormData.price}
              onChange={(e) => setEditServiceFormData({...editServiceFormData, price: e.target.value})}
              min="0"
              step="50"
            />
          </div>
          <div>
            <Label htmlFor="edit-service-skill-level">Skill Level</Label>
            <Select value={editServiceFormData.skill_level} onValueChange={(value) => setEditServiceFormData({...editServiceFormData, skill_level: value})}>
              <SelectTrigger id="edit-service-skill-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
                <SelectItem value="expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="edit-sac_code">SAC Code (GST)</Label>
            <Input
              id="edit-sac_code"
              name="sac_code"
              value={editServiceFormData.sac_code}
              onChange={(e) => setEditServiceFormData({...editServiceFormData, sac_code: e.target.value})}
              placeholder="e.g. 998714"
              data-testid="edit-service-sac-input"
            />
          </div>
          <div>
            <Label htmlFor="edit-service-gst-rate">GST Rate (%)</Label>
            <Select value={editServiceFormData.gst_rate} onValueChange={(value) => setEditServiceFormData({...editServiceFormData, gst_rate: value})}>
              <SelectTrigger id="edit-service-gst-rate" data-testid="edit-service-gst-rate">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0% (Exempt)</SelectItem>
                <SelectItem value="5">5%</SelectItem>
                <SelectItem value="12">12%</SelectItem>
                <SelectItem value="18">18%</SelectItem>
                <SelectItem value="28">28%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? "Updating..." : "Update Service"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const PartsInventorySection = ({ onUpdate }) => {
  const { user } = useAuth();
  const isBasic = user?.role === 'basic_user';
  const [parts, setParts] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  
  // Vehicle filter states
  const [vehicleMakeFilter, setVehicleMakeFilter] = useState('all');
  const [vehicleModelFilter, setVehicleModelFilter] = useState('all');
  const [vehicleSeriesFilter, setVehicleSeriesFilter] = useState('all');
  
  // Dynamic vehicle data for filters
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  const [vehicleDataLoading, setVehicleDataLoading] = useState(false);
  
  const [statistics, setStatistics] = useState({});
  const [selectedPart, setSelectedPart] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [batchHistoryOpen, setBatchHistoryOpen] = useState(false);
  const [batchHistoryLoading, setBatchHistoryLoading] = useState(false);
  const [batchHistoryData, setBatchHistoryData] = useState(null);

  // Bulk compatibility tagging
  const [selectedPartIds, setSelectedPartIds] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkModelsMake, setBulkModelsMake] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Fetch parts data
  const fetchParts = async () => {
    try {
      const response = await apiFetch(`${backendUrl}/api/parts`);
      if (response.ok) {
        const partsData = await response.json();
        setParts(partsData);
        setFilteredParts(partsData);
      } else {
        console.error('Failed to fetch parts');
      }
    } catch (error) {
      console.error('Error fetching parts:', error);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const response = await apiFetch(`${backendUrl}/api/parts/stats`);
      if (response.ok) {
        const stats = await response.json();
        setStatistics(stats);
      } else {
        console.error('Failed to fetch statistics');
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch vehicle makes for filters
  const fetchVehicleMakes = useCallback(async () => {
    try {
      setVehicleDataLoading(true);
      const response = await axios.get(`${backendUrl}/api/vehicle-data/makes`);
      const makes = (response.data.makes || []).filter(make => make && make.trim() !== '');
      setAvailableMakes(makes);
    } catch (error) {
      console.error('Error fetching vehicle makes:', error);
      setAvailableMakes([]);
    } finally {
      setVehicleDataLoading(false);
    }
  }, [backendUrl]);

  // Fetch vehicle models for selected make
  const fetchVehicleModels = useCallback(async (make) => {
    if (!make || make === 'all') {
      setAvailableModels([]);
      return;
    }

    try {
      const response = await axios.get(`${backendUrl}/api/vehicle-data/models/${encodeURIComponent(make)}`);
      const models = (response.data.models || []).filter(model => model && model.trim() !== '');
      setAvailableModels(models);
    } catch (error) {
      console.error('Error fetching vehicle models:', error);
      setAvailableModels([]);
    }
  }, [backendUrl]);

  // Fetch vehicle series for selected make and model
  const fetchVehicleSeries = useCallback(async (make, model) => {
    if (!make || !model || make === 'all' || model === 'all') {
      setAvailableSeries([]);
      return;
    }

    try {
      const response = await axios.get(`${backendUrl}/api/vehicle-data/series/${encodeURIComponent(make)}/${encodeURIComponent(model)}`);
      const series = (response.data.series || []).filter(s => s && s.trim() !== '');
      setAvailableSeries(series);
    } catch (error) {
      console.error('Error fetching vehicle series:', error);
      setAvailableSeries([]);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchParts();
    fetchStatistics();
    fetchVehicleMakes();
  }, [fetchVehicleMakes]);

  // Listen for vehicle data updates
  useEffect(() => {
    const handleVehicleDataUpdate = () => {
      fetchVehicleMakes();
      if (vehicleMakeFilter !== 'all') {
        fetchVehicleModels(vehicleMakeFilter);
      }
      if (vehicleMakeFilter !== 'all' && vehicleModelFilter !== 'all') {
        fetchVehicleSeries(vehicleMakeFilter, vehicleModelFilter);
      }
    };

    window.addEventListener('vehicleDataUpdated', handleVehicleDataUpdate);
    return () => window.removeEventListener('vehicleDataUpdated', handleVehicleDataUpdate);
  }, [vehicleMakeFilter, vehicleModelFilter, fetchVehicleMakes, fetchVehicleModels, fetchVehicleSeries]);

  // Fetch models when make filter changes
  useEffect(() => {
    fetchVehicleModels(vehicleMakeFilter);
  }, [vehicleMakeFilter, fetchVehicleModels]);

  // Fetch series when make or model filter changes
  useEffect(() => {
    fetchVehicleSeries(vehicleMakeFilter, vehicleModelFilter);
  }, [vehicleMakeFilter, vehicleModelFilter, fetchVehicleSeries]);

  // Filter parts based on search and filters
  useEffect(() => {
    let filtered = parts;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(part =>
        part.part_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        part.part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        part.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        part.brand?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(part => part.category === categoryFilter);
    }

    // Stock filter
    if (stockFilter === 'low') {
      filtered = filtered.filter(part => part.stock_quantity < part.minimum_stock);
    } else if (stockFilter === 'out') {
      filtered = filtered.filter(part => part.stock_quantity === 0);
    } else if (stockFilter === 'good') {
      filtered = filtered.filter(part => part.stock_quantity >= part.minimum_stock);
    }

    // Vehicle compatibility filters
    if (vehicleMakeFilter !== 'all') {
      filtered = filtered.filter(part => {
        const compatibleMakes = part.compatible_makes || [];
        return compatibleMakes.includes(vehicleMakeFilter);
      });
    }

    if (vehicleModelFilter !== 'all') {
      filtered = filtered.filter(part => {
        const compatibleModels = part.compatible_models || [];
        return compatibleModels.includes(vehicleModelFilter);
      });
    }

    if (vehicleSeriesFilter !== 'all') {
      filtered = filtered.filter(part => {
        const compatibleSeries = part.compatible_series || [];
        return compatibleSeries.includes(vehicleSeriesFilter);
      });
    }

    setFilteredParts(filtered);
  }, [parts, searchTerm, categoryFilter, stockFilter, vehicleMakeFilter, vehicleModelFilter, vehicleSeriesFilter]);

  // Format currency in Indian Rupees
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Get stock status
  const getStockStatus = (part) => {
    if (part.stock_quantity === 0) {
      return { status: 'out', label: 'Out of Stock', color: 'destructive' };
    } else if (part.stock_quantity < part.minimum_stock) {
      return { status: 'low', label: 'Low Stock', color: 'warning' };
    } else {
      return { status: 'good', label: 'In Stock', color: 'success' };
    }
  };

  // Handle part deletion
  const handleDeletePart = async (partId) => {
    if (window.confirm('Are you sure you want to delete this part?')) {
      try {
        const response = await apiFetch(`${backendUrl}/api/parts/${partId}`, {
          method: 'DELETE',
        });
        
        if (response.ok) {
          await fetchParts();
          await fetchStatistics();
          if (onUpdate) onUpdate();
        } else {
          console.error('Failed to delete part');
        }
      } catch (error) {
        console.error('Error deleting part:', error);
      }
    }
  };

  // Bulk compatibility tagging — select multiple parts and mark them universal
  const togglePartSelected = (partId) => {
    setSelectedPartIds((prev) =>
      prev.includes(partId) ? prev.filter((id) => id !== partId) : [...prev, partId]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredParts.map((p) => p.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedPartIds.includes(id));
    setSelectedPartIds(allSelected ? [] : visibleIds);
  };

  const clearSelection = () => setSelectedPartIds([]);

  const bulkUpdateCompatibility = async (payload, successLabel) => {
    if (selectedPartIds.length === 0) return;
    setBulkUpdating(true);
    try {
      const token = localStorage.getItem('auth_token');
      let successCount = 0;
      for (const id of selectedPartIds) {
        const res = await fetch(`${backendUrl}/api/parts/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (res.ok) successCount++;
      }
      toast.success(`${successLabel}: ${successCount} of ${selectedPartIds.length} part(s) updated`);
      clearSelection();
      setBulkModelsMake('');
      await fetchParts();
      await fetchStatistics();
    } catch (error) {
      console.error('Bulk compatibility update failed:', error);
      toast.error('Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkFitsAllMakes = () => {
    if (!window.confirm(`Mark ${selectedPartIds.length} selected part(s) as "Fits All Makes"? This clears their existing Make/Model/Series compatibility.`)) return;
    bulkUpdateCompatibility({ compatible_makes: null, compatible_models: null, compatible_series: null }, 'Fits All Makes applied');
  };

  const handleBulkFitsAllModels = () => {
    if (!bulkModelsMake) return;
    if (!window.confirm(`Mark ${selectedPartIds.length} selected part(s) as fitting all ${bulkModelsMake} models?`)) return;
    bulkUpdateCompatibility({ compatible_makes: [bulkModelsMake], compatible_models: null, compatible_series: null }, `Fits all ${bulkModelsMake} models applied`);
  };

  // Flatten parts (incl. per-brand rows) into export-ready rows
  const buildPartsExportRows = () => {
    const rows = [];
    filteredParts.forEach((part) => {
      if (Array.isArray(part.brands) && part.brands.length > 0) {
        part.brands.forEach((b) => {
          rows.push({
            part_name: part.part_name,
            part_number: b.part_number || part.part_number || '—',
            brand: b.brand_name || '—',
            selling_price: b.selling_price ?? 0,
            cost_price: b.cost_price ?? 0,
            stock_quantity: b.stock_quantity ?? 0,
          });
        });
      } else {
        rows.push({
          part_name: part.part_name,
          part_number: part.part_number || '—',
          brand: '—',
          selling_price: part.selling_price ?? 0,
          cost_price: part.cost_price ?? 0,
          stock_quantity: part.stock_quantity ?? 0,
        });
      }
    });
    return rows;
  };

  const handleExportPartsCSV = () => {
    const rows = buildPartsExportRows();
    exportRowsToCSV(
      `parts-inventory-${toLocalISODate(new Date())}`,
      ['Part Name', 'Part No', 'Brand', 'Selling Price', 'Buying Price', 'Stock'],
      rows.map(r => [r.part_name, r.part_number, r.brand, r.selling_price, r.cost_price, r.stock_quantity])
    );
  };

  const handlePrintPartsPDF = () => {
    const rows = buildPartsExportRows();
    const tableRows = rows.map(r => `<tr>
      <td>${r.part_name || ''}</td>
      <td>${r.part_number || ''}</td>
      <td>${r.brand || ''}</td>
      <td style="text-align:right">${formatCurrency(r.selling_price)}</td>
      <td style="text-align:right">${formatCurrency(r.cost_price)}</td>
      <td style="text-align:right">${r.stock_quantity}</td>
    </tr>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Parts Inventory</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111} h1{font-size:20px;margin-bottom:4px} p{color:#555;font-size:12px;margin-top:0} table{width:100%;border-collapse:collapse;margin-top:16px} th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;font-size:12px} th{background:#f3f4f6}</style>
      </head><body>
      <h1>Parts Inventory Report</h1>
      <p>Generated on ${new Date().toLocaleString('en-IN')} — ${rows.length} item(s)</p>
      <table><thead><tr><th>Part Name</th><th>Part No</th><th>Brand</th><th>Selling Price</th><th>Buying Price</th><th>Stock</th></tr></thead>
      <tbody>${tableRows}</tbody></table>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  // View the FIFO batch/PO cost history for a part (optionally scoped to one brand)
  const viewBatchHistory = async (part, brand) => {
    setBatchHistoryOpen(true);
    setBatchHistoryLoading(true);
    setBatchHistoryData(null);
    try {
      const qs = brand?.id ? `?brand_id=${encodeURIComponent(brand.id)}` : '';
      const response = await apiFetch(`${backendUrl}/api/parts/${part.id}/inventory-batches${qs}`);
      if (response.ok) {
        const data = await response.json();
        setBatchHistoryData({ ...data, brand_name: brand?.brand_name });
      } else {
        toast.error('Failed to load PO cost history');
      }
    } catch (error) {
      console.error('Error fetching batch history:', error);
      toast.error('Failed to load PO cost history');
    } finally {
      setBatchHistoryLoading(false);
    }
  };

  // Category options
  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'engine', label: 'Engine' },
    { value: 'transmission', label: 'Transmission' },
    { value: 'brakes', label: 'Brakes' },
    { value: 'suspension', label: 'Suspension' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'body', label: 'Body' },
    { value: 'interior', label: 'Interior' },
    { value: 'exhaust', label: 'Exhaust' },
    { value: 'cooling', label: 'Cooling' },
    { value: 'fuel_system', label: 'Fuel System' },
    { value: 'ac_system', label: 'AC System' },
    { value: 'filters', label: 'Filters' },
    { value: 'oils_fluids', label: 'Oils & Fluids' },
    { value: 'tires', label: 'Tires' },
    { value: 'others', label: 'Others' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading parts inventory...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Parts & Inventory</h2>
          <p className="text-muted-foreground">Manage your parts inventory with pricing in Indian Rupees (₹)</p>
        </div>
        <div className="flex items-center gap-2">
          {!isBasic && (
            <>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={handleExportPartsCSV}
                data-testid="parts-export-csv-btn"
              >
                <Download size={16} />
                Export CSV
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={handlePrintPartsPDF}
                data-testid="parts-print-pdf-btn"
              >
                <FileText size={16} />
                Print / PDF
              </Button>
            </>
          )}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          {!isBasic && (
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2" data-testid="add-new-part-btn">
              <Plus size={16} />
              Add New Part
            </Button>
          </DialogTrigger>
          )}
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Part</DialogTitle>
              <DialogDescription>
                Add a new part to your inventory with pricing, stock levels, and vehicle compatibility information.
              </DialogDescription>
            </DialogHeader>
            <div className="p-4">
              <MasterPartForm
                onSuccess={() => {
                  setShowAddDialog(false);
                  fetchParts();
                  fetchStatistics();
                  if (onUpdate) onUpdate();
                }}
                onCancel={() => setShowAddDialog(false)}
              />
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Parts</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.total_parts || 0}</div>
            <p className="text-xs text-muted-foreground">Active inventory items</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-in-stock">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Stock</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statistics.in_stock_count || 0}
            </div>
            <p className="text-xs text-muted-foreground">At or above minimum</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {statistics.low_stock_count || 0}
            </div>
            <p className="text-xs text-muted-foreground">Below minimum level</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Out of Stock</CardTitle>
            <PackageX className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {statistics.out_of_stock_count || 0}
            </div>
            <p className="text-xs text-muted-foreground">Zero inventory</p>
          </CardContent>
        </Card>

        {!isBasic && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inventory Value</CardTitle>
            <Package className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(statistics.total_inventory_value || 0)}
            </div>
            <p className="text-xs text-muted-foreground">Total stock value</p>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* First Row - Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  placeholder="Search parts by name, number, description, or brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 no-uppercase"
                />
              </div>
            </div>
            
            {/* Second Row - Category and Stock Filters */}
            <div className="flex flex-col lg:flex-row gap-4">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full lg:w-48">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={stockFilter} onValueChange={setStockFilter}>
                <SelectTrigger className="w-full lg:w-48">
                  <SelectValue placeholder="Stock Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stock</SelectItem>
                  <SelectItem value="good">In Stock</SelectItem>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="out">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Third Row - Vehicle Compatibility Filters */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                🚗 Filter by Vehicle Compatibility
                <span className="text-xs text-gray-500">(Find parts compatible with specific vehicles)</span>
              </h4>
              <div className="flex flex-col lg:flex-row gap-4">
                {/* MAKE Filter */}
                <div className="flex-1">
                  <Select 
                    value={vehicleMakeFilter} 
                    onValueChange={(value) => {
                      setVehicleMakeFilter(value);
                      setVehicleModelFilter('all');
                      setVehicleSeriesFilter('all');
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Makes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Makes</SelectItem>
                      {vehicleDataLoading ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          Loading makes...
                        </div>
                      ) : availableMakes.length > 0 ? (
                        availableMakes.map((make) => (
                          <SelectItem key={make} value={make}>
                            {make}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No makes available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* MODEL Filter */}
                <div className="flex-1">
                  <Select 
                    value={vehicleModelFilter} 
                    onValueChange={(value) => {
                      setVehicleModelFilter(value);
                      setVehicleSeriesFilter('all');
                    }}
                    disabled={vehicleMakeFilter === 'all'}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={
                        vehicleMakeFilter === 'all' ? "Select Make first" : "All Models"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Models</SelectItem>
                      {availableModels.length > 0 ? (
                        availableModels.map((model) => (
                          <SelectItem key={model} value={model}>
                            {model}
                          </SelectItem>
                        ))
                      ) : vehicleMakeFilter !== 'all' ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No models available for {vehicleMakeFilter}
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* SERIES Filter */}
                <div className="flex-1">
                  <Select 
                    value={vehicleSeriesFilter} 
                    onValueChange={setVehicleSeriesFilter}
                    disabled={vehicleMakeFilter === 'all' || vehicleModelFilter === 'all'}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={
                        vehicleMakeFilter === 'all' ? "Select Make first" :
                        vehicleModelFilter === 'all' ? "Select Model first" :
                        "All Series"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Series</SelectItem>
                      {availableSeries.length > 0 ? (
                        availableSeries.map((series) => (
                          <SelectItem key={series} value={series}>
                            {series}
                          </SelectItem>
                        ))
                      ) : vehicleMakeFilter !== 'all' && vehicleModelFilter !== 'all' ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No series available for {vehicleModelFilter}
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Reset Vehicle Filters Button */}
                {(vehicleMakeFilter !== 'all' || vehicleModelFilter !== 'all' || vehicleSeriesFilter !== 'all') && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setVehicleMakeFilter('all');
                      setVehicleModelFilter('all');
                      setVehicleSeriesFilter('all');
                    }}
                    className="whitespace-nowrap"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Compatibility Tagging Bar */}
      {selectedPartIds.length > 0 && (
        <Card className="border-blue-300 bg-blue-50" data-testid="bulk-compat-bar">
          <CardContent className="pt-4 pb-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-blue-900" data-testid="bulk-selected-count">
              {selectedPartIds.length} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkFitsAllMakes}
              disabled={bulkUpdating}
              data-testid="bulk-fits-all-makes-btn"
            >
              Mark as Fits All Makes
            </Button>
            <div className="flex items-center gap-2">
              <Select value={bulkModelsMake} onValueChange={setBulkModelsMake} disabled={bulkUpdating}>
                <SelectTrigger className="w-44 bg-white" data-testid="bulk-models-make-select">
                  <SelectValue placeholder="Pick a Make…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableMakes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkFitsAllModels}
                disabled={bulkUpdating || !bulkModelsMake}
                data-testid="bulk-fits-all-models-btn"
              >
                Mark as Fits All Models
              </Button>
            </div>
            <Button size="sm" variant="ghost" onClick={clearSelection} disabled={bulkUpdating} data-testid="bulk-clear-selection-btn">
              Clear selection
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Parts List */}
      <div className="grid gap-4">
        {filteredParts.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold">No parts found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {searchTerm || categoryFilter !== 'all' || stockFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Get started by adding your first part.'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="flex items-center gap-2 px-1">
            <Checkbox
              checked={filteredParts.length > 0 && filteredParts.every((p) => selectedPartIds.includes(p.id))}
              onCheckedChange={toggleSelectAllVisible}
              data-testid="select-all-visible-checkbox"
            />
            <span className="text-xs text-muted-foreground">Select all {filteredParts.length} visible</span>
          </div>
          {[...filteredParts].sort((a, b) => {
            // Alphabetical A → Z by part name (fallback to part number)
            const aKey = a.part_name || a.part_number || '';
            const bKey = b.part_name || b.part_number || '';
            return String(aKey).localeCompare(String(bKey), undefined, { sensitivity: 'base', numeric: true });
          }).map((part) => {
            const stockStatus = getStockStatus(part);
            return (
              <Card key={part.id} className="transition-all hover:shadow-md">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {/* Part Details */}
                    <div className="lg:col-span-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedPartIds.includes(part.id)}
                            onCheckedChange={() => togglePartSelected(part.id)}
                            className="mt-1"
                            data-testid={`part-select-checkbox-${part.id}`}
                          />
                          <div>
                            <h3 className="font-semibold text-lg">{part.part_name}</h3>
                            <p className="text-sm text-muted-foreground">
                              Part #: {part.part_number}
                            </p>
                            {part.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {part.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge variant={stockStatus.color === 'success' ? 'secondary' : 
                               stockStatus.color === 'warning' ? 'outline' : 'destructive'}>
                          {stockStatus.label}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Badge variant="outline">{part.category?.replace('_', ' ')}</Badge>
                        {part.brand && <Badge variant="outline">{part.brand}</Badge>}
                        {part.location && <Badge variant="outline">📍 {part.location}</Badge>}
                      </div>
                    </div>

                    {/* Stock Info */}
                    <div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Current Stock:</span>
                          <span className={`font-medium ${
                            stockStatus.status === 'out' ? 'text-red-600' :
                            stockStatus.status === 'low' ? 'text-orange-600' : 'text-green-600'
                          }`}>
                            {part.stock_quantity}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Min Stock:</span>
                          <span className="font-medium">{part.minimum_stock}</span>
                        </div>
                        {part.supplier && !isBasic && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Supplier:</span>
                            <span className="font-medium text-sm">{part.supplier}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pricing Info */}
                    {!isBasic && (
                    <div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Cost Price:</span>
                          <span className="font-medium text-blue-600">
                            {formatCurrency(part.cost_price)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Sell Price:</span>
                          <span className="font-medium text-green-600">
                            {formatCurrency(part.selling_price)}
                          </span>
                        </div>
                        {part.profit_margin !== undefined && part.profit_margin !== null && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Profit:</span>
                            <span className="font-medium text-green-600">
                              {part.profit_margin.toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {part.total_value !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Stock Value:</span>
                            <span className="font-medium text-purple-600">
                              {formatCurrency(part.total_value)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                  </div>

                  {/* Brand variants breakdown */}
                  {Array.isArray(part.brands) && part.brands.length > 0 && (
                    <div className="mb-4 mt-4" data-testid={`part-brands-${part.id}`}>
                      <p className="text-sm text-muted-foreground mb-2">Brands ({part.brands.length}):</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-muted-foreground border-b">
                              <th className="py-1 pr-4">Brand</th>
                              <th className="py-1 pr-4">Part #</th>
                              {!isBasic && <th className="py-1 pr-4">Supplier</th>}
                              {!isBasic && <th className="py-1 pr-4 text-right">Cost</th>}
                              {!isBasic && <th className="py-1 pr-4 text-right">Sell</th>}
                              <th className="py-1 text-right">Stock</th>
                              {!isBasic && <th className="py-1 pl-4 text-right">Cost History</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {part.brands.map((b, bi) => (
                              <tr key={b.id || bi} className="border-b last:border-0">
                                <td className="py-1 pr-4 font-medium">{b.brand_name}</td>
                                <td className="py-1 pr-4 text-muted-foreground">{b.part_number || '\u2014'}</td>
                                {!isBasic && <td className="py-1 pr-4 text-muted-foreground">{b.supplier || '\u2014'}</td>}
                                {!isBasic && <td className="py-1 pr-4 text-right text-blue-600">{formatCurrency(b.cost_price)}</td>}
                                {!isBasic && <td className="py-1 pr-4 text-right text-green-600">{formatCurrency(b.selling_price)}</td>}
                                <td className={`py-1 text-right font-medium ${
                                  (b.stock_quantity ?? 0) <= 0 ? 'text-red-600'
                                  : (b.stock_quantity ?? 0) <= (b.minimum_stock ?? 0) ? 'text-orange-600'
                                  : 'text-green-600'
                                }`}>{b.stock_quantity}</td>
                                {!isBasic && (
                                  <td className="py-1 pl-4 text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => viewBatchHistory(part, b)}
                                      data-testid={`view-po-history-${part.id}-${b.id || bi}`}
                                    >
                                      <Clock className="h-3 w-3 mr-1" />
                                      View POs
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Vehicle Compatibility */}
                  {((part.compatible_makes && part.compatible_makes.length > 0) ||
                    (part.compatible_models && part.compatible_models.length > 0) ||
                    (part.compatible_series && part.compatible_series.length > 0)) && (
                    <div className="mb-4 mt-4">
                      <p className="text-sm text-muted-foreground mb-2">Compatible Vehicles:</p>
                      <div className="flex flex-wrap gap-1" data-testid={`part-compat-${part.id}`}>
                        {(() => {
                          const makes = part.compatible_makes || [];
                          const models = part.compatible_models || [];
                          const series = part.compatible_series || [];
                          const years = part.compatible_years || [];
                          const rowCount = Math.max(makes.length, models.length, series.length, years.length);
                          const rows = [];
                          for (let i = 0; i < rowCount; i++) {
                            const segs = [makes[i], models[i], series[i], years[i]].filter(Boolean);
                            if (segs.length) rows.push(segs.join(' · '));
                          }
                          return rows.map((label, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {label}
                            </Badge>
                          ));
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {!isBasic && (
                  <div className="flex items-center gap-2 pt-4 border-t">
                    <Dialog open={showEditDialog && selectedPart?.id === part.id} 
                           onOpenChange={(open) => {
                             setShowEditDialog(open);
                             if (!open) setSelectedPart(null);
                           }}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSelectedPart(part)}
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Edit Part - {selectedPart?.part_name}</DialogTitle>
                        </DialogHeader>
                        {selectedPart && (
                          <div className="p-4">
                            <MasterPartForm
                              part={selectedPart}
                              onSuccess={() => {
                                setShowEditDialog(false);
                                setSelectedPart(null);
                                fetchParts();
                                fetchStatistics();
                                if (onUpdate) onUpdate();
                              }}
                              onCancel={() => {
                                setShowEditDialog(false);
                                setSelectedPart(null);
                              }}
                            />
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDeletePart(part.id)}
                      className="text-red-600 hover:text-red-700 hover:border-red-300"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                  )}
                </CardContent>
              </Card>
            );
          })
          }
          </>
        )}
      </div>

      {/* PO Cost History Dialog — shows every FIFO batch/PO a part's stock came from */}
      <Dialog open={batchHistoryOpen} onOpenChange={setBatchHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="po-cost-history-dialog">
          <DialogHeader>
            <DialogTitle>PO Cost History{batchHistoryData?.part_name ? ` — ${batchHistoryData.part_name}` : ''}</DialogTitle>
            <DialogDescription>
              {batchHistoryData?.brand_name ? `Brand: ${batchHistoryData.brand_name}. ` : ''}
              Every Purchase Order batch this part's stock was received from, oldest first (FIFO), with quantity remaining per batch.
            </DialogDescription>
          </DialogHeader>
          {batchHistoryLoading ? (
            <p className="text-sm text-gray-500 text-center py-8">Loading…</p>
          ) : !batchHistoryData || batchHistoryData.batches.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm" data-testid="po-cost-history-empty">
              <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>No Purchase Order batches recorded for this part/brand yet.</p>
              <p className="text-xs mt-1">Batches are created when a Purchase Order is marked "Delivered".</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-500">Total Remaining (from POs)</p>
                  <p className="text-lg font-bold text-gray-800" data-testid="po-cost-history-total-remaining">{batchHistoryData.total_remaining}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <p className="text-xs text-emerald-600">Avg Cost of Remaining Stock</p>
                  <p className="text-lg font-bold text-emerald-700">{formatCurrency(batchHistoryData.avg_cost_of_remaining_stock)}</p>
                </div>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm" data-testid="po-cost-history-table">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2">PO Number</th>
                      <th className="text-left px-3 py-2">Received</th>
                      <th className="text-right px-3 py-2">Unit Cost</th>
                      <th className="text-right px-3 py-2">Qty Received</th>
                      <th className="text-right px-3 py-2">Qty Remaining</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {batchHistoryData.batches.map((b) => (
                      <tr key={b.id} data-testid={`po-cost-history-row-${b.id}`}>
                        <td className="px-3 py-2 font-medium text-gray-900">{b.po_number || '\u2014'}</td>
                        <td className="px-3 py-2 text-gray-600">{b.received_date ? new Date(b.received_date).toLocaleDateString('en-IN') : '\u2014'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(b.unit_cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{b.quantity_received}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${b.quantity_remaining > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {b.quantity_remaining}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={b.status === 'active' ? 'default' : 'secondary'} className={b.status === 'active' ? 'bg-emerald-100 text-emerald-700' : ''}>
                            {b.status === 'active' ? 'Active' : 'Exhausted'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edit Invoice Form Component (Fully Functional)
const CustomersSection = ({ onUpdate }) => {
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Fetch customers from API
  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
      setCustomers([]);
    }
  };

  // Fetch vehicles to link with customers
  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/enhanced`);
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchVehicles()]);
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Handle customer added
  const handleCustomerAdded = (newCustomer) => {
    if (newCustomer) {
      // Add the new customer to the customers list
      setCustomers(prev => [...prev, newCustomer]);
      setIsAddCustomerModalOpen(false);
      toast.success("Customer added successfully!");
      if (onUpdate) {
        onUpdate(newCustomer);
      }
    }
  };

  // Get vehicles for a customer
  const getVehiclesForCustomer = (customer) => {
    return vehicles.filter(vehicle => vehicle && vehicle.customer_id === customer.id);
  };

  // Handle edit customer
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setIsEditCustomerModalOpen(true);
  };

  // Handle customer updated
  const handleCustomerUpdated = (updatedCustomer) => {
    console.log('Updating customer in list:', updatedCustomer);
    
    setCustomers(prev => {
      const updated = prev.map(customer => 
        customer.id === updatedCustomer.id ? updatedCustomer : customer
      );
      console.log('Updated customers list length:', updated.length);
      return updated;
    });
    
    setIsEditCustomerModalOpen(false);
    setEditingCustomer(null);
    toast.success(`Customer "${updatedCustomer.name}" updated successfully!`);
  };

  // Handle delete customer
  const handleDeleteCustomer = async (customer) => {
    console.log('Attempting to delete customer:', customer);
    
    try {
      const customerVehicles = getVehiclesForCustomer(customer);
      console.log('Customer vehicles:', customerVehicles);
      
      let confirmDelete = false;
      
      if (customerVehicles.length > 0) {
        confirmDelete = window.confirm(
          `This customer has ${customerVehicles.length} vehicle(s) associated. Deleting the customer will also remove their vehicle associations. Are you sure you want to continue?`
        );
      } else {
        confirmDelete = window.confirm(
          `Are you sure you want to delete customer "${customer.name}"? This action cannot be undone.`
        );
      }
      
      if (!confirmDelete) {
        console.log('Delete cancelled by user');
        return;
      }

      console.log('Proceeding with delete...');
      
      // Delete customer from backend database
      await axios.delete(`${API}/customers/${customer.id}`);
      
      // Remove customer from list
      setCustomers(prev => {
        const updated = prev.filter(c => c.id !== customer.id);
        console.log('Updated customers list:', updated.length);
        return updated;
      });
      
      // Remove customer reference from their vehicles
      setVehicles(prev => prev
        .filter(vehicle => vehicle && vehicle.id) // Filter out undefined vehicles first
        .map(vehicle => 
          vehicle.customer_id === customer.id 
            ? { ...vehicle, customer_id: null, customer_name: null, customer_contact: null }
            : vehicle
        )
      );
      
      // Call onUpdate to refresh parent components if needed
      if (onUpdate) {
        onUpdate();
      }
      
      toast.success(`Customer "${customer.name}" deleted successfully!`);
      
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  // Customer statistics
  const customerStats = {
    totalCustomers: customers.length,
    withContact: customers.filter(customer => customer.contact).length,
    withLocation: customers.filter(customer => customer.location).length,
    recentCustomers: customers.filter(customer => {
      const created = new Date(customer.created_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return created > weekAgo;
    }).length
  };

  // Simple working filter (matching User Management pattern)
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = !searchTerm || searchTerm.trim() === '' ||
      (customer.name && customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.contact && customer.contact.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.phone && customer.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.location && customer.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.address && customer.address.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  }).sort((a, b) => {
    // Newest first
    const aKey = a.created_at || a.created_date || a.name || '';
    const bKey = b.created_at || b.created_date || b.name || '';
    return String(bKey).localeCompare(String(aKey));
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customers</h2>
          <p className="text-gray-600">Manage your customer database</p>
        </div>
        <Button 
          onClick={() => setIsAddCustomerModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <div className="text-2xl font-bold">{customerStats.totalCustomers}</div>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">With Contact</p>
                <div className="text-2xl font-bold">{customerStats.withContact}</div>
              </div>
              <Phone className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">With Location</p>
                <div className="text-2xl font-bold">{customerStats.withLocation}</div>
              </div>
              <MapPin className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Recent (7 days)</p>
                <div className="text-2xl font-bold">{customerStats.recentCustomers}</div>
              </div>
              <Calendar className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, contact, email, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 no-uppercase"
            />
          </div>
        </div>
        {searchTerm && (
          <Button 
            variant="outline" 
            onClick={() => setSearchTerm('')}
          >
            Clear Search
          </Button>
        )}
      </div>

      {/* Customers List */}
      <div className="space-y-4">
        {filteredCustomers.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
              <p className="text-gray-600">
                {customers.length === 0 
                  ? "Customers will appear here when you create job cards with customer details."
                  : "No customers match your search criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredCustomers.map((customer) => (
            <Card key={customer.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{customer.name}</h3>
                      <div className="text-sm text-gray-600 space-y-1">
                        {customer.contact && (
                          <div className="flex items-center space-x-1">
                            <Phone className="h-3 w-3" />
                            <span>{customer.contact}</span>
                          </div>
                        )}
                        {customer.location && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="h-3 w-3" />
                            <span>{customer.location}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Customer's Vehicles */}
                      {(() => {
                        const customerVehicles = getVehiclesForCustomer(customer);
                        if (customerVehicles.length > 0) {
                          return (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-xs font-medium text-blue-600 mb-1">
                                🚗 Vehicles ({customerVehicles.length})
                              </div>
                              <div className="space-y-1">
                                {customerVehicles
                                  .filter(vehicle => vehicle && vehicle.id) // Extra safety check
                                  .slice(0, 2).map((vehicle, index) => (
                                  <div key={vehicle.id} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                    <div className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                                    <div className="flex space-x-3 text-xs">
                                      <span>📋 {vehicle.license_plate}</span>
                                      <span>🔧 {vehicle.vin ? vehicle.vin.substring(0, 8) + '...' : 'No VIN'}</span>
                                    </div>
                                  </div>
                                ))}
                                {customerVehicles.length > 2 && (
                                  <div className="text-xs text-blue-600">
                                    +{customerVehicles.length - 2} more vehicles
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-xs text-gray-400">🚗 No vehicles registered</div>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 mb-2">
                      <div>ID: {customer.id ? customer.id.substring(0, 8) + '...' : 'Unknown'}</div>
                      <div>Created: {new Date(customer.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditCustomer(customer)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteCustomer(customer)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Summary */}
      {filteredCustomers.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600">
          Showing {filteredCustomers.length} of {customers.length} customers
        </div>
      )}

      {/* Add Customer Modal */}
      <Dialog open={isAddCustomerModalOpen} onOpenChange={setIsAddCustomerModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <AddCustomerForm onCustomerAdded={handleCustomerAdded} />
        </DialogContent>
      </Dialog>

      {/* Edit Customer Modal */}
      <Dialog open={isEditCustomerModalOpen} onOpenChange={(open) => {
        setIsEditCustomerModalOpen(open);
        if (!open) {
          setEditingCustomer(null);
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {editingCustomer && (
            <EditCustomerForm 
              customer={editingCustomer} 
              onCustomerUpdated={handleCustomerUpdated}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const VehiclesSection = ({ onUpdate }) => {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);
  const [isAddNewVehicleDataModalOpen, setIsAddNewVehicleDataModalOpen] = useState(false);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);

  // Fetch vehicles and customers
  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/enhanced`);
      // Filter out any undefined or invalid vehicles
      const validVehicles = (response.data || []).filter(vehicle => 
        vehicle && typeof vehicle === 'object' && vehicle.id
      );
      setVehicles(validVehicles);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      toast.error("Failed to load vehicles");
      setVehicles([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      // Filter out any undefined or invalid customers
      const validCustomers = (response.data || []).filter(customer => 
        customer && typeof customer === 'object' && customer.id
      );
      setCustomers(validCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVehicles(), fetchCustomers()]);
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Handle vehicle added
  const handleVehicleAdded = (newVehicle) => {
    // Ensure newVehicle is valid before adding
    if (!newVehicle || !newVehicle.id) {
      console.error("Invalid new vehicle data:", newVehicle);
      toast.error("Failed to add vehicle - invalid data");
      return;
    }
    
    setVehicles(prev => [...prev.filter(v => v && v.id), newVehicle]);
    setIsAddVehicleModalOpen(false);
    toast.success("Vehicle added successfully!");
    if (onUpdate) {
      onUpdate(newVehicle);
    }
  };

  // Handle vehicle data added
  const handleVehicleDataAdded = () => {
    setIsAddNewVehicleDataModalOpen(false);
    toast.success("Vehicle data has been added to the database successfully!");
    // Optionally refresh any cached vehicle data here
  };

  // Handle vehicle edit
  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setIsEditVehicleModalOpen(true);
  };

  // Handle vehicle delete
  const handleDeleteVehicle = async (vehicle) => {
    if (!vehicle || !vehicle.id) {
      toast.error("Cannot delete - invalid vehicle data");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${vehicle.year} ${vehicle.make} ${vehicle.model}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/vehicles/${vehicle.id}`);
      setVehicles(prev => prev.filter(v => v && v.id && v.id !== vehicle.id));
      toast.success("Vehicle deleted successfully!");
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast.error("Failed to delete vehicle");
    }
  };

  // Get customer details for a vehicle
  const getCustomerForVehicle = (vehicle) => {
    if (!vehicle || !vehicle.customer_id) return null;
    return customers.find(customer => customer && customer.id === vehicle.customer_id);
  };

  // Vehicle statistics
  const vehicleStats = {
    totalVehicles: vehicles.length,
    withCustomers: vehicles.filter(vehicle => vehicle && vehicle.customer_id).length,
    uniqueMakes: new Set(vehicles.filter(vehicle => vehicle && vehicle.make).map(vehicle => vehicle.make)).size,
    recentVehicles: vehicles.filter(vehicle => {
      if (!vehicle || !vehicle.created_at) return false;
      const created = new Date(vehicle.created_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return created > weekAgo;
    }).length
  };

  // Filter vehicles based on search and status
  const filteredVehicles = vehicles.filter(vehicle => {
    // Skip undefined or null vehicles
    if (!vehicle) return false;
    
    const matchesSearch = searchTerm === '' ||
      (vehicle.make && vehicle.make.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (vehicle.model && vehicle.model.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (vehicle.vin && vehicle.vin.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (vehicle.license_plate && vehicle.license_plate.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || statusFilter === '' || vehicle.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading vehicles...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Header with Enhanced Quick Actions */}
      <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-center md:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🚗 Vehicles</h2>
          <p className="text-gray-600">Manage your vehicle database efficiently</p>
        </div>
        
        {/* Quick Action Buttons */}
        <div className="flex space-x-3">
          <Button 
            onClick={() => setIsAddVehicleModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 shadow-lg"
            size="lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Vehicle
          </Button>
          
          <Button 
            onClick={() => setIsAddNewVehicleDataModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 shadow-lg"
            size="lg"
          >
            <Database className="h-5 w-5 mr-2" />
            Add New Vehicle
          </Button>
          
          <Button 
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
            variant="outline"
            size="lg"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Floating Add Vehicle Button - Fixed Position */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button 
          onClick={() => setIsAddVehicleModalOpen(true)}
          className="bg-green-600 hover:bg-green-700 shadow-2xl rounded-full w-14 h-14 p-0 group"
          title="Add Vehicle"
        >
          <Plus className="h-6 w-6 group-hover:scale-110 transition-transform" />
        </Button>
      </div>

      {/* Compact Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{vehicleStats.totalVehicles}</div>
                <div className="text-xs opacity-90">Total Vehicles</div>
              </div>
              <Car className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{vehicleStats.withCustomers}</div>
                <div className="text-xs opacity-90">With Owners</div>
              </div>
              <Users className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{vehicleStats.uniqueMakes}</div>
                <div className="text-xs opacity-90">Unique Makes</div>
              </div>
              <Settings2 className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{vehicleStats.recentVehicles}</div>
                <div className="text-xs opacity-90">Recent (7 days)</div>
              </div>
              <Calendar className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Search and Quick Filters */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="🔍 Search vehicles by make, model, VIN, or license plate..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white"
                />
              </div>
            </div>
            
            {/* Status Filter */}
            <div className="md:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="check-in">Check-in</SelectItem>
                  <SelectItem value="diagnosis">Diagnosis</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quick Add Button for Mobile */}
            <div className="md:hidden">
              <Button 
                onClick={() => setIsAddVehicleModalOpen(true)}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Vehicle
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vehicles List */}
      <div className="space-y-4">
        {filteredVehicles.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Car className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
              <p className="text-gray-600">
                {vehicles.length === 0 
                  ? "Vehicles will appear here when you create job cards with vehicle details."
                  : "No vehicles match your search criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredVehicles
            .filter(vehicle => vehicle && vehicle.id) // Extra safety check before mapping
            .map((vehicle) => (
            <Card key={vehicle.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Car className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                        {vehicle.series && ` ${vehicle.series}`}
                      </h3>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center space-x-4">
                          <span>📋 {vehicle.license_plate}</span>
                          <span>🔧 {vehicle.vin}</span>
                          {vehicle.fuel_type && <span>⛽ {vehicle.fuel_type}</span>}
                          {vehicle.transmission_code && <span>🔄 {vehicle.transmission_code}</span>}
                        </div>
                        {vehicle.engine_no && (
                          <div>Engine: {vehicle.engine_no}</div>
                        )}
                        {vehicle.mileage && (
                          <div>Mileage: {(vehicle.mileage || 0).toLocaleString()} km</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      statusConfig[vehicle.status]?.color || 'bg-gray-100 text-gray-800'
                    }`}>
                      {statusConfig[vehicle.status]?.label || vehicle.status}
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      <div>ID: {vehicle.id ? vehicle.id.substring(0, 8) + '...' : 'Unknown'}</div>
                      <div>Added: {new Date(vehicle.created_at).toLocaleDateString()}</div>
                      {(() => {
                        const customer = getCustomerForVehicle(vehicle);
                        if (customer) {
                          return (
                            <div className="text-green-600 mt-1">
                              <div className="font-medium">👤 {customer.name}</div>
                              <div className="text-xs text-gray-600">📞 {customer.phone}</div>
                              {customer.city && <div className="text-xs text-gray-600">📍 {customer.city}</div>}
                            </div>
                          );
                        } else if (vehicle.customer_id) {
                          return <div className="text-orange-600">👤 Customer ID: {vehicle.customer_id}</div>;
                        } else {
                          return <div className="text-gray-400">👤 No Owner</div>;
                        }
                      })()}
                    </div>
                  </div>
                  
                  {/* Edit and Delete Buttons */}
                  <div className="flex space-x-2 mt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEditVehicle(vehicle)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeleteVehicle(vehicle)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Summary */}
      {filteredVehicles.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600">
          Showing {filteredVehicles.length} of {vehicles.length} vehicles
        </div>
      )}

      {/* Add Vehicle Modal */}
      <Dialog open={isAddVehicleModalOpen} onOpenChange={setIsAddVehicleModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🚗 Add Vehicle</DialogTitle>
            <DialogDescription>
              Register a new vehicle in the garage management system with complete details and specifications.
            </DialogDescription>
          </DialogHeader>
          <AddVehicleForm onVehicleAdded={handleVehicleAdded} />
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Modal */}
      <Dialog open={isEditVehicleModalOpen} onOpenChange={setIsEditVehicleModalOpen}>
        <DialogContent className="max-w-md">
          {editingVehicle && (
            <UpdateVehicleForm 
              vehicle={editingVehicle} 
              onVehicleUpdated={(updatedVehicle) => {
                // Ensure updatedVehicle is valid before processing
                if (!updatedVehicle || !updatedVehicle.id) {
                  console.error("Invalid updated vehicle data:", updatedVehicle);
                  toast.error("Failed to update vehicle - invalid data");
                  return;
                }
                
                setVehicles(prev => prev
                  .filter(v => v && v.id) // Filter out undefined/null vehicles first
                  .map(v => 
                    v && v.id === updatedVehicle.id ? updatedVehicle : v
                  )
                  .filter(v => v && v.id) // Extra safety filter after mapping
                );
                setIsEditVehicleModalOpen(false);
                setEditingVehicle(null);
                toast.success("Vehicle updated successfully!");
                if (onUpdate) {
                  onUpdate();
                }
              }} 
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add New Vehicle Data Modal */}
      <Dialog open={isAddNewVehicleDataModalOpen} onOpenChange={setIsAddNewVehicleDataModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" />
              Add New Vehicle Data
            </DialogTitle>
            <DialogDescription>
              Add new Make → Model → Series combinations to the vehicle database for cascading dropdowns.
            </DialogDescription>
          </DialogHeader>
          <AddVehicleDataForm onVehicleDataAdded={handleVehicleDataAdded} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
// Add Quotation Form Component
const EnhancedAddIssueForm = ({ vehicles, dtcCodes, onIssueAdded, onCancel }) => {
  const [selectedDtcCodes, setSelectedDtcCodes] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [scanningReport, setScanningReport] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customModel, setCustomModel] = useState('');
  const [customSeries, setCustomSeries] = useState('');
  const [showAddDTCDialog, setShowAddDTCDialog] = useState(false);
  const [localDtcCodes, setLocalDtcCodes] = useState(dtcCodes || []);

  // DTC Form using React Hook Form
  const dtcForm = useForm({
    defaultValues: {
      code: '',
      description: '',
      system: 'Engine'
    }
  });

  // Use React Hook Form to prevent field clearing issues
  const { register, handleSubmit, watch, setValue, control, formState: { errors }, reset } = useForm({
    defaultValues: {
      vehicle_year: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_series: '',
      title: '',
      description: '',
      symptoms: '',
      severity: 'medium',
      status: 'open',
      category: 'general',
      subcategory: '',
      solution_status: 'unsolved',
      solution_description: '',
      remedy_used: ''
    }
  });

  // Watch for form changes
  const watchSolutionStatus = watch('solution_status');
  const watchIsSolved = watchSolutionStatus === 'solved';

  // Update local DTC codes when prop changes
  React.useEffect(() => {
    setLocalDtcCodes(dtcCodes || []);
  }, [dtcCodes]);

  // Handle adding new DTC code
  const handleAddNewDTC = async (e) => {
    e.preventDefault();
    
    const formData = dtcForm.getValues();
    if (!formData.code.trim() || !formData.description.trim()) {
      toast.error("Please fill in DTC code and description");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dtc-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const newDTC = await response.json();
        // Add the new DTC to local state
        setLocalDtcCodes(prev => [...prev, newDTC.dtc_code]);
        
        // Clear form and close dialog
        dtcForm.reset({ code: '', description: '', system: 'Engine' });
        setShowAddDTCDialog(false);
        
        toast.success(`DTC code ${newDTC.dtc_code.code} added successfully!`);
      } else if (response.status === 409) {
        toast.error('DTC code already exists');
      } else {
        toast.error('Failed to add DTC code');
      }
    } catch (error) {
      console.error('Error adding DTC code:', error);
      toast.error('Error adding DTC code');
    }
  };

  // Handle DTC form input changes
  const handleDTCInputChange = (field, value) => {
    dtcForm.setValue(field, value);
  };

  const onSubmit = async (data) => {
    if (!data.title || !data.title.trim()) {
      toast.error("Please enter an issue title");
      return;
    }

    const isSolved = data.solution_status === 'solved';
    const isWorkingOn = data.solution_status === 'working';

    if (isSolved && (!data.solution_description || !data.solution_description.trim())) {
      toast.error("Please provide solution description for solved issues");
      return;
    }

    // Map solution_status -> backend status enum (if user hasn't manually picked one)
    // Solved → resolved; Working → in-progress; Unsolved keeps the user-picked status (open/deferred/etc.)
    const computedStatus = isSolved
      ? 'resolved'
      : isWorkingOn
        ? 'in-progress'
        : data.status;

    setIsSubmitting(true);

    try {
      // First create the issue
      const issueData = {
        vehicle_year: data.vehicle_year ? parseInt(data.vehicle_year) : null,
        vehicle_make: (data.vehicle_make || '').trim(),
        vehicle_model: (data.vehicle_model || '').trim(),
        vehicle_series: (data.vehicle_series || '').trim(),
        title: data.title.trim(),
        description: (data.description || '').trim(),
        symptoms: (data.symptoms || '').trim(),
        severity: data.severity,
        status: computedStatus,
        category: data.category,
        subcategory: data.subcategory || null,
        dtc_codes: selectedDtcCodes,
        is_solved: isSolved,
        // Allow capturing solution details for both "Solved" and "Working" states
        solution_description: (isSolved || isWorkingOn) ? (data.solution_description || '').trim() : '',
        remedy_used: (isSolved || isWorkingOn) ? (data.remedy_used || '').trim() : '',
        solved_at: isSolved ? new Date().toISOString() : null
      };

      const response = await axios.post(`${API}/vehicle-issues`, issueData);
      const issueId = response.data.id;

      // Upload photos if any
      if (selectedPhotos.length > 0) {
        const photoFormData = new FormData();
        selectedPhotos.forEach(photo => {
          photoFormData.append('files', photo);
        });
        
        try {
          await axios.post(`${API}/vehicle-issues/${issueId}/upload-photos`, photoFormData);
        } catch (photoError) {
          console.log("Photo upload simulated for demo");
        }
      }

      // Upload scanning report if any
      if (scanningReport) {
        const reportFormData = new FormData();
        reportFormData.append('file', scanningReport);
        
        try {
          await axios.post(`${API}/vehicle-issues/${issueId}/upload-report`, reportFormData);
        } catch (reportError) {
          console.log("Report upload simulated for demo");
        }
      }

      // Upload videos if any
      if (selectedVideos.length > 0) {
        const videoFormData = new FormData();
        selectedVideos.forEach(video => {
          videoFormData.append('files', video);
        });
        
        try {
          await axios.post(`${API}/vehicle-issues/${issueId}/upload-videos`, videoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (videoError) {
          console.log("Video upload endpoint:", videoError);
          // Save video filenames to issue even if upload endpoint doesn't exist
          try {
            await axios.put(`${API}/vehicle-issues/${issueId}`, {
              video_filenames: selectedVideos.map(v => v.name).join(', ')
            });
          } catch (e) {
            console.log("Video filename save:", e);
          }
        }
      }

      let successMessage = "Enhanced issue added successfully!";
      if (selectedPhotos.length > 0) {
        successMessage += ` ${selectedPhotos.length} photos uploaded.`;
      }
      if (selectedVideos.length > 0) {
        successMessage += ` ${selectedVideos.length} video(s) uploaded.`;
      }
      if (scanningReport) {
        successMessage += " Scanning report uploaded.";
      }

      toast.success(successMessage);
      onIssueAdded();

      // Reset form
      reset();
      setSelectedDtcCodes([]);
      setSelectedPhotos([]);
      setSelectedVideos([]);
      setScanningReport(null);
      setCustomModel('');
      setCustomSeries('');
      
    } catch (error) {
      console.error("Error adding enhanced issue:", error);
      toast.error("Failed to add enhanced issue");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDtcCodeToggle = (dtcCode) => {
    setSelectedDtcCodes(prev => {
      const exists = prev.find(code => code.code === dtcCode.code);
      if (exists) {
        return prev.filter(code => code.code !== dtcCode.code);
      } else {
        return [...prev, dtcCode];
      }
    });
  };

  // Cascade vehicle data from vehicle_data database
  const [cascadeMakes, setCascadeMakes] = useState([]);
  const [cascadeModels, setCascadeModels] = useState([]);
  const [cascadeSeries, setCascadeSeries] = useState([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/vehicle-data/makes`);
        setCascadeMakes((response.data.makes || []).filter(m => m && m.trim() !== '').sort());
      } catch (error) { setCascadeMakes([]); }
    };
    fetchMakes();
  }, []);

  const watchedMake = watch('vehicle_make');
  const watchedModel = watch('vehicle_model');

  useEffect(() => {
    if (!watchedMake) { setCascadeModels([]); return; }
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/vehicle-data/models/${encodeURIComponent(watchedMake)}`);
        setCascadeModels((response.data.models || []).filter(m => m && m.trim() !== '').sort());
      } catch (error) { setCascadeModels([]); }
    };
    fetchModels();
  }, [watchedMake]);

  useEffect(() => {
    if (!watchedMake || !watchedModel) { setCascadeSeries([]); return; }
    const fetchSeries = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/vehicle-data/series/${encodeURIComponent(watchedMake)}/${encodeURIComponent(watchedModel)}`);
        setCascadeSeries((response.data.series || []).filter(s => s && s.trim() !== '').sort());
      } catch (error) { setCascadeSeries([]); }
    };
    fetchSeries();
  }, [watchedMake, watchedModel]);

  const vehicleMakes = cascadeMakes;
  const vehicleModels = cascadeModels;
  const vehicleSeries = cascadeSeries;

  // Sub-category mapping for each main category
  const categorySubcategories = {
    engine: [
      "Turbo", "Fuel System", "Cooling System", "Ignition", "Exhaust", 
      "Air Intake", "Oil System", "Timing Belt/Chain", "Pistons/Cylinders", 
      "Valves", "Head Gasket", "Block", "Sensors", "ECU/PCM", "Other"
    ],
    transmission: [
      "Automatic", "Manual", "CVT", "Clutch", "Gears", "Torque Converter",
      "Transmission Fluid", "Shift Solenoids", "TCM", "Driveshaft", 
      "Differential", "Other"
    ],
    electrical: [
      "Battery", "Alternator", "Starter", "Wiring", "Fuses", "Lights",
      "ECU/PCM", "Sensors", "Ignition System", "Charging System",
      "Power Windows", "Central Locking", "Audio System", "Other"
    ],
    brakes: [
      "Brake Pads", "Brake Discs", "Brake Fluid", "Brake Lines",
      "Master Cylinder", "Brake Calipers", "ABS", "Handbrake",
      "Brake Booster", "Other"
    ],
    suspension: [
      "Shock Absorbers", "Springs", "Struts", "Ball Joints", "Bushings",
      "Control Arms", "Sway Bar", "Steering", "Wheel Alignment", 
      "Wheel Bearings", "Other"
    ],
    general: [
      "Body Work", "Interior", "Exterior", "HVAC", "Tires", "Wheels",
      "Mirrors", "Wipers", "Maintenance", "Inspection", "Other"
    ]
  };

  const handleVehicleSelect = (vehicleId) => {
    if (vehicleId === 'manual') {
      // Reset to manual entry
      setValue('vehicle_year', '');
      setValue('vehicle_make', '');
      setValue('vehicle_model', '');
      setValue('vehicle_series', '');
      return;
    }

    const selectedVehicle = vehicles.find(v => v.id === vehicleId);
    if (selectedVehicle) {
      setValue('vehicle_year', selectedVehicle.year?.toString() || '');
      setValue('vehicle_make', selectedVehicle.make || '');
      setValue('vehicle_model', selectedVehicle.model || '');
      setValue('vehicle_series', selectedVehicle.series || '');
    }
  };

  // Show a toast when RHF validation blocks submission so the user knows why nothing happened.
  const onValidationError = (validationErrors) => {
    console.error("Form validation errors:", validationErrors);
    const firstField = Object.keys(validationErrors || {})[0];
    const firstMsg = firstField ? validationErrors[firstField]?.message : null;
    toast.error(firstMsg || "Please fill in all required fields");
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onValidationError)} className="space-y-6">
      {/* Vehicle Selection */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🚗 Vehicle Selection</h3>
        <div>
          <Label htmlFor="vehicle_select">Select Existing Vehicle (Optional)</Label>
          <Select onValueChange={handleVehicleSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Choose from existing vehicles or enter manually" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">📝 Enter Vehicle Details Manually</SelectItem>
              {vehicles
                .filter(vehicle => vehicle && vehicle.id) // Safety check for undefined vehicles
                .map(vehicle => (
                <SelectItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.series ? `(${vehicle.series})` : ''} - {vehicle.license_plate}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="vehicle_year">Year</Label>
          <Input
            id="vehicle_year"
            type="number"
            {...register("vehicle_year")}
            placeholder="e.g., 2020, 2021, 2022"
            min="1990"
            max="2030"
          />
        </div>
        <div>
          <Label htmlFor="vehicle_make">Make</Label>
          <Controller
            name="vehicle_make"
            control={control}
            render={({ field }) => (
              <Select 
                value={field.value} 
                onValueChange={(value) => {
                  field.onChange(value);
                  setValue('vehicle_model', '');
                  setValue('vehicle_series', '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vehicle make" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleMakes.map(make => (
                    <SelectItem key={make} value={make}>{make}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div>
          <Label htmlFor="vehicle_model">Model</Label>
          <Select 
            value={watch('vehicle_model') || ''} 
            onValueChange={(value) => {
              if (value === 'Other') {
                setValue('vehicle_model', customModel);
                setValue('vehicle_series', '');
              } else {
                setValue('vehicle_model', value);
                setValue('vehicle_series', '');
                setCustomModel('');
              }
            }}
            disabled={!watch('vehicle_make')}
          >
            <SelectTrigger>
              <SelectValue placeholder={watch('vehicle_make') ? "Select model" : "Select make first"} />
            </SelectTrigger>
            <SelectContent>
              {vehicleModels.map(model => (
                <SelectItem key={model} value={model}>{model}</SelectItem>
              ))}
              {/* Allow other models if not in database */}
              <SelectItem value="Other">🔧 Other (Custom Model)</SelectItem>
            </SelectContent>
          </Select>
          {watch('vehicle_model') === 'Other' && (
            <Input
              className="mt-2"
              placeholder="Enter custom model name"
              value={customModel}
              onChange={(e) => {
                setCustomModel(e.target.value);
                setValue('vehicle_model', e.target.value);
              }}
            />
          )}
        </div>
        <div>
          <Label htmlFor="vehicle_series">SERIES</Label>
          <Select 
            value={watch('vehicle_series')} 
            onValueChange={(value) => {
              if (value === 'Other') {
                setValue('vehicle_series', customSeries);
              } else {
                setValue('vehicle_series', value);
                setCustomSeries('');
              }
            }}
            disabled={!watch('vehicle_make') || !watch('vehicle_model')}
          >
            <SelectTrigger>
              <SelectValue placeholder={
                !watch('vehicle_make') ? "Select make first" :
                !watch('vehicle_model') ? "Select model first" :
                vehicleSeries.length > 0 ? "Select series/variant" : "No series available"
              } />
            </SelectTrigger>
            <SelectContent>
              {vehicleSeries.map(series => (
                <SelectItem key={series} value={series}>{series}</SelectItem>
              ))}
              <SelectItem value="Other">🔧 Other (Custom Series)</SelectItem>
            </SelectContent>
          </Select>
          {watch('vehicle_series') === 'Other' && (
            <Input
              className="mt-2"
              placeholder="Enter custom series/variant"
              value={customSeries}
              onChange={(e) => {
                setCustomSeries(e.target.value);
                setValue('vehicle_series', e.target.value);
              }}
            />
          )}
        </div>
      </div>

      {/* Problem Title */}
      <div>
        <Label htmlFor="title">Problem Title *</Label>
        <Input
          id="title"
          {...register("title", { required: "Problem title is required" })}
          placeholder="Brief description of the issue"
        />
      </div>

      {/* Problem Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="severity">Severity</Label>
          <Controller
            name="severity"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">🟢 Low</SelectItem>
                  <SelectItem value="medium">🟡 Medium</SelectItem>
                  <SelectItem value="high">🟠 High</SelectItem>
                  <SelectItem value="critical">🔴 Critical</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div>
          <Label htmlFor="category">Category</Label>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={(value) => {
                field.onChange(value);
                setValue('subcategory', ''); // Reset subcategory when category changes
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="engine">Engine</SelectItem>
                  <SelectItem value="transmission">Transmission</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="brakes">Brakes</SelectItem>
                  <SelectItem value="suspension">Suspension</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      {/* Sub-category (appears when category is selected) */}
      {watch('category') && categorySubcategories[watch('category')] && (
        <div>
          <Label htmlFor="subcategory">Sub-Category</Label>
          <Controller
            name="subcategory"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${watch('category')} sub-category`} />
                </SelectTrigger>
                <SelectContent>
                  {categorySubcategories[watch('category')].map(subcategory => (
                    <SelectItem key={subcategory} value={subcategory.toLowerCase().replace(/\s+/g, '_')}>
                      {subcategory}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {/* Description */}
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          className="no-uppercase"
          {...register("description")}
          placeholder="Detailed description of the issue..."
          rows="3"
        />
      </div>

      {/* Symptoms */}
      <div>
        <Label htmlFor="symptoms">Symptoms</Label>
        <Textarea
          id="symptoms"
          className="no-uppercase"
          {...register("symptoms")}
          placeholder="What symptoms are observed? (e.g., strange noises, warning lights, performance issues)"
          rows="2"
        />
      </div>

      {/* Problem Status */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium text-gray-900 mb-2 block">
            Solution Status
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${watchSolutionStatus === 'unsolved' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input
                type="radio"
                value="unsolved"
                {...register("solution_status")}
                className="text-red-600"
                data-testid="add-issue-status-unsolved"
              />
              <span className="text-sm font-medium text-red-700">🔴 Unsolved</span>
            </label>

            <label className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${watchSolutionStatus === 'working' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input
                type="radio"
                value="working"
                {...register("solution_status")}
                className="text-amber-600"
                data-testid="add-issue-status-working"
              />
              <span className="text-sm font-medium text-amber-700">🟡 Working on Solution</span>
            </label>

            <label className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${watchSolutionStatus === 'solved' ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input
                type="radio"
                value="solved"
                {...register("solution_status")}
                className="text-green-600"
                data-testid="add-issue-status-solved"
              />
              <span className="text-sm font-medium text-green-700">🟢 Solved</span>
            </label>
          </div>
        </div>

        {/* Solution Details - Show for both Working & Solved */}
        {(watchSolutionStatus === 'working' || watchSolutionStatus === 'solved') && (
          <div className={`border rounded-lg p-4 space-y-4 ${watchSolutionStatus === 'solved' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <h4 className={`font-medium ${watchSolutionStatus === 'solved' ? 'text-green-900' : 'text-amber-900'}`}>
              {watchSolutionStatus === 'solved' ? 'Solution Details' : 'Work-in-Progress Notes'}
            </h4>

            <div>
              <Label htmlFor="solution_description">
                Solution Applied {watchSolutionStatus === 'solved' ? '*' : '(optional)'}
              </Label>
              <Textarea
                id="solution_description"
                className="no-uppercase"
                {...register("solution_description", { 
                  required: watchSolutionStatus === 'solved' ? "Solution description is required when issue is marked as solved" : false 
                })}
                placeholder={watchSolutionStatus === 'solved' 
                  ? 'Describe the solution that was applied to fix this issue...' 
                  : 'Describe what is being tried, current findings, or steps in progress...'}
                rows="3"
              />
              {errors.solution_description && (
                <p className="text-red-500 text-sm mt-1">{errors.solution_description.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="remedy_used">Remedy/Parts Used</Label>
              <Textarea
                id="remedy_used"
                {...register("remedy_used")}
                placeholder="List the parts replaced, repairs done, or procedures followed..."
                rows="2"
              />
            </div>
          </div>
        )}
      </div>

      {/* DTC Codes Selection */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Diagnostic Trouble Codes (DTC)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddDTCDialog(true)}
            className="flex items-center gap-1"
          >
            <Plus size={14} />
            Add New DTC Code
          </Button>
        </div>
        
        <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
          {!localDtcCodes || localDtcCodes.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <p>No DTC codes available.</p>
              <p className="text-xs mt-1">Add your first DTC code using the button above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {localDtcCodes.slice(0, 10).map((dtc) => (
                <div key={dtc.code} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`dtc-${dtc.code}`}
                    checked={selectedDtcCodes.some(code => code.code === dtc.code)}
                    onChange={() => handleDtcCodeToggle(dtc)}
                    className="rounded"
                  />
                  <label htmlFor={`dtc-${dtc.code}`} className="flex-1 text-sm cursor-pointer">
                    <span className="font-medium text-blue-600">{dtc.code}</span> - {dtc.description}
                    <span className="text-gray-500 ml-2">({dtc.system})</span>
                  </label>
                </div>
              ))}
              {localDtcCodes.length > 10 && (
                <p className="text-xs text-gray-500 mt-2">
                  Showing first 10 of {localDtcCodes.length} DTC codes. Visit DTC Management for full list.
                </p>
              )}
            </div>
          )}
        </div>
        {selectedDtcCodes.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-gray-600">Selected: {selectedDtcCodes.length} DTC codes</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {selectedDtcCodes.map((code) => (
                <Badge key={code.code} variant="outline" className="text-xs">
                  {code.code}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Photo Upload Section */}
      <div>
        <Label htmlFor="photos">Problem Area Photos</Label>
        <Input
          id="photos"
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => {
            const files = Array.from(e.target.files);
            setSelectedPhotos(files);
          }}
          className="mb-2"
        />
        {selectedPhotos.length > 0 && (
          <div>
            <p className="text-sm text-gray-600 mb-2">Selected: {selectedPhotos.length} photos</p>
            <div className="grid grid-cols-4 gap-2">
              {selectedPhotos.map((file, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-16 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Scanning Report Upload Section */}
      <div>
        <Label htmlFor="scanning_report">Scanning Report (PDF)</Label>
        <Input
          id="scanning_report"
          type="file"
          accept=".pdf"
          onChange={(e) => {
            setScanningReport(e.target.files[0]);
          }}
        />
        {scanningReport && (
          <div className="mt-2">
            <p className="text-sm text-gray-600">
              Selected: {scanningReport.name} ({Math.round(scanningReport.size / 1024)}KB)
            </p>
          </div>
        )}
      </div>

      {/* Video Upload Section */}
      <div className="border rounded-lg p-4 bg-purple-50">
        <Label className="text-base font-medium text-gray-900 mb-2 block">Vehicle Problem Videos (Max 10)</Label>
        <div className="flex space-x-2">
          <Input
            value={selectedVideos.map(v => v.name).join(', ')}
            placeholder="No videos selected"
            readOnly
            className="flex-1 bg-white"
          />
          <input
            type="file"
            accept="video/mp4,video/mov,video/avi,video/wmv,video/mkv,video/webm"
            multiple
            style={{ display: 'none' }}
            ref={(el) => window.issuVideoFileInput = el}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length > 10) {
                toast.error('Maximum 10 videos allowed.');
                e.target.value = '';
                return;
              }
              const oversized = files.filter(f => f.size > 100 * 1024 * 1024);
              if (oversized.length > 0) {
                toast.error(`Some videos are too large (max 100MB): ${oversized.map(f => f.name).join(', ')}`);
                return;
              }
              setSelectedVideos(files);
              toast.success(`${files.length} video(s) selected`);
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300"
            onClick={() => window.issuVideoFileInput?.click()}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Videos
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-1">Supported: MP4, MOV, AVI, WMV, MKV, WebM (max 100MB each)</p>
        {selectedVideos.length > 0 && (
          <div className="mt-3 space-y-1">
            {selectedVideos.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-white rounded p-2 border">
                <span className="text-sm text-purple-700 truncate">{file.name} ({(file.size / (1024 * 1024)).toFixed(1)} MB)</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                  onClick={() => setSelectedVideos(prev => prev.filter((_, i) => i !== index))}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="bg-blue-600 hover:bg-blue-700"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Adding..." : "Add Enhanced Problem"}
        </Button>
      </div>

      {/* Add DTC Code Dialog */}
      <Dialog open={showAddDTCDialog} onOpenChange={setShowAddDTCDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New DTC Code</DialogTitle>
            <DialogDescription>
              Create a new diagnostic trouble code that can be associated with vehicle problems.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddNewDTC} className="space-y-4">
            <div>
              <Label htmlFor="new_dtc_code">DTC Code *</Label>
              <Input
                id="new_dtc_code"
                {...dtcForm.register('code', {
                  required: 'DTC code is required',
                  onChange: (e) => {
                    e.target.value = e.target.value.toUpperCase();
                  }
                })}
                placeholder="P0171"
                className="uppercase"
              />
              {dtcForm.formState.errors.code && (
                <p className="text-red-500 text-sm mt-1">{dtcForm.formState.errors.code.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="new_dtc_system">System *</Label>
              <Controller
                control={dtcForm.control}
                name="system"
                rules={{ required: 'System is required' }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Engine">Engine</SelectItem>
                      <SelectItem value="Transmission">Transmission</SelectItem>
                      <SelectItem value="ABS">ABS</SelectItem>
                      <SelectItem value="Airbag">Airbag</SelectItem>
                      <SelectItem value="Body Control">Body Control</SelectItem>
                      <SelectItem value="Climate Control">Climate Control</SelectItem>
                      <SelectItem value="Exhaust">Exhaust</SelectItem>
                      <SelectItem value="Fuel System">Fuel System</SelectItem>
                      <SelectItem value="Ignition">Ignition</SelectItem>
                      <SelectItem value="Emissions">Emissions</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {dtcForm.formState.errors.system && (
                <p className="text-red-500 text-sm mt-1">{dtcForm.formState.errors.system.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="new_dtc_description">Description *</Label>
              <Input
                id="new_dtc_description"
                {...dtcForm.register('description', {
                  required: 'Description is required'
                })}
                placeholder="System Too Lean (Bank 1)"
              />
              {dtcForm.formState.errors.description && (
                <p className="text-red-500 text-sm mt-1">{dtcForm.formState.errors.description.message}</p>
              )}
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1">Add DTC Code</Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAddDTCDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </form>
  );
};

const AddIssueForm = ({ vehicles, onIssueAdded }) => {
  const [addCustomerFormData, setFormData] = useState({
    vehicleId: '',
    title: '',
    description: '',
    priority: 'medium',
    status: 'open',
    category: 'general',
    estimated_cost: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isManualCustomer, setIsManualCustomer] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!addCustomerFormData.title.trim()) {
      toast.error("Please enter an issue title");
      return;
    }

    setIsSubmitting(true);

    try {
      // Find the selected vehicle
      const vehicle = vehicles.find(v => v.id === addCustomerFormData.vehicleId);
      if (!vehicle) {
        toast.error("Selected vehicle not found");
        return;
      }

      // Create new issue
      const newIssue = {
        id: Date.now().toString(),
        title: addCustomerFormData.title.trim(),
        description: addCustomerFormData.description.trim(),
        priority: addCustomerFormData.priority,
        status: addCustomerFormData.status,
        category: addCustomerFormData.category,
        estimated_cost: parseFloat(addCustomerFormData.estimated_cost) || null,
        created_at: new Date().toISOString(),
        resolved_at: null
      };

      // Update vehicle issues
      const updatedIssues = [...(vehicle.issues || []), newIssue];
      
      await axios.put(`${API}/vehicles/${addCustomerFormData.vehicleId}`, { issues: updatedIssues });
      toast.success("Problem added successfully!");
      onIssueAdded();
      
      // Reset form
      setFormData({
        vehicleId: '',
        title: '',
        description: '',
        priority: 'medium',
        status: 'open',
        category: 'general',
        estimated_cost: ''
      });
    } catch (error) {
      console.error("Error adding issue:", error);
      toast.error("Failed to add issue");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add Vehicle Problem</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="vehicleId">Select Vehicle *</Label>
          <Select 
            value={addCustomerFormData.vehicleId} 
            onValueChange={(value) => setFormData({...addCustomerFormData, vehicleId: value})}
            required
          >
            <SelectTrigger data-testid="issue-vehicle-select">
              <SelectValue placeholder="Choose a vehicle" />
            </SelectTrigger>
            <SelectContent>
              {vehicles
                .filter(vehicle => vehicle && vehicle.id && vehicle.make && vehicle.model)
                .map(vehicle => (
                  <SelectItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.year} {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="title">Problem Title *</Label>
          <Input
            data-testid="issue-title-input"
            id="title"
            value={addCustomerFormData.title}
            onChange={(e) => setFormData({...addCustomerFormData, title: e.target.value})}
            required
            placeholder="Brief description of the issue"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="priority">Priority</Label>
            <Select 
              value={addCustomerFormData.priority} 
              onValueChange={(value) => setFormData({...addCustomerFormData, priority: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="category">Category</Label>
            <Select 
              value={addCustomerFormData.category} 
              onValueChange={(value) => setFormData({...addCustomerFormData, category: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="engine">Engine</SelectItem>
                <SelectItem value="transmission">Transmission</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="brakes">Brakes</SelectItem>
                <SelectItem value="suspension">Suspension</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="estimated_cost">Est. Cost (₹)</Label>
            <Input
              data-testid="issue-cost-input"
              id="estimated_cost"
              type="number"
              value={addCustomerFormData.estimated_cost}
              onChange={(e) => setFormData({...addCustomerFormData, estimated_cost: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            data-testid="issue-description-input"
            id="description"
            name="description"
            value={addCustomerFormData.description}
            onChange={(e) => setFormData({...addCustomerFormData, description: e.target.value})}
            placeholder="Detailed description of the issue..."
            rows="3"
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            data-testid="submit-issue-btn"
          >
            {isSubmitting ? "Adding Problem..." : "Add Problem"}
          </Button>
        </div>
      </form>
    </>
  );
};

// Duplicate removed - using the first EnhancedAddIssueForm declaration above

// Duplicate malformed EditIssueForm removed - correct one is below
 

const EditIssueForm = ({ issue, vehicles, onIssueUpdated, onCancel }) => {
  // Derive initial solution_status from existing issue
  const deriveSolutionStatus = (iss) => {
    if (iss.is_solved) return 'solved';
    if (iss.status === 'in-progress') return 'working';
    return 'unsolved';
  };

  const [formData, setFormData] = useState({
    title: issue.title || '',
    description: issue.description || '',
    symptoms: issue.symptoms || '',
    severity: issue.severity || 'medium',
    status: issue.status || 'open',
    category: issue.category || 'general',
    subcategory: issue.subcategory || '',
    customer_complaint: issue.customer_complaint || '',
    diagnosed_by: issue.diagnosed_by || '',
    under_warranty: issue.under_warranty || false,
    warranty_claim_number: issue.warranty_claim_number || '',
    solution_status: deriveSolutionStatus(issue),
    solution_description: issue.solution_description || '',
    remedy_used: issue.remedy_used || ''
  });
  const [selectedDtcCodes, setSelectedDtcCodes] = useState(issue.dtc_codes || []);
  const [dtcCodes, setDtcCodes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddDTCDialog, setShowAddDTCDialog] = useState(false);
  const [newDTCFormData, setNewDTCFormData] = useState({
    code: '',
    description: '',
    system: 'Engine'
  });

  // DTC Form using React Hook Form
  const dtcForm = useForm({
    defaultValues: {
      code: '',
      description: '',
      system: 'Engine'
    }
  });

  // Fetch DTC codes on component mount
  useEffect(() => {
    const fetchDtcCodes = async () => {
      try {
        const response = await axios.get(`${API}/dtc-codes`);
        setDtcCodes(response.data);
      } catch (error) {
        console.error("Error fetching DTC codes:", error);
        setDtcCodes([]);
      }
    };
    fetchDtcCodes();
  }, [API]);

  // Handle adding new DTC code in edit form
  const handleAddNewDTC = dtcForm.handleSubmit(async (data) => {
    if (!data.code.trim() || !data.description.trim()) {
      toast.error("Please fill in DTC code and description");
      return;
    }

    try {
      const response = await apiFetch(`${API}/dtc-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const newDTC = await response.json();
        // Add the new DTC to local state
        setDtcCodes(prev => [...prev, newDTC.dtc_code]);
        
        // Clear form and close dialog using React Hook Form
        dtcForm.reset();
        setShowAddDTCDialog(false);
        
        toast.success(`DTC code ${newDTC.dtc_code.code} added successfully!`);
      } else if (response.status === 409) {
        toast.error('DTC code already exists');
      } else {
        toast.error('Failed to add DTC code');
      }
    } catch (error) {
      console.error('Error adding DTC code:', error);
      toast.error('Error adding DTC code');
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }

    const isSolved = formData.solution_status === 'solved';
    const isWorking = formData.solution_status === 'working';

    if (isSolved && !(formData.solution_description || '').trim()) {
      alert('Please provide solution description for solved issues');
      return;
    }

    // Derive backend status from solution_status when applicable
    const computedStatus = isSolved
      ? 'resolved'
      : isWorking
        ? 'in-progress'
        : formData.status;

    setIsSubmitting(true);
    try {
      const response = await axios.put(`${API}/vehicle-issues/${issue.id}`, {
        title: formData.title,
        description: formData.description,
        symptoms: formData.symptoms,
        severity: formData.severity,
        status: computedStatus,
        category: formData.category,
        subcategory: formData.subcategory || null,
        dtc_codes: selectedDtcCodes,
        customer_complaint: formData.customer_complaint,
        diagnosed_by: formData.diagnosed_by,
        under_warranty: formData.under_warranty,
        warranty_claim_number: formData.warranty_claim_number || null,
        is_solved: isSolved,
        solution_description: (isSolved || isWorking) ? (formData.solution_description || '').trim() : '',
        remedy_used: (isSolved || isWorking) ? (formData.remedy_used || '').trim() : '',
        solved_at: isSolved ? (issue.solved_at || new Date().toISOString()) : null
      });

      if (response.status === 200) {
        onIssueUpdated();
      }
    } catch (error) {
      console.error("Error updating issue:", error);
      alert('Failed to update issue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDtcCodeToggle = (dtcCode) => {
    setSelectedDtcCodes(prev => {
      const exists = prev.find(code => code.code === dtcCode.code);
      if (exists) {
        return prev.filter(code => code.code !== dtcCode.code);
      } else {
        return [...prev, dtcCode];
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="title">Problem Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Brief description of the problem"
            required
          />
        </div>

        <div>
          <Label htmlFor="severity">Severity *</Label>
          <Select value={formData.severity} onValueChange={(value) => handleInputChange('severity', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Detailed Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Detailed description of the problem"
          rows={3}
          required
        />
      </div>

      <div>
        <Label htmlFor="symptoms">Symptoms</Label>
        <Textarea
          id="symptoms"
          value={formData.symptoms}
          onChange={(e) => handleInputChange('symptoms', e.target.value)}
          placeholder="Observable symptoms (noise, behavior, etc.)"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Category</Label>
          <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="engine">Engine</SelectItem>
              <SelectItem value="transmission">Transmission</SelectItem>
              <SelectItem value="brakes">Brakes</SelectItem>
              <SelectItem value="electrical">Electrical</SelectItem>
              <SelectItem value="suspension">Suspension</SelectItem>
              <SelectItem value="cooling">Cooling System</SelectItem>
              <SelectItem value="fuel">Fuel System</SelectItem>
              <SelectItem value="exhaust">Exhaust System</SelectItem>
              <SelectItem value="interior">Interior</SelectItem>
              <SelectItem value="exterior">Exterior</SelectItem>
              <SelectItem value="general">General</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="status">Status</Label>
          <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="deferred">Deferred</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="subcategory">Subcategory</Label>
        <Input
          id="subcategory"
          value={formData.subcategory}
          onChange={(e) => handleInputChange('subcategory', e.target.value)}
          placeholder="e.g., Turbo, Fuel Pump, etc."
        />
      </div>

      <div>
        <Label htmlFor="customer_complaint">Customer Complaint</Label>
        <Textarea
          id="customer_complaint"
          value={formData.customer_complaint}
          onChange={(e) => handleInputChange('customer_complaint', e.target.value)}
          placeholder="What the customer reported"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="diagnosed_by">Diagnosed By</Label>
          <Input
            id="diagnosed_by"
            value={formData.diagnosed_by}
            onChange={(e) => handleInputChange('diagnosed_by', e.target.value)}
            placeholder="Technician name"
          />
        </div>

        <div>
          <Label htmlFor="warranty_claim_number">Warranty Claim Number</Label>
          <Input
            id="warranty_claim_number"
            value={formData.warranty_claim_number}
            onChange={(e) => handleInputChange('warranty_claim_number', e.target.value)}
            placeholder="If under warranty"
            disabled={!formData.under_warranty}
          />
        </div>
      </div>

      {/* DTC Codes Selection */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Diagnostic Trouble Codes (DTC)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddDTCDialog(true)}
            className="flex items-center gap-1"
          >
            <Plus size={14} />
            Add New DTC Code
          </Button>
        </div>
        
        <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
          <div className="grid grid-cols-1 gap-2">
            {dtcCodes.slice(0, 10).map((dtc) => (
              <div key={dtc.code} className="flex items-center space-x-2">
                <Checkbox
                  id={`edit-dtc-${dtc.code}`}
                  checked={selectedDtcCodes.some(code => code.code === dtc.code)}
                  onCheckedChange={() => handleDtcCodeToggle(dtc)}
                />
                <Label htmlFor={`edit-dtc-${dtc.code}`} className="flex-1 text-sm cursor-pointer">
                  <span className="font-medium text-blue-600">{dtc.code}</span> - {dtc.description}
                  <span className="text-gray-500 ml-2">({dtc.system})</span>
                </Label>
              </div>
            ))}
            {dtcCodes.length > 10 && (
              <p className="text-xs text-gray-500 mt-2">
                Showing first 10 of {dtcCodes.length} DTC codes. Visit DTC Management for full list.
              </p>
            )}
          </div>
        </div>
        {selectedDtcCodes.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-gray-600">Selected: {selectedDtcCodes.length} DTC codes</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {selectedDtcCodes.map((code) => (
                <Badge key={code.code} variant="outline" className="text-xs">
                  {code.code}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="under_warranty"
          checked={formData.under_warranty}
          onCheckedChange={(checked) => handleInputChange('under_warranty', checked)}
        />
        <Label htmlFor="under_warranty">Under Warranty</Label>
      </div>

      {/* Solution Status & Applied Solution */}
      <div className="space-y-4 border-t pt-4">
        <div>
          <Label className="text-base font-medium text-gray-900 mb-2 block">
            Solution Status
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${formData.solution_status === 'unsolved' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input
                type="radio"
                name="solution_status"
                value="unsolved"
                checked={formData.solution_status === 'unsolved'}
                onChange={() => handleInputChange('solution_status', 'unsolved')}
                className="text-red-600"
                data-testid="edit-issue-status-unsolved"
              />
              <span className="text-sm font-medium text-red-700">🔴 Unsolved</span>
            </label>

            <label className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${formData.solution_status === 'working' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input
                type="radio"
                name="solution_status"
                value="working"
                checked={formData.solution_status === 'working'}
                onChange={() => handleInputChange('solution_status', 'working')}
                className="text-amber-600"
                data-testid="edit-issue-status-working"
              />
              <span className="text-sm font-medium text-amber-700">🟡 Working on Solution</span>
            </label>

            <label className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${formData.solution_status === 'solved' ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input
                type="radio"
                name="solution_status"
                value="solved"
                checked={formData.solution_status === 'solved'}
                onChange={() => handleInputChange('solution_status', 'solved')}
                className="text-green-600"
                data-testid="edit-issue-status-solved"
              />
              <span className="text-sm font-medium text-green-700">🟢 Solved</span>
            </label>
          </div>
        </div>

        {(formData.solution_status === 'working' || formData.solution_status === 'solved') && (
          <div className={`border rounded-lg p-4 space-y-4 ${formData.solution_status === 'solved' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <h4 className={`font-medium ${formData.solution_status === 'solved' ? 'text-green-900' : 'text-amber-900'}`}>
              {formData.solution_status === 'solved' ? 'Solution Details' : 'Work-in-Progress Notes'}
            </h4>

            <div>
              <Label htmlFor="edit_solution_description">
                Solution Applied {formData.solution_status === 'solved' ? '*' : '(optional)'}
              </Label>
              <Textarea
                id="edit_solution_description"
                value={formData.solution_description}
                onChange={(e) => handleInputChange('solution_description', e.target.value)}
                placeholder={formData.solution_status === 'solved' 
                  ? 'Describe the solution that was applied to fix this issue...' 
                  : 'Describe what is being tried, current findings, or steps in progress...'}
                rows="3"
              />
            </div>

            <div>
              <Label htmlFor="edit_remedy_used">Remedy/Parts Used</Label>
              <Textarea
                id="edit_remedy_used"
                value={formData.remedy_used}
                onChange={(e) => handleInputChange('remedy_used', e.target.value)}
                placeholder="List the parts replaced, repairs done, or procedures followed..."
                rows="2"
              />
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-4 pt-4">
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="flex-1"
        >
          {isSubmitting ? 'Updating...' : 'Update Issue'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
      
      {/* Add DTC Code Dialog */}
      <Dialog open={showAddDTCDialog} onOpenChange={setShowAddDTCDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New DTC Code</DialogTitle>
            <DialogDescription>
              Create a new diagnostic trouble code that can be associated with vehicle problems.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddNewDTC} className="space-y-4">
            <div>
              <Label htmlFor="edit_new_dtc_code">DTC Code *</Label>
              <Controller
                name="code"
                control={dtcForm.control}
                render={({ field }) => (
                  <Input
                    id="edit_new_dtc_code"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    placeholder="P0171"
                    required
                    className="uppercase"
                  />
                )}
              />
            </div>
            
            <div>
              <Label htmlFor="edit_new_dtc_system">System *</Label>
              <Controller
                name="system"
                control={dtcForm.control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Engine">Engine</SelectItem>
                      <SelectItem value="Transmission">Transmission</SelectItem>
                      <SelectItem value="ABS">ABS</SelectItem>
                      <SelectItem value="Airbag">Airbag</SelectItem>
                      <SelectItem value="Body Control">Body Control</SelectItem>
                      <SelectItem value="Climate Control">Climate Control</SelectItem>
                      <SelectItem value="Exhaust">Exhaust</SelectItem>
                      <SelectItem value="Fuel System">Fuel System</SelectItem>
                      <SelectItem value="Ignition">Ignition</SelectItem>
                      <SelectItem value="Emissions">Emissions</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            
            <div>
              <Label htmlFor="edit_new_dtc_description">Description *</Label>
              <Controller
                name="description"
                control={dtcForm.control}
                render={({ field }) => (
                  <Input
                    id="edit_new_dtc_description"
                    {...field}
                    placeholder="System Too Lean (Bank 1)"
                    required
                  />
                )}
              />
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1">Add DTC Code</Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAddDTCDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </form>
  );
};

// New Job Card Form using React Hook Form for better state management


// Add Job Card Form Component with React Hook Form
const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  return <MainWindow />;
};

// Vehicle Specifications Section Component
const VehicleSpecificationsSection = ({ onUpdate }) => {
  const [specifications, setSpecifications] = useState([]);
  const [filteredSpecs, setFilteredSpecs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [makeFilter, setMakeFilter] = useState('all');
  const [fuelFilter, setFuelFilter] = useState('all');
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [showSpecDialog, setShowSpecDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  const [selectedMediaSpec, setSelectedMediaSpec] = useState(null);
  const [checklistSpec, setChecklistSpec] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState({ title: '', category: 'General', description: '', is_critical: false });
  const [showEngineProcedures, setShowEngineProcedures] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Fetch specifications data
  const fetchSpecifications = async () => {
    try {
      const response = await apiFetch(`${backendUrl}/api/vehicle-specs`);
      if (response.ok) {
        const specsData = await response.json();
        setSpecifications(specsData);
        setFilteredSpecs(specsData);
        calculateStatistics(specsData);
      } else {
        console.error('Failed to fetch vehicle specifications');
      }
    } catch (error) {
      console.error('Error fetching specifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const calculateStatistics = (specs) => {
    const makes = new Set();
    const models = new Set();
    const engineCodes = new Set();
    let totalSpecs = specs.length;
    let petrolSpecs = 0;
    let dieselSpecs = 0;

    specs.forEach(spec => {
      makes.add(spec.make);
      models.add(spec.model);
      engineCodes.add(spec.engine_code);
      if (spec.fuel_type.toLowerCase() === 'petrol') petrolSpecs++;
      if (spec.fuel_type.toLowerCase() === 'diesel') dieselSpecs++;
    });

    setStatistics({
      totalSpecifications: totalSpecs,
      totalMakes: makes.size,
      totalModels: models.size,
      totalEngineCodes: engineCodes.size,
      petrolSpecs,
      dieselSpecs
    });
  };

  // Handle specification deletion
  const handleDeleteSpec = async (specId, vehicleName) => {
    if (window.confirm(`Are you sure you want to delete the specification for ${vehicleName}?\n\nThis action cannot be undone.`)) {
      try {
        const response = await apiFetch(`${backendUrl}/api/vehicle-specs/${specId}`, {
          method: 'DELETE',
        });
        
        if (response.ok) {
          await fetchSpecifications();
        } else {
          console.error('Failed to delete vehicle specification');
          alert('Failed to delete vehicle specification. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting vehicle specification:', error);
        alert('An error occurred while deleting the specification.');
      }
    }
  };

  // Checklist functions
  const openChecklist = async (spec) => {
    setChecklistSpec(spec);
    setShowChecklistDialog(true);
    setChecklistLoading(true);
    try {
      const response = await apiFetch(`${backendUrl}/api/vehicle-specs/${spec.id}/checklist`);
      if (response.ok) {
        const data = await response.json();
        setChecklistItems(data || []);
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      setChecklistItems(spec.checklist || []);
    } finally {
      setChecklistLoading(false);
    }
  };

  const addChecklistItem = async () => {
    if (!newChecklistItem.title.trim()) return;
    try {
      const response = await apiFetch(`${backendUrl}/api/vehicle-specs/${checklistSpec.id}/checklist/item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newChecklistItem)
      });
      if (response.ok) {
        const data = await response.json();
        setChecklistItems(prev => [...prev, data.item]);
        setNewChecklistItem({ title: '', category: 'General', description: '', is_critical: false });
      }
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  const deleteChecklistItem = async (itemId) => {
    try {
      const response = await apiFetch(`${backendUrl}/api/vehicle-specs/${checklistSpec.id}/checklist/${itemId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setChecklistItems(prev => prev.filter(item => item.id !== itemId));
      }
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  const checklistCategories = ['General', 'Engine', 'Brakes', 'Transmission', 'Electrical', 'Fluids', 'Suspension', 'Exhaust', 'Cooling', 'Fuel System'];

  useEffect(() => {
    fetchSpecifications();
  }, []);

  // Filter specifications based on search and filters
  useEffect(() => {
    let filtered = specifications;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(spec =>
        spec.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        spec.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (spec.series || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        spec.engine_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        spec.fuel_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        spec.transmission_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Make filter
    if (makeFilter !== 'all') {
      filtered = filtered.filter(spec => spec.make === makeFilter);
    }

    // Fuel type filter
    if (fuelFilter !== 'all') {
      filtered = filtered.filter(spec => spec.fuel_type.toLowerCase() === fuelFilter.toLowerCase());
    }

    setFilteredSpecs(filtered);
  }, [specifications, searchTerm, makeFilter, fuelFilter]);

  // Get unique makes for filter
  const uniqueMakes = [...new Set(specifications.map(spec => spec.make))];

  // Get color class for fluid color
  const getColorClass = (color) => {
    const colorMap = {
      'Green': 'bg-green-400',
      'Blue': 'bg-blue-400', 
      'Pink': 'bg-pink-400',
      'Orange': 'bg-orange-400',
      'Red': 'bg-red-400'
    };
    return colorMap[color] || 'bg-gray-400';
  };

  // Format fluid card
  const FluidCard = ({ title, fluid, icon: Icon, colorClass }) => {
    if (!fluid) return null;
    const hasData = (fluid.capacity_liters > 0) || fluid.viscosity || fluid.type || fluid.color || fluid.service_interval_km || (fluid.brand_recommendations?.length > 0);
    if (!hasData) return null;

    return (
      <Card className={`${colorClass} border-l-4`}>
        <CardContent className="pt-4">
          <div className="flex items-start gap-2">
            <Icon size={16} className="mt-1 opacity-70" />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm">{title}</h4>
              <div className="space-y-1 text-xs opacity-90">
                {fluid.capacity_liters > 0 && (
                  <div className="flex justify-between">
                    <span>Capacity:</span>
                    <span className="font-medium">{fluid.capacity_liters}L</span>
                  </div>
                )}
                {fluid.viscosity && (
                  <div className="flex justify-between">
                    <span>Viscosity:</span>
                    <span className="font-medium">{fluid.viscosity}</span>
                  </div>
                )}
                {fluid.type && (
                  <div className="flex justify-between">
                    <span>Type:</span>
                    <span className="font-medium">{fluid.type}</span>
                  </div>
                )}
                {fluid.color && (
                  <div className="flex justify-between items-center">
                    <span>Color:</span>
                    <div className="flex items-center gap-1">
                      <div className={`w-3 h-3 rounded-full border ${getColorClass(fluid.color)}`}></div>
                      <span className="font-medium">{fluid.color}</span>
                    </div>
                  </div>
                )}
                {fluid.service_interval_km && (
                  <div className="flex justify-between">
                    <span>Interval:</span>
                    <span className="font-medium">{(fluid.service_interval_km / 1000).toFixed(0)}K km</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading vehicle specifications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Vehicle Specifications Database</h2>
          <p className="text-muted-foreground">Comprehensive fluid specifications and maintenance data for Indian vehicles</p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={showChecklistDialog && !checklistSpec} onOpenChange={(open) => {
            if (open) {
              setChecklistSpec(null);
              setShowChecklistDialog(true);
            } else {
              setShowChecklistDialog(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2 text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700" data-testid="checklist-master-btn">
                <ClipboardList size={16} />
                Checklist
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Vehicle Specification Checklists</DialogTitle>
                <DialogDescription>
                  Select a vehicle specification to view or manage its maintenance checklist, work procedures, and inspection items.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {specifications.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="mx-auto h-10 w-10 mb-2 opacity-50" />
                    <p>No vehicle specifications found. Add a specification first.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {specifications.map(spec => (
                      <Card 
                        key={spec.id} 
                        className="cursor-pointer hover:border-amber-400 hover:shadow-md transition-all duration-200"
                        onClick={() => {
                          setShowChecklistDialog(false);
                          setTimeout(() => openChecklist(spec), 100);
                        }}
                        data-testid={`checklist-spec-card-${spec.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-sm">{spec.make} {spec.model}</h4>
                              <p className="text-xs text-muted-foreground">{spec.engine_code} | {spec.fuel_type} | {spec.transmission_type}</p>
                              <p className="text-xs text-muted-foreground mt-1">{spec.year_from}–{spec.year_to}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={spec.checklist?.length > 0 ? "default" : "secondary"} className="text-xs">
                                {spec.checklist?.length || 0} items
                              </Badge>
                              <ClipboardList size={16} className="text-amber-500" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Button 
            variant={showEngineProcedures ? "default" : "outline"} 
            className={`flex items-center gap-2 ${!showEngineProcedures ? 'text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-700' : ''}`}
            onClick={() => setShowEngineProcedures(!showEngineProcedures)}
            data-testid="engine-procedures-btn"
          >
            <ClipboardList size={16} />
            Engine Procedures
          </Button>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus size={16} />
                Add Vehicle Specification
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Vehicle Specification</DialogTitle>
              <DialogDescription>
                Create detailed vehicle specifications including fluid capacities, service intervals, and maintenance requirements for Indian vehicles.
              </DialogDescription>
            </DialogHeader>
            <AddVehicleSpecificationForm 
              key={showAddDialog ? 'open' : 'closed'}
              onSuccess={() => {
                setShowAddDialog(false);
                fetchSpecifications();
              }}
              onCancel={() => setShowAddDialog(false)}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Engine Procedures - toggled view */}
      {showEngineProcedures ? (
        <EngineProceduresSection />
      ) : (
      <>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Specifications</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.totalSpecifications || 0}</div>
            <p className="text-xs text-muted-foreground">Vehicle models covered</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Makes Covered</CardTitle>
            <Car className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {statistics.totalMakes || 0}
            </div>
            <p className="text-xs text-muted-foreground">Different manufacturers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Petrol Specs</CardTitle>
            <Gauge className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statistics.petrolSpecs || 0}
            </div>
            <p className="text-xs text-muted-foreground">Petrol engine specs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Diesel Specs</CardTitle>
            <Gauge className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {statistics.dieselSpecs || 0}
            </div>
            <p className="text-xs text-muted-foreground">Diesel engine specs</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  placeholder="Search by make, model, engine code, fuel type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 no-uppercase"
                />
              </div>
            </div>
            <Select value={makeFilter} onValueChange={setMakeFilter}>
              <SelectTrigger className="w-full lg:w-48">
                <SelectValue placeholder="All Makes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Makes</SelectItem>
                {uniqueMakes.map(make => (
                  <SelectItem key={make} value={make}>
                    {make}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={fuelFilter} onValueChange={setFuelFilter}>
              <SelectTrigger className="w-full lg:w-48">
                <SelectValue placeholder="All Fuel Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fuel Types</SelectItem>
                <SelectItem value="petrol">Petrol</SelectItem>
                <SelectItem value="diesel">Diesel</SelectItem>
                <SelectItem value="cng">CNG</SelectItem>
                <SelectItem value="electric">Electric</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Specifications List */}
      <div className="grid gap-4">
        {filteredSpecs.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Wrench className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold">No specifications found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {searchTerm || makeFilter !== 'all' || fuelFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'No vehicle specifications available.'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredSpecs.map((spec) => (
            <Card key={spec.id} className="transition-all hover:shadow-md">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Vehicle Details */}
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-xl">{spec.make} {spec.model}</h3>
                        <p className="text-sm text-muted-foreground">
                          {spec.year_from} - {spec.year_to}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {spec.series && <Badge className="bg-slate-700 text-white" data-testid={`spec-series-badge-${spec.id}`}>{spec.series}</Badge>}
                          <Badge variant="outline" data-testid={`spec-engine-code-badge-${spec.id}`}>Engine: {spec.engine_code}</Badge>
                          <Badge variant="outline">{spec.engine_displacement}</Badge>
                          <Badge variant={spec.fuel_type === 'Petrol' ? 'default' : 'secondary'}>
                            {spec.fuel_type}
                          </Badge>
                          <Badge variant="outline">{spec.transmission_type}</Badge>
                          {spec.transmission_code && (
                            <Badge variant="outline" data-testid={`spec-transmission-code-badge-${spec.id}`}>Trans: {spec.transmission_code}</Badge>
                          )}
                          <Badge variant="outline">{spec.drive_type}</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="opacity-70" />
                        <span className="text-muted-foreground">Service:</span>
                        <span className="font-medium">{(spec.service_interval_km / 1000).toFixed(0)}K km</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="opacity-70" />
                        <span className="text-muted-foreground">Oil Change:</span>
                        <span className="font-medium">{(spec.oil_change_interval_km / 1000).toFixed(0)}K km</span>
                      </div>
                    </div>
                  </div>

                  {/* Fluid Specifications Preview */}
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <FluidCard 
                        title="Engine Oil" 
                        fluid={spec.engine_oil} 
                        icon={Archive}
                        colorClass="border-l-blue-500"
                      />
                      <FluidCard 
                        title="Coolant" 
                        fluid={spec.coolant} 
                        icon={Zap}
                        colorClass="border-l-green-500"
                      />
                      {spec.transmission_oil && (
                        <FluidCard 
                          title="Transmission" 
                          fluid={spec.transmission_oil} 
                          icon={Settings2}
                          colorClass="border-l-purple-500"
                        />
                      )}
                      {spec.transfer_case_oil && (
                        <FluidCard 
                          title="Transfer Case" 
                          fluid={spec.transfer_case_oil} 
                          icon={Wrench}
                          colorClass="border-l-orange-500"
                        />
                      )}
                      {spec.rear_differential_oil && (
                        <FluidCard 
                          title="Rear Diff" 
                          fluid={spec.rear_differential_oil} 
                          icon={Gauge}
                          colorClass="border-l-red-500"
                        />
                      )}
                      {spec.front_differential_oil && (
                        <FluidCard 
                          title="Front Diff" 
                          fluid={spec.front_differential_oil} 
                          icon={Gauge}
                          colorClass="border-l-yellow-500"
                        />
                      )}
                      {spec.brake_fluid && (
                        <FluidCard 
                          title="Brake Fluid" 
                          fluid={spec.brake_fluid} 
                          icon={AlertCircle}
                          colorClass="border-l-rose-500"
                        />
                      )}
                      {spec.power_steering_fluid && (
                        <FluidCard 
                          title="Power Steering" 
                          fluid={spec.power_steering_fluid} 
                          icon={Settings2}
                          colorClass="border-l-cyan-500"
                        />
                      )}
                    </div>

                    {/* Filter Part Numbers */}
                    {(spec.engine_oil_filter || spec.air_filter || spec.fuel_filter || spec.cabin_filter) && (
                      <div className="mt-3 flex flex-wrap gap-3 text-xs" data-testid={`spec-filter-numbers-${spec.id}`}>
                        {spec.engine_oil_filter && (
                          <div className="flex items-center gap-1 bg-slate-50 border rounded px-2 py-1">
                            <span className="text-muted-foreground">Oil Filter:</span>
                            <span className="font-medium">{spec.engine_oil_filter}</span>
                          </div>
                        )}
                        {spec.air_filter && (
                          <div className="flex items-center gap-1 bg-slate-50 border rounded px-2 py-1">
                            <span className="text-muted-foreground">Air Filter:</span>
                            <span className="font-medium">{spec.air_filter}</span>
                          </div>
                        )}
                        {spec.fuel_filter && (
                          <div className="flex items-center gap-1 bg-slate-50 border rounded px-2 py-1">
                            <span className="text-muted-foreground">Fuel Filter:</span>
                            <span className="font-medium">{spec.fuel_filter}</span>
                          </div>
                        )}
                        {spec.cabin_filter && (
                          <div className="flex items-center gap-1 bg-slate-50 border rounded px-2 py-1">
                            <span className="text-muted-foreground">Cabin Filter:</span>
                            <span className="font-medium">{spec.cabin_filter}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-sm text-muted-foreground">
                    {spec.notes}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                  <Dialog open={showMediaDialog && selectedMediaSpec?.id === spec.id}
                         onOpenChange={(open) => {
                           setShowMediaDialog(open);
                           if (!open) setSelectedMediaSpec(null);
                         }}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedMediaSpec(spec)}
                        className="text-blue-600 hover:text-blue-700 hover:border-blue-300"
                        data-testid={`view-attachments-btn-${spec.id}`}
                      >
                        <FileText size={14} className="mr-1" />
                        Manuals & Media {((spec.photos?.length || 0) + (spec.videos?.length || 0) + (spec.documents?.length || 0)) > 0 ? `(${(spec.photos?.length || 0) + (spec.videos?.length || 0) + (spec.documents?.length || 0)})` : ''}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`attachments-dialog-${spec.id}`}>
                      <DialogHeader>
                        <DialogTitle>Manuals & Media - {selectedMediaSpec?.make} {selectedMediaSpec?.model} {selectedMediaSpec?.series}</DialogTitle>
                      </DialogHeader>
                      {selectedMediaSpec && <SpecMediaViewer spec={selectedMediaSpec} backendUrl={backendUrl} onUpdate={fetchSpecifications} />}
                    </DialogContent>
                  </Dialog>

                  <Dialog open={showEditDialog && selectedSpec?.id === spec.id} 
                         onOpenChange={(open) => {
                           setShowEditDialog(open);
                           if (!open) setSelectedSpec(null);
                         }}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setSelectedSpec(spec)}
                      >
                        <Edit size={14} className="mr-1" />
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Edit Vehicle Specification - {selectedSpec?.make} {selectedSpec?.model}</DialogTitle>
                      </DialogHeader>
                      {selectedSpec && (
                        <EditVehicleSpecificationForm 
                          specification={selectedSpec}
                          onSuccess={() => {
                            setShowEditDialog(false);
                            setSelectedSpec(null);
                            fetchSpecifications();
                          }}
                          onCancel={() => {
                            setShowEditDialog(false);
                            setSelectedSpec(null);
                          }}
                        />
                      )}
                    </DialogContent>
                  </Dialog>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openChecklist(spec)}
                    className="text-amber-600 hover:text-amber-700 hover:border-amber-300"
                    data-testid={`checklist-btn-${spec.id}`}
                  >
                    <ClipboardList size={14} className="mr-1" />
                    Checklist {spec.checklist?.length > 0 ? `(${spec.checklist.length})` : ''}
                  </Button>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDeleteSpec(spec.id, `${spec.make} ${spec.model}`)}
                    className="text-red-600 hover:text-red-700 hover:border-red-300"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      </>
      )}

      {/* Checklist Dialog - Per Vehicle Spec */}
      <Dialog open={showChecklistDialog && !!checklistSpec} onOpenChange={(open) => {
        setShowChecklistDialog(open);
        if (!open) {
          setChecklistSpec(null);
          setChecklistItems([]);
          setNewChecklistItem({ title: '', category: 'General', description: '', is_critical: false });
          fetchSpecifications();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="checklist-dialog-title">
              Checklist - {checklistSpec?.make} {checklistSpec?.model} ({checklistSpec?.engine_code})
            </DialogTitle>
          </DialogHeader>

          {checklistLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading checklist...</div>
          ) : (
            <div className="space-y-6">
              {/* Add New Item Form */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-sm">Add Checklist Item</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="cl-title">Title *</Label>
                    <Input
                      id="cl-title"
                      placeholder="e.g., Check timing chain tension"
                      value={newChecklistItem.title}
                      onChange={(e) => setNewChecklistItem(prev => ({ ...prev, title: e.target.value }))}
                      data-testid="checklist-item-title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cl-category">Category</Label>
                    <Select value={newChecklistItem.category} onValueChange={(val) => setNewChecklistItem(prev => ({ ...prev, category: val }))}>
                      <SelectTrigger data-testid="checklist-item-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {checklistCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="cl-desc">Description (Optional)</Label>
                  <Input
                    id="cl-desc"
                    placeholder="Additional details or instructions"
                    value={newChecklistItem.description}
                    onChange={(e) => setNewChecklistItem(prev => ({ ...prev, description: e.target.value }))}
                    data-testid="checklist-item-description"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newChecklistItem.is_critical}
                      onChange={(e) => setNewChecklistItem(prev => ({ ...prev, is_critical: e.target.checked }))}
                      className="rounded"
                      data-testid="checklist-item-critical"
                    />
                    <span className="text-sm text-red-600 font-medium">Mark as Critical</span>
                  </label>
                  <Button onClick={addChecklistItem} size="sm" disabled={!newChecklistItem.title.trim()} data-testid="add-checklist-item-btn">
                    <Plus size={14} className="mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>

              {/* Checklist Items List */}
              {checklistItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="mx-auto h-10 w-10 mb-2 opacity-50" />
                  <p>No checklist items yet. Add items above.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">{checklistItems.length} Checklist Items</h4>
                  </div>
                  {checklistCategories.filter(cat => checklistItems.some(item => item.category === cat)).map(category => (
                    <div key={category} className="mb-4">
                      <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                        {category}
                      </h5>
                      {checklistItems.filter(item => item.category === category).map((item) => (
                        <div key={item.id} className={`flex items-start justify-between p-3 rounded-lg border mb-2 ${item.is_critical ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`} data-testid={`checklist-item-${item.id}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{item.title}</span>
                              {item.is_critical && (
                                <Badge variant="destructive" className="text-xs">Critical</Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteChecklistItem(item.id)}
                            className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                            data-testid={`delete-checklist-item-${item.id}`}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Fixed section tags for workshop manuals & wiring diagrams (keep in sync with backend DOC_CATEGORIES)
const DOC_CATEGORIES = ["Engine", "Electrical", "AC/Climate", "Transmission", "Brakes", "Suspension", "General"];

// Vehicle Specification Media Viewer - view/upload/delete photos, videos, workshop manuals & wiring diagrams
const SpecMediaViewer = ({ spec, backendUrl, onUpdate }) => {
  const [photos, setPhotos] = useState(spec.photos || []);
  const [videos, setVideos] = useState(spec.videos || []);
  const [documents, setDocuments] = useState(spec.documents || []);
  const [uploading, setUploading] = useState(false);
  const [manualCategory, setManualCategory] = useState('General');
  const [wiringCategory, setWiringCategory] = useState('General');

  useEffect(() => {
    setPhotos(spec.photos || []);
    setVideos(spec.videos || []);
    setDocuments(spec.documents || []);
  }, [spec]);

  const uploadPhotos = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await axios.post(`${backendUrl}/api/vehicle-specs/${spec.id}/upload-photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotos(prev => [...prev, ...res.data.photos]);
      toast.success(`${files.length} photo(s) uploaded`);
      onUpdate && onUpdate();
    } catch (err) { toast.error('Photo upload failed'); }
    finally { setUploading(false); }
  };

  const uploadVideos = async (files) => {
    if (!files.length) return;
    if (files.length > 10) { toast.error('Max 10 videos'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await axios.post(`${backendUrl}/api/vehicle-specs/${spec.id}/upload-videos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setVideos(prev => [...prev, ...res.data.videos]);
      toast.success(`${files.length} video(s) uploaded`);
      onUpdate && onUpdate();
    } catch (err) { toast.error('Video upload failed'); }
    finally { setUploading(false); }
  };

  const uploadDocuments = async (files, docType, category) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('doc_type', docType);
      formData.append('category', category);
      files.forEach(f => formData.append('files', f));
      const res = await axios.post(`${backendUrl}/api/vehicle-specs/${spec.id}/upload-documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocuments(prev => [...prev, ...res.data.documents]);
      toast.success(`${files.length} document(s) uploaded`);
      onUpdate && onUpdate();
    } catch (err) { toast.error('Document upload failed'); }
    finally { setUploading(false); }
  };

  const deletePhoto = async (idx) => {
    try {
      await axios.delete(`${backendUrl}/api/vehicle-specs/${spec.id}/photos/${idx}`);
      setPhotos(prev => prev.filter((_, i) => i !== idx));
      onUpdate && onUpdate();
    } catch (err) { toast.error('Failed to delete photo'); }
  };
  const deleteVideo = async (idx) => {
    try {
      await axios.delete(`${backendUrl}/api/vehicle-specs/${spec.id}/videos/${idx}`);
      setVideos(prev => prev.filter((_, i) => i !== idx));
      onUpdate && onUpdate();
    } catch (err) { toast.error('Failed to delete video'); }
  };
  const deleteDocument = async (idx) => {
    try {
      await axios.delete(`${backendUrl}/api/vehicle-specs/${spec.id}/documents/${idx}`);
      setDocuments(prev => prev.filter((_, i) => i !== idx));
      onUpdate && onUpdate();
    } catch (err) { toast.error('Failed to delete document'); }
  };

  const manuals = documents.filter(d => d.doc_type === 'workshop_manual');
  const wiring = documents.filter(d => d.doc_type === 'wiring_diagram');

  const groupByCategory = (docs) => {
    const groups = {};
    docs.forEach(d => {
      const cat = d.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(d);
    });
    return DOC_CATEGORIES.filter(cat => groups[cat]?.length).map(cat => [cat, groups[cat]]);
  };

  return (
    <div className="space-y-6" data-testid="spec-media-viewer">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-base font-medium">Photos ({photos.length})</Label>
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={el => window.mediaPhotoInput = el}
            onChange={(e) => { uploadPhotos(Array.from(e.target.files)); e.target.value = ''; }} />
          <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => window.mediaPhotoInput?.click()} data-testid="media-add-photo-btn">
            <Camera className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        {photos.length === 0 ? <p className="text-sm text-muted-foreground">No photos uploaded</p> : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((p, idx) => (
              <div key={idx} className="relative group">
                <a href={`${backendUrl}/api/vehicle-specs/${spec.id}/photos/${idx}`} target="_blank" rel="noreferrer">
                  <img src={`${backendUrl}/api/vehicle-specs/${spec.id}/photos/${idx}`} alt={p.filename} className="w-full h-20 object-cover rounded border" />
                </a>
                <button type="button" onClick={() => deletePhoto(idx)} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100" data-testid={`delete-photo-${idx}`}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-base font-medium">Videos ({videos.length})</Label>
          <input type="file" accept="video/mp4,video/mov,video/avi,video/wmv,video/mkv,video/webm" multiple style={{ display: 'none' }} ref={el => window.mediaVideoInput = el}
            onChange={(e) => { uploadVideos(Array.from(e.target.files)); e.target.value = ''; }} />
          <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => window.mediaVideoInput?.click()} data-testid="media-add-video-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        {videos.length === 0 ? <p className="text-sm text-muted-foreground">No videos uploaded</p> : (
          <div className="space-y-2">
            {videos.map((v, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded p-2 border text-sm">
                <a href={`${backendUrl}/api/vehicle-specs/${spec.id}/videos/${idx}`} target="_blank" rel="noreferrer" className="text-purple-700 truncate hover:underline">{v.filename}</a>
                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => deleteVideo(idx)} data-testid={`delete-video-${idx}`}><X className="h-3 w-3" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-base font-medium">Workshop Manuals ({manuals.length})</Label>
          <div className="flex items-center gap-2">
            <select value={manualCategory} onChange={(e) => setManualCategory(e.target.value)}
              className="h-8 rounded-md border border-input bg-white px-2 text-xs" data-testid="media-manual-category-select">
              {DOC_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <input type="file" accept="application/pdf" multiple style={{ display: 'none' }} ref={el => window.mediaManualInput = el}
              onChange={(e) => { uploadDocuments(Array.from(e.target.files), 'workshop_manual', manualCategory); e.target.value = ''; }} />
            <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => window.mediaManualInput?.click()} data-testid="media-add-manual-btn">
              <FileText className="h-4 w-4 mr-1" /> Add PDF
            </Button>
          </div>
        </div>
        {manuals.length === 0 ? <p className="text-sm text-muted-foreground">No workshop manual uploaded</p> : (
          <div className="space-y-3">
            {groupByCategory(manuals).map(([cat, docs]) => (
              <div key={cat}>
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">{cat}</p>
                <div className="space-y-2">
                  {docs.map((d) => {
                    const idx = documents.findIndex(x => x === d);
                    return (
                      <div key={idx} className="flex items-center justify-between bg-amber-50 rounded p-2 border text-sm">
                        <a href={`${backendUrl}/api/vehicle-specs/${spec.id}/documents/${idx}`} target="_blank" rel="noreferrer" className="text-amber-700 truncate hover:underline flex items-center gap-1"><FileText className="h-3 w-3" />{d.filename}</a>
                        <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => deleteDocument(idx)} data-testid={`delete-document-${idx}`}><X className="h-3 w-3" /></Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-base font-medium">Wiring Diagrams ({wiring.length})</Label>
          <div className="flex items-center gap-2">
            <select value={wiringCategory} onChange={(e) => setWiringCategory(e.target.value)}
              className="h-8 rounded-md border border-input bg-white px-2 text-xs" data-testid="media-wiring-category-select">
              {DOC_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <input type="file" accept="application/pdf" multiple style={{ display: 'none' }} ref={el => window.mediaWiringInput = el}
              onChange={(e) => { uploadDocuments(Array.from(e.target.files), 'wiring_diagram', wiringCategory); e.target.value = ''; }} />
            <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => window.mediaWiringInput?.click()} data-testid="media-add-wiring-btn">
              <FileText className="h-4 w-4 mr-1" /> Add PDF
            </Button>
          </div>
        </div>
        {wiring.length === 0 ? <p className="text-sm text-muted-foreground">No wiring diagram uploaded</p> : (
          <div className="space-y-3">
            {groupByCategory(wiring).map(([cat, docs]) => (
              <div key={cat}>
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">{cat}</p>
                <div className="space-y-2">
                  {docs.map((d) => {
                    const idx = documents.findIndex(x => x === d);
                    return (
                      <div key={idx} className="flex items-center justify-between bg-blue-50 rounded p-2 border text-sm">
                        <a href={`${backendUrl}/api/vehicle-specs/${spec.id}/documents/${idx}`} target="_blank" rel="noreferrer" className="text-blue-700 truncate hover:underline flex items-center gap-1"><FileText className="h-3 w-3" />{d.filename}</a>
                        <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => deleteDocument(idx)} data-testid={`delete-document-wiring-${idx}`}><X className="h-3 w-3" /></Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Add Vehicle Specification Form Component
const AddVehicleSpecificationForm = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // File upload state
  const [specPhotos, setSpecPhotos] = useState([]);
  const [specVideos, setSpecVideos] = useState([]);
  const [specManuals, setSpecManuals] = useState([]);
  const [specWiringDiagrams, setSpecWiringDiagrams] = useState([]);
  const [specManualCategory, setSpecManualCategory] = useState('General');
  const [specWiringCategory, setSpecWiringCategory] = useState('General');

  // Vehicle Specification Form using React Hook Form
  const { register, handleSubmit: rhfHandleSubmit, control, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      make: '',
      model: '',
      series: '',
      year_from: '',
      year_to: '',
      engine_code: '',
      engine_displacement: '',
      fuel_type: 'Petrol',
      transmission_type: 'Manual',
      transmission_code: '',
      drive_type: 'FWD',
      service_interval_km: '10000',
      oil_change_interval_km: '5000',
      notes: '',
      
      // Engine Oil
      engine_oil_capacity: '',
      engine_oil_viscosity: '5W-30',
      engine_oil_type: 'Semi-Synthetic',
      engine_oil_brands: '',
      engine_oil_interval: '10000',
      engine_oil_notes: '',
      
      // Coolant
      coolant_capacity: '',
      coolant_color: 'Green',
      coolant_brands: '',
      coolant_mixing_ratio: '50:50',
      coolant_temp_rating: '-37°C to +129°C',
      coolant_interval: '40000',
      coolant_notes: '',
      
      // Transmission Oil
      trans_oil_capacity: '',
      trans_oil_viscosity: '80W-90',
      trans_oil_type: 'Mineral',
      trans_oil_brands: '',
      trans_oil_interval: '40000',
      trans_oil_notes: '',
      
      // Transfer Case Oil (4WD)
      transfer_case_capacity: '',
      transfer_case_viscosity: '',
      transfer_case_brands: '',
      
      // Front Differential Oil
      front_diff_capacity: '',
      front_diff_viscosity: '',
      front_diff_brands: '',
      
      // Rear Differential Oil
      rear_diff_capacity: '',
      rear_diff_viscosity: '',
      rear_diff_brands: '',
      
      // Brake Fluid
      brake_fluid_capacity: '',
      brake_fluid_grade: 'DOT4',
      brake_fluid_brands: '',
      
      // Power Steering Fluid
      ps_fluid_capacity: '',
      ps_fluid_type: '',
      ps_fluid_brands: '',
      
      // Filter Part Numbers
      engine_oil_filter: '',
      air_filter: '',
      fuel_filter: '',
      cabin_filter: ''
    }
  });

  // Cascade vehicle data from vehicle_data database
  const [specMakes, setSpecMakes] = useState([]);
  const [specModels, setSpecModels] = useState([]);
  const [specSeries, setSpecSeries] = useState([]);
  const specWatchedMake = watch('make');
  const specWatchedModel = watch('model');

  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/vehicle-data/makes`);
        setSpecMakes((response.data.makes || []).filter(m => m && m.trim() !== '').sort());
      } catch (error) { setSpecMakes([]); }
    };
    fetchMakes();
  }, []);

  useEffect(() => {
    if (!specWatchedMake) { setSpecModels([]); setValue('model', ''); setValue('series', ''); return; }
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/vehicle-data/models/${encodeURIComponent(specWatchedMake)}`);
        setSpecModels((response.data.models || []).filter(m => m && m.trim() !== '').sort());
      } catch (error) { setSpecModels([]); }
    };
    fetchModels();
    setValue('model', '');
    setValue('series', '');
  }, [specWatchedMake]);

  useEffect(() => {
    if (!specWatchedMake || !specWatchedModel) { setSpecSeries([]); setValue('series', ''); return; }
    const fetchSeries = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/vehicle-data/series/${encodeURIComponent(specWatchedMake)}/${encodeURIComponent(specWatchedModel)}`);
        setSpecSeries((response.data.series || []).filter(s => s && s.trim() !== '').sort());
      } catch (error) { setSpecSeries([]); }
    };
    fetchSeries();
    setValue('series', '');
  }, [specWatchedMake, specWatchedModel]);

  // Handle form submission using React Hook Form
  const handleSubmit = rhfHandleSubmit(async (data) => {
    if (!data.make?.trim() || !data.model?.trim() || !data.engine_code?.trim()) {
      toast.error("Please fill in all required fields (Make, Model, Engine Code)");
      return;
    }

    setLoading(true);

    try {
      // Prepare data for submission
      const submitData = {
        make: data.make.trim(),
        model: data.model.trim(),
        series: data.series?.trim() || '',
        year_from: parseInt(data.year_from),
        year_to: parseInt(data.year_to),
        engine_code: data.engine_code.trim(),
        engine_displacement: data.engine_displacement?.trim() || null,
        fuel_type: data.fuel_type,
        transmission_type: data.transmission_type,
        transmission_code: data.transmission_code?.trim() || null,
        drive_type: data.drive_type,
        service_interval_km: parseInt(data.service_interval_km),
        oil_change_interval_km: parseInt(data.oil_change_interval_km),
        notes: data.notes?.trim() || null,
        
        // Engine Oil
        engine_oil: {
          name: "Engine Oil",
          capacity_liters: parseFloat(data.engine_oil_capacity) || 0,
          viscosity: data.engine_oil_viscosity,
          type: data.engine_oil_type,
          brand_recommendations: data.engine_oil_brands ? data.engine_oil_brands.split(',').map(b => b.trim()) : null,
          service_interval_km: parseInt(data.engine_oil_interval),
          notes: data.engine_oil_notes?.trim() || null
        },
        
        // Coolant
        coolant: {
          name: "Coolant",
          capacity_liters: parseFloat(data.coolant_capacity) || 0,
          color: data.coolant_color,
          brand_recommendations: data.coolant_brands ? data.coolant_brands.split(',').map(b => b.trim()) : null,
          mixing_ratio: data.coolant_mixing_ratio,
          temp_rating: data.coolant_temp_rating,
          service_interval_km: parseInt(data.coolant_interval),
          notes: data.coolant_notes?.trim() || null
        },
        
        // Transmission Oil
        transmission_oil: {
          name: "Transmission Oil",
          capacity_liters: parseFloat(data.trans_oil_capacity) || 0,
          viscosity: data.trans_oil_viscosity,
          type: data.trans_oil_type,
          brand_recommendations: data.trans_oil_brands ? data.trans_oil_brands.split(',').map(b => b.trim()) : null,
          service_interval_km: parseInt(data.trans_oil_interval),
          notes: data.trans_oil_notes?.trim() || null
        },
        
        // Transfer Case Oil (optional - 4WD)
        transfer_case_oil: data.transfer_case_capacity ? {
          name: "Transfer Case Oil",
          capacity_liters: parseFloat(data.transfer_case_capacity) || 0,
          viscosity: data.transfer_case_viscosity?.trim() || null,
          brand_recommendations: data.transfer_case_brands ? data.transfer_case_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Front Differential Oil (optional)
        front_differential_oil: data.front_diff_capacity ? {
          name: "Front Differential Oil",
          capacity_liters: parseFloat(data.front_diff_capacity) || 0,
          viscosity: data.front_diff_viscosity?.trim() || null,
          brand_recommendations: data.front_diff_brands ? data.front_diff_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Rear Differential Oil (optional)
        rear_differential_oil: data.rear_diff_capacity ? {
          name: "Rear Differential Oil",
          capacity_liters: parseFloat(data.rear_diff_capacity) || 0,
          viscosity: data.rear_diff_viscosity?.trim() || null,
          brand_recommendations: data.rear_diff_brands ? data.rear_diff_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Brake Fluid (optional)
        brake_fluid: (data.brake_fluid_capacity || data.brake_fluid_grade) ? {
          name: "Brake Fluid",
          capacity_liters: parseFloat(data.brake_fluid_capacity) || 0,
          type: data.brake_fluid_grade?.trim() || null,
          brand_recommendations: data.brake_fluid_brands ? data.brake_fluid_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Power Steering Fluid (optional)
        power_steering_fluid: data.ps_fluid_capacity ? {
          name: "Power Steering Fluid",
          capacity_liters: parseFloat(data.ps_fluid_capacity) || 0,
          type: data.ps_fluid_type?.trim() || null,
          brand_recommendations: data.ps_fluid_brands ? data.ps_fluid_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Filter Part Numbers
        engine_oil_filter: data.engine_oil_filter?.trim() || null,
        air_filter: data.air_filter?.trim() || null,
        fuel_filter: data.fuel_filter?.trim() || null,
        cabin_filter: data.cabin_filter?.trim() || null
      };

      const response = await apiFetch(`${backendUrl}/api/vehicle-specs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const result = await response.json();
        const specId = result.id;
        let uploadCount = 0;

        // Upload photos
        if (specPhotos.length > 0) {
          try {
            const formData = new FormData();
            specPhotos.forEach(f => formData.append('files', f));
            await axios.post(`${backendUrl}/api/vehicle-specs/${specId}/upload-photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            uploadCount += specPhotos.length;
          } catch (err) { toast.error('Photo upload failed'); }
        }

        // Upload videos
        if (specVideos.length > 0) {
          try {
            toast.info(`Uploading ${specVideos.length} video(s)...`);
            const formData = new FormData();
            specVideos.forEach(f => formData.append('files', f));
            await axios.post(`${backendUrl}/api/vehicle-specs/${specId}/upload-videos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            uploadCount += specVideos.length;
          } catch (err) { toast.error('Video upload failed'); }
        }

        // Upload workshop manuals
        if (specManuals.length > 0) {
          try {
            const formData = new FormData();
            formData.append('doc_type', 'workshop_manual');
            formData.append('category', specManualCategory);
            specManuals.forEach(f => formData.append('files', f));
            await axios.post(`${backendUrl}/api/vehicle-specs/${specId}/upload-documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            uploadCount += specManuals.length;
          } catch (err) { toast.error('Workshop manual upload failed'); }
        }

        // Upload wiring diagrams
        if (specWiringDiagrams.length > 0) {
          try {
            const formData = new FormData();
            formData.append('doc_type', 'wiring_diagram');
            formData.append('category', specWiringCategory);
            specWiringDiagrams.forEach(f => formData.append('files', f));
            await axios.post(`${backendUrl}/api/vehicle-specs/${specId}/upload-documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            uploadCount += specWiringDiagrams.length;
          } catch (err) { toast.error('Wiring diagram upload failed'); }
        }

        reset();
        setSpecPhotos([]);
        setSpecVideos([]);
        setSpecManuals([]);
        setSpecWiringDiagrams([]);
        setSpecManualCategory('General');
        setSpecWiringCategory('General');
        toast.success(uploadCount > 0 ? `Specification created with ${uploadCount} file(s)!` : 'Vehicle specification created successfully!');
        onSuccess();
      } else {
        const errorData = await response.json();
        console.error('Failed to create vehicle specification:', errorData);
        toast.error('Failed to create vehicle specification. Please try again.');
      }
    } catch (error) {
      console.error('Error creating vehicle specification:', error);
      toast.error('An error occurred while creating the vehicle specification.');
    } finally {
      setLoading(false);
    }
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car size={18} />
            Basic Vehicle Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="make">Make *</Label>
              <select
                value={specWatchedMake || ''}
                onChange={(e) => setValue('make', e.target.value)}
                className={`flex h-10 w-full rounded-md border ${errors.make ? 'border-red-500' : 'border-input'} bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
              >
                <option value="">Select Make</option>
                {specMakes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
              <input type="hidden" {...register('make', { required: 'Make is required' })} />
              {errors.make && (
                <p className="text-sm text-red-500 mt-1">{errors.make.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="model">Model *</Label>
              <select
                value={specWatchedModel || ''}
                onChange={(e) => setValue('model', e.target.value)}
                disabled={!specWatchedMake}
                className={`flex h-10 w-full rounded-md border ${errors.model ? 'border-red-500' : 'border-input'} bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50`}
              >
                <option value="">{specWatchedMake ? `Select ${specWatchedMake} Model` : 'Select Make first'}</option>
                {specModels.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
              <input type="hidden" {...register('model', { required: 'Model is required' })} />
              {errors.model && (
                <p className="text-sm text-red-500 mt-1">{errors.model.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="series">Series</Label>
              <select
                value={watch('series') || ''}
                onChange={(e) => setValue('series', e.target.value)}
                disabled={!specWatchedMake || !specWatchedModel}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                <option value="">{specWatchedModel ? `Select ${specWatchedModel} Series` : 'Select Model first'}</option>
                {specSeries.map(series => (
                  <option key={series} value={series}>{series}</option>
                ))}
              </select>
              <input type="hidden" {...register('series')} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="year_from">Year From *</Label>
              <Input
                id="year_from"
                type="number"
                min="1950"
                max="2030"
                {...register('year_from', { 
                  required: 'Starting year is required',
                  min: { value: 1950, message: 'Year must be 1950 or later' }
                })}
                className={errors.year_from ? 'border-red-500' : ''}
                placeholder="2020"
              />
              {errors.year_from && (
                <p className="text-sm text-red-500 mt-1">{errors.year_from.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="year_to">Year To *</Label>
              <Input
                id="year_to"
                type="number"
                min="1950"
                max="2030"
                {...register('year_to', { 
                  required: 'Ending year is required'
                })}
                className={errors.year_to ? 'border-red-500' : ''}
                placeholder="2024"
              />
              {errors.year_to && (
                <p className="text-sm text-red-500 mt-1">{errors.year_to.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="engine_code">Engine Code *</Label>
              <Input
                id="engine_code"
                {...register('engine_code', { required: 'Engine code is required' })}
                className={errors.engine_code ? 'border-red-500' : ''}
                placeholder="e.g., K12M"
              />
              {errors.engine_code && (
                <p className="text-sm text-red-500 mt-1">{errors.engine_code.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="engine_displacement">Displacement</Label>
              <Input
                id="engine_displacement"
                {...register('engine_displacement')}
                placeholder="e.g., 1.2L"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="fuel_type">Fuel Type *</Label>
              <Controller
                control={control}
                name="fuel_type"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Petrol">Petrol</SelectItem>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="CNG">CNG</SelectItem>
                      <SelectItem value="Electric">Electric</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div>
              <Label htmlFor="transmission_type">Transmission *</Label>
              <Controller
                control={control}
                name="transmission_type"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Manual">Manual</SelectItem>
                      <SelectItem value="Automatic">Automatic</SelectItem>
                      <SelectItem value="CVT">CVT</SelectItem>
                      <SelectItem value="AMT">AMT</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div>
              <Label htmlFor="transmission_code">Transmission Code</Label>
              <Input
                id="transmission_code"
                {...register('transmission_code')}
                placeholder="e.g., AQ250, CD17"
              />
            </div>

            <div>
              <Label htmlFor="drive_type">Drive Type *</Label>
              <Controller
                control={control}
                name="drive_type"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FWD">FWD</SelectItem>
                      <SelectItem value="RWD">RWD</SelectItem>
                      <SelectItem value="AWD">AWD</SelectItem>
                      <SelectItem value="4WD">4WD</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Engine Oil Specifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive size={18} />
            Engine Oil Specifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="engine_oil_capacity">Capacity (Liters) *</Label>
              <Input
                id="engine_oil_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('engine_oil_capacity', {
                  required: 'Engine oil capacity is required',
                  min: { value: 0.1, message: 'Capacity must be greater than 0' }
                })}
                className={errors.engine_oil_capacity ? 'border-red-500' : ''}
                placeholder="4.2"
              />
              {errors.engine_oil_capacity && (
                <p className="text-sm text-red-500 mt-1">{errors.engine_oil_capacity.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="engine_oil_viscosity">Viscosity</Label>
              <Controller
                control={control}
                name="engine_oil_viscosity"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0W-20">0W-20</SelectItem>
                      <SelectItem value="5W-20">5W-20</SelectItem>
                      <SelectItem value="5W-30">5W-30</SelectItem>
                      <SelectItem value="5W-40">5W-40</SelectItem>
                      <SelectItem value="10W-30">10W-30</SelectItem>
                      <SelectItem value="10W-40">10W-40</SelectItem>
                      <SelectItem value="15W-40">15W-40</SelectItem>
                      <SelectItem value="20W-50">20W-50</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div>
              <Label htmlFor="engine_oil_type">Oil Type</Label>
              <Controller
                control={control}
                name="engine_oil_type"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mineral">Mineral</SelectItem>
                      <SelectItem value="Semi-Synthetic">Semi-Synthetic</SelectItem>
                      <SelectItem value="Fully Synthetic">Fully Synthetic</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="engine_oil_brands">Brand Recommendations (comma separated)</Label>
              <Input
                id="engine_oil_brands"
                {...register('engine_oil_brands')}
                placeholder="Castrol GTX, Mobil Super, Shell Helix"
              />
            </div>

            <div>
              <Label htmlFor="engine_oil_interval">Service Interval (km)</Label>
              <Input
                id="engine_oil_interval"
                type="number"
                {...register('engine_oil_interval', {
                  required: 'Engine oil service interval is required',
                  min: { value: 1000, message: 'Minimum 1000 km' }
                })}
                className={errors.engine_oil_interval ? 'border-red-500' : ''}
                placeholder="10000"
              />
              {errors.engine_oil_interval && (
                <p className="text-sm text-red-500 mt-1">{errors.engine_oil_interval.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="engine_oil_notes">Notes</Label>
            <Textarea
              id="engine_oil_notes"
              {...register('engine_oil_notes')}
              placeholder="Additional notes about engine oil specifications"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Coolant Specifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap size={18} />
            Coolant Specifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="coolant_capacity">Capacity (Liters) *</Label>
              <Input
                id="coolant_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('coolant_capacity', {
                  required: 'Coolant capacity is required',
                  min: { value: 0.1, message: 'Capacity must be greater than 0' }
                })}
                className={errors.coolant_capacity ? 'border-red-500' : ''}
                placeholder="6.2"
              />
              {errors.coolant_capacity && (
                <p className="text-sm text-red-500 mt-1">{errors.coolant_capacity.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="coolant_color">Color</Label>
              <Controller
                control={control}
                name="coolant_color"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Green">Green</SelectItem>
                      <SelectItem value="Blue">Blue</SelectItem>
                      <SelectItem value="Pink">Pink</SelectItem>
                      <SelectItem value="Orange">Orange</SelectItem>
                      <SelectItem value="Red">Red</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div>
              <Label htmlFor="coolant_mixing_ratio">Mixing Ratio</Label>
              <Controller
                control={control}
                name="coolant_mixing_ratio"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50:50">50:50</SelectItem>
                      <SelectItem value="60:40">60:40</SelectItem>
                      <SelectItem value="70:30">70:30</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="coolant_brands">Brand Recommendations (comma separated)</Label>
              <Input
                id="coolant_brands"
                {...register('coolant_brands')}
                placeholder="Castrol Radicool, Shell Radiator Fluid"
              />
            </div>

            <div>
              <Label htmlFor="coolant_interval">Service Interval (km)</Label>
              <Input
                id="coolant_interval"
                type="number"
                min="1000"
                {...register('coolant_interval')}
                placeholder="40000"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transmission Oil (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 size={18} />
            Transmission Oil (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="trans_oil_capacity">Capacity (Liters)</Label>
              <Input
                id="trans_oil_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('trans_oil_capacity')}
                placeholder="1.8"
              />
            </div>

            <div>
              <Label htmlFor="trans_oil_viscosity">Viscosity</Label>
              <Input
                id="trans_oil_viscosity"
                {...register('trans_oil_viscosity')}
                placeholder="80W-90"
              />
            </div>

            <div>
              <Label htmlFor="trans_oil_type">Oil Type</Label>
              <Input
                id="trans_oil_type"
                {...register('trans_oil_type')}
                placeholder="Gear Oil"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="trans_oil_brands">Brand Recommendations (comma separated)</Label>
            <Input
              id="trans_oil_brands"
              {...register('trans_oil_brands')}
              placeholder="Castrol GL-4, Shell Spirax"
            />
          </div>
        </CardContent>
      </Card>

      {/* Transfer Case Oil (Optional - 4WD) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench size={18} />
            Transfer Case Oil (Optional - 4WD)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="transfer_case_capacity">Capacity (Liters)</Label>
              <Input
                id="transfer_case_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('transfer_case_capacity')}
                placeholder="1.0"
              />
            </div>
            <div>
              <Label htmlFor="transfer_case_viscosity">Oil Grade</Label>
              <Input
                id="transfer_case_viscosity"
                {...register('transfer_case_viscosity')}
                placeholder="75W-85"
              />
            </div>
            <div>
              <Label htmlFor="transfer_case_brands">Brand Recommendations (comma separated)</Label>
              <Input
                id="transfer_case_brands"
                {...register('transfer_case_brands')}
                placeholder="Castrol Syntrax, Toyota Genuine"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Differential Oil (Optional - 4WD/RWD/AWD) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge size={18} />
            Differential Oil (Optional - 4WD/RWD/AWD)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="front_diff_capacity">Front Diff Capacity (Liters)</Label>
              <Input
                id="front_diff_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('front_diff_capacity')}
                placeholder="1.2"
              />
            </div>
            <div>
              <Label htmlFor="front_diff_viscosity">Front Diff Oil Grade</Label>
              <Input
                id="front_diff_viscosity"
                {...register('front_diff_viscosity')}
                placeholder="80W-90"
              />
            </div>
            <div>
              <Label htmlFor="front_diff_brands">Front Diff Brand Recommendations</Label>
              <Input
                id="front_diff_brands"
                {...register('front_diff_brands')}
                placeholder="Castrol EPX, Shell Spirax"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="rear_diff_capacity">Rear Diff Capacity (Liters)</Label>
              <Input
                id="rear_diff_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('rear_diff_capacity')}
                placeholder="1.5"
              />
            </div>
            <div>
              <Label htmlFor="rear_diff_viscosity">Rear Diff Oil Grade</Label>
              <Input
                id="rear_diff_viscosity"
                {...register('rear_diff_viscosity')}
                placeholder="85W-140"
              />
            </div>
            <div>
              <Label htmlFor="rear_diff_brands">Rear Diff Brand Recommendations</Label>
              <Input
                id="rear_diff_brands"
                {...register('rear_diff_brands')}
                placeholder="Castrol EPX, Shell Spirax"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brake Fluid (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle size={18} />
            Brake Fluid (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="brake_fluid_capacity">Capacity (Liters)</Label>
              <Input
                id="brake_fluid_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('brake_fluid_capacity')}
                placeholder="0.5"
              />
            </div>
            <div>
              <Label htmlFor="brake_fluid_grade">Grade (DOT Rating)</Label>
              <Controller
                control={control}
                name="brake_fluid_grade"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOT3">DOT3</SelectItem>
                      <SelectItem value="DOT4">DOT4</SelectItem>
                      <SelectItem value="DOT5.1">DOT5.1</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div>
              <Label htmlFor="brake_fluid_brands">Brand Recommendations (comma separated)</Label>
              <Input
                id="brake_fluid_brands"
                {...register('brake_fluid_brands')}
                placeholder="Castrol DOT4, Bosch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Power Steering Fluid (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 size={18} />
            Power Steering Fluid (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="ps_fluid_capacity">Capacity (Liters)</Label>
              <Input
                id="ps_fluid_capacity"
                type="number"
                step="0.1"
                min="0"
                {...register('ps_fluid_capacity')}
                placeholder="1.0"
              />
            </div>
            <div>
              <Label htmlFor="ps_fluid_type">Fluid Type</Label>
              <Input
                id="ps_fluid_type"
                {...register('ps_fluid_type')}
                placeholder="ATF / PSF"
              />
            </div>
            <div>
              <Label htmlFor="ps_fluid_brands">Brand Recommendations (comma separated)</Label>
              <Input
                id="ps_fluid_brands"
                {...register('ps_fluid_brands')}
                placeholder="Castrol PSF, Dexron III"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter Part Numbers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package size={18} />
            Filter Part Numbers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="engine_oil_filter">Engine Oil Filter</Label>
              <Input
                id="engine_oil_filter"
                {...register('engine_oil_filter')}
                placeholder="e.g., 26300-2E100"
              />
            </div>

            <div>
              <Label htmlFor="air_filter">Air Filter</Label>
              <Input
                id="air_filter"
                {...register('air_filter')}
                placeholder="e.g., 28113-S1000"
              />
            </div>

            <div>
              <Label htmlFor="fuel_filter">Fuel Filter</Label>
              <Input
                id="fuel_filter"
                {...register('fuel_filter')}
                placeholder="e.g., 31112-2E000"
              />
            </div>

            <div>
              <Label htmlFor="cabin_filter">Cabin Filter</Label>
              <Input
                id="cabin_filter"
                {...register('cabin_filter')}
                placeholder="e.g., 97133-S1000"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Intervals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar size={18} />
            Service Intervals
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="service_interval_km">Service Interval (km)</Label>
              <Input
                id="service_interval_km"
                type="number"
                min="1000"
                {...register('service_interval_km')}
                placeholder="10000"
              />
            </div>

            <div>
              <Label htmlFor="oil_change_interval_km">Oil Change Interval (km)</Label>
              <Input
                id="oil_change_interval_km"
                type="number"
                min="1000"
                {...register('oil_change_interval_km')}
                placeholder="5000"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardContent className="pt-6">
          <div>
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Any additional information about this vehicle specification"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* File Uploads */}
      <Card>
        <CardHeader>
          <CardTitle>Attachments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border rounded-lg p-4 bg-cyan-50">
            <Label className="text-base font-medium mb-2 block">Vehicle/Engine Photos</Label>
            <div className="flex space-x-2">
              <Input value={specPhotos.map(f => f.name).join(', ')} placeholder="No photos selected" readOnly className="flex-1 bg-white" />
              <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={el => window.specPhotoInput = el}
                onChange={(e) => { setSpecPhotos(Array.from(e.target.files)); toast.success(`${e.target.files.length} photo(s) selected`); }} />
              <Button type="button" variant="outline" className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300" onClick={() => window.specPhotoInput?.click()}>
                <Camera className="h-4 w-4 mr-2" /> Add Photos
              </Button>
            </div>
          </div>
          <div className="border rounded-lg p-4 bg-purple-50">
            <Label className="text-base font-medium mb-2 block">Reference Videos (Max 10)</Label>
            <div className="flex space-x-2">
              <Input value={specVideos.map(f => f.name).join(', ')} placeholder="No videos selected" readOnly className="flex-1 bg-white" />
              <input type="file" accept="video/mp4,video/mov,video/avi,video/wmv,video/mkv,video/webm" multiple style={{ display: 'none' }} ref={el => window.specVideoInput = el}
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > 10) { toast.error('Max 10 videos'); return; }
                  if (files.some(f => f.size > 100 * 1024 * 1024)) { toast.error('Max 100MB per video'); return; }
                  setSpecVideos(files); toast.success(`${files.length} video(s) selected`);
                }} />
              <Button type="button" variant="outline" className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300" onClick={() => window.specVideoInput?.click()}>
                <Plus className="h-4 w-4 mr-2" /> Add Videos
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">MP4, MOV, AVI, WMV, MKV, WebM (max 100MB each)</p>
            {specVideos.length > 0 && (
              <div className="mt-2 space-y-1">
                {specVideos.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded p-2 border text-sm">
                    <span className="text-purple-700 truncate">{file.name} ({(file.size / (1024 * 1024)).toFixed(1)} MB)</span>
                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => setSpecVideos(prev => prev.filter((_, i) => i !== idx))}><X className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border rounded-lg p-4 bg-amber-50" data-testid="spec-workshop-manual-upload">
            <Label className="text-base font-medium mb-2 block">Workshop Manual (PDF)</Label>
            <div className="flex space-x-2 mb-2">
              <select
                value={specManualCategory}
                onChange={(e) => setSpecManualCategory(e.target.value)}
                className="flex h-10 w-40 rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="spec-manual-category-select"
              >
                {DOC_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <Input value={specManuals.map(f => f.name).join(', ')} placeholder="No manual selected" readOnly className="flex-1 bg-white" />
              <input type="file" accept="application/pdf" multiple style={{ display: 'none' }} ref={el => window.specManualInput = el}
                onChange={(e) => { setSpecManuals(Array.from(e.target.files)); toast.success(`${e.target.files.length} manual(s) selected`); }} />
              <Button type="button" variant="outline" className="bg-amber-100 hover:bg-amber-200 text-amber-700 border-amber-300" onClick={() => window.specManualInput?.click()} data-testid="spec-workshop-manual-upload-btn">
                <FileText className="h-4 w-4 mr-2" /> Add Manual
              </Button>
            </div>
          </div>
          <div className="border rounded-lg p-4 bg-blue-50" data-testid="spec-wiring-diagram-upload">
            <Label className="text-base font-medium mb-2 block">Wiring Diagram (PDF)</Label>
            <div className="flex space-x-2 mb-2">
              <select
                value={specWiringCategory}
                onChange={(e) => setSpecWiringCategory(e.target.value)}
                className="flex h-10 w-40 rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="spec-wiring-category-select"
              >
                {DOC_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <Input value={specWiringDiagrams.map(f => f.name).join(', ')} placeholder="No wiring diagram selected" readOnly className="flex-1 bg-white" />
              <input type="file" accept="application/pdf" multiple style={{ display: 'none' }} ref={el => window.specWiringInput = el}
                onChange={(e) => { setSpecWiringDiagrams(Array.from(e.target.files)); toast.success(`${e.target.files.length} wiring diagram(s) selected`); }} />
              <Button type="button" variant="outline" className="bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300" onClick={() => window.specWiringInput?.click()} data-testid="spec-wiring-diagram-upload-btn">
                <FileText className="h-4 w-4 mr-2" /> Add Wiring Diagram
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button type="submit" disabled={loading} className="flex-1">
          {loading ? 'Creating...' : 'Create Vehicle Specification'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={reset}
          disabled={loading}
          className="flex-none"
        >
          Reset
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Edit Vehicle Specification Form Component
const EditVehicleSpecificationForm = ({ specification, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    make: specification?.make || '',
    model: specification?.model || '',
    series: specification?.series || '',
    year_from: specification?.year_from?.toString() || '',
    year_to: specification?.year_to?.toString() || '',
    engine_code: specification?.engine_code || '',
    engine_displacement: specification?.engine_displacement || '',
    fuel_type: specification?.fuel_type || 'Petrol',
    transmission_type: specification?.transmission_type || 'Manual',
    transmission_code: specification?.transmission_code || '',
    drive_type: specification?.drive_type || 'FWD',
    service_interval_km: specification?.service_interval_km?.toString() || '10000',
    oil_change_interval_km: specification?.oil_change_interval_km?.toString() || '5000',
    notes: specification?.notes || '',
    
    // Engine Oil
    engine_oil_capacity: specification?.engine_oil?.capacity_liters?.toString() || '',
    engine_oil_viscosity: specification?.engine_oil?.viscosity || '5W-30',
    engine_oil_type: specification?.engine_oil?.type || 'Semi-Synthetic',
    engine_oil_brands: specification?.engine_oil?.brand_recommendations?.join(', ') || '',
    engine_oil_interval: specification?.engine_oil?.service_interval_km?.toString() || '10000',
    engine_oil_notes: specification?.engine_oil?.notes || '',
    
    // Coolant
    coolant_capacity: specification?.coolant?.capacity_liters?.toString() || '',
    coolant_color: specification?.coolant?.color || 'Green',
    coolant_brands: specification?.coolant?.brand_recommendations?.join(', ') || '',
    coolant_mixing_ratio: specification?.coolant?.mixing_ratio || '50:50',
    coolant_temp_rating: specification?.coolant?.temperature_rating || '-37°C to +129°C',
    coolant_interval: specification?.coolant?.service_interval_km?.toString() || '40000',
    coolant_notes: specification?.coolant?.notes || '',
    
    // Transmission Oil
    trans_oil_capacity: specification?.transmission_oil?.capacity_liters?.toString() || '',
    trans_oil_viscosity: specification?.transmission_oil?.viscosity || '80W-90',
    trans_oil_type: specification?.transmission_oil?.type || 'Mineral',
    trans_oil_brands: specification?.transmission_oil?.brand_recommendations?.join(', ') || '',
    trans_oil_interval: specification?.transmission_oil?.service_interval_km?.toString() || '40000',
    trans_oil_notes: specification?.transmission_oil?.notes || '',
    
    // Transfer Case Oil
    transfer_case_capacity: specification?.transfer_case_oil?.capacity_liters?.toString() || '',
    transfer_case_viscosity: specification?.transfer_case_oil?.viscosity || '',
    transfer_case_brands: specification?.transfer_case_oil?.brand_recommendations?.join(', ') || '',
    
    // Front Differential Oil
    front_diff_capacity: specification?.front_differential_oil?.capacity_liters?.toString() || '',
    front_diff_viscosity: specification?.front_differential_oil?.viscosity || '',
    front_diff_brands: specification?.front_differential_oil?.brand_recommendations?.join(', ') || '',
    
    // Rear Differential Oil
    rear_diff_capacity: specification?.rear_differential_oil?.capacity_liters?.toString() || '',
    rear_diff_viscosity: specification?.rear_differential_oil?.viscosity || '',
    rear_diff_brands: specification?.rear_differential_oil?.brand_recommendations?.join(', ') || '',
    
    // Brake Fluid
    brake_fluid_capacity: specification?.brake_fluid?.capacity_liters?.toString() || '',
    brake_fluid_grade: specification?.brake_fluid?.type || 'DOT4',
    brake_fluid_brands: specification?.brake_fluid?.brand_recommendations?.join(', ') || '',
    
    // Power Steering Fluid
    ps_fluid_capacity: specification?.power_steering_fluid?.capacity_liters?.toString() || '',
    ps_fluid_type: specification?.power_steering_fluid?.type || '',
    ps_fluid_brands: specification?.power_steering_fluid?.brand_recommendations?.join(', ') || '',
    
    // Filter Part Numbers
    engine_oil_filter: specification?.engine_oil_filter || '',
    air_filter: specification?.air_filter || '',
    fuel_filter: specification?.fuel_filter || '',
    cabin_filter: specification?.cabin_filter || ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.make.trim()) {
      newErrors.make = 'Make is required';
    }
    if (!formData.model.trim()) {
      newErrors.model = 'Model is required';
    }
    if (!formData.year_from || formData.year_from < 1950) {
      newErrors.year_from = 'Valid starting year is required';
    }
    if (!formData.year_to || formData.year_to < formData.year_from) {
      newErrors.year_to = 'Valid ending year is required';
    }
    if (!formData.engine_code.trim()) {
      newErrors.engine_code = 'Engine code is required';
    }
    if (!formData.engine_oil_capacity || parseFloat(formData.engine_oil_capacity) <= 0) {
      newErrors.engine_oil_capacity = 'Valid engine oil capacity is required';
    }
    if (!formData.coolant_capacity || parseFloat(formData.coolant_capacity) <= 0) {
      newErrors.coolant_capacity = 'Valid coolant capacity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data for submission
      const submitData = {
        make: formData.make.trim(),
        model: formData.model.trim(),
        series: formData.series?.trim() || null,
        year_from: parseInt(formData.year_from),
        year_to: parseInt(formData.year_to),
        engine_code: formData.engine_code.trim(),
        engine_displacement: formData.engine_displacement.trim() || null,
        fuel_type: formData.fuel_type,
        transmission_type: formData.transmission_type,
        transmission_code: formData.transmission_code.trim() || null,
        drive_type: formData.drive_type,
        service_interval_km: parseInt(formData.service_interval_km),
        oil_change_interval_km: parseInt(formData.oil_change_interval_km),
        notes: formData.notes.trim() || null,
        
        // Engine Oil
        engine_oil: {
          name: "Engine Oil",
          capacity_liters: parseFloat(formData.engine_oil_capacity),
          viscosity: formData.engine_oil_viscosity,
          type: formData.engine_oil_type,
          brand_recommendations: formData.engine_oil_brands ? formData.engine_oil_brands.split(',').map(b => b.trim()) : null,
          service_interval_km: parseInt(formData.engine_oil_interval),
          notes: formData.engine_oil_notes.trim() || null
        },
        
        // Coolant
        coolant: {
          name: "Coolant",
          capacity_liters: parseFloat(formData.coolant_capacity),
          color: formData.coolant_color,
          brand_recommendations: formData.coolant_brands ? formData.coolant_brands.split(',').map(b => b.trim()) : null,
          mixing_ratio: formData.coolant_mixing_ratio,
          temperature_rating: formData.coolant_temp_rating,
          service_interval_km: parseInt(formData.coolant_interval),
          notes: formData.coolant_notes.trim() || null
        },
        
        // Transmission Oil (optional)
        transmission_oil: formData.trans_oil_capacity ? {
          name: "Transmission Oil",
          capacity_liters: parseFloat(formData.trans_oil_capacity),
          viscosity: formData.trans_oil_viscosity,
          type: formData.trans_oil_type,
          brand_recommendations: formData.trans_oil_brands ? formData.trans_oil_brands.split(',').map(b => b.trim()) : null,
          service_interval_km: parseInt(formData.trans_oil_interval),
          notes: formData.trans_oil_notes.trim() || null
        } : null,
        
        // Transfer Case Oil (optional - 4WD)
        transfer_case_oil: formData.transfer_case_capacity ? {
          name: "Transfer Case Oil",
          capacity_liters: parseFloat(formData.transfer_case_capacity),
          viscosity: formData.transfer_case_viscosity?.trim() || null,
          brand_recommendations: formData.transfer_case_brands ? formData.transfer_case_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Front Differential Oil (optional)
        front_differential_oil: formData.front_diff_capacity ? {
          name: "Front Differential Oil",
          capacity_liters: parseFloat(formData.front_diff_capacity),
          viscosity: formData.front_diff_viscosity?.trim() || null,
          brand_recommendations: formData.front_diff_brands ? formData.front_diff_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Rear Differential Oil (optional)
        rear_differential_oil: formData.rear_diff_capacity ? {
          name: "Rear Differential Oil",
          capacity_liters: parseFloat(formData.rear_diff_capacity),
          viscosity: formData.rear_diff_viscosity?.trim() || null,
          brand_recommendations: formData.rear_diff_brands ? formData.rear_diff_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Brake Fluid (optional)
        brake_fluid: (formData.brake_fluid_capacity || formData.brake_fluid_grade) ? {
          name: "Brake Fluid",
          capacity_liters: parseFloat(formData.brake_fluid_capacity) || 0,
          type: formData.brake_fluid_grade?.trim() || null,
          brand_recommendations: formData.brake_fluid_brands ? formData.brake_fluid_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Power Steering Fluid (optional)
        power_steering_fluid: formData.ps_fluid_capacity ? {
          name: "Power Steering Fluid",
          capacity_liters: parseFloat(formData.ps_fluid_capacity),
          type: formData.ps_fluid_type?.trim() || null,
          brand_recommendations: formData.ps_fluid_brands ? formData.ps_fluid_brands.split(',').map(b => b.trim()) : null
        } : null,
        
        // Filter Part Numbers
        engine_oil_filter: formData.engine_oil_filter.trim() || null,
        air_filter: formData.air_filter.trim() || null,
        fuel_filter: formData.fuel_filter.trim() || null,
        cabin_filter: formData.cabin_filter.trim() || null
      };

      const response = await apiFetch(`${backendUrl}/api/vehicle-specs/${specification.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        onSuccess();
      } else {
        const errorData = await response.json();
        console.error('Failed to update vehicle specification:', errorData);
        alert('Failed to update vehicle specification. Please try again.');
      }
    } catch (error) {
      console.error('Error updating vehicle specification:', error);
      alert('An error occurred while updating the vehicle specification.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car size={18} />
            Basic Vehicle Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_make">Make *</Label>
              <Input
                id="edit_make"
                value={formData.make}
                onChange={(e) => handleInputChange('make', e.target.value)}
                className={errors.make ? 'border-red-500' : ''}
                placeholder="e.g., Maruti Suzuki"
              />
              {errors.make && (
                <p className="text-sm text-red-500 mt-1">{errors.make}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_model">Model *</Label>
              <Input
                id="edit_model"
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                className={errors.model ? 'border-red-500' : ''}
                placeholder="e.g., Swift Dzire"
              />
              {errors.model && (
                <p className="text-sm text-red-500 mt-1">{errors.model}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_series">Series</Label>
              <Input
                id="edit_series"
                value={formData.series}
                onChange={(e) => handleInputChange('series', e.target.value)}
                placeholder="e.g., VXi, ZDi+"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="edit_year_from">Year From *</Label>
              <Input
                id="edit_year_from"
                type="number"
                min="1950"
                max="2030"
                value={formData.year_from}
                onChange={(e) => handleInputChange('year_from', e.target.value)}
                className={errors.year_from ? 'border-red-500' : ''}
                placeholder="2020"
              />
              {errors.year_from && (
                <p className="text-sm text-red-500 mt-1">{errors.year_from}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_year_to">Year To *</Label>
              <Input
                id="edit_year_to"
                type="number"
                min="1950"
                max="2030"
                value={formData.year_to}
                onChange={(e) => handleInputChange('year_to', e.target.value)}
                className={errors.year_to ? 'border-red-500' : ''}
                placeholder="2024"
              />
              {errors.year_to && (
                <p className="text-sm text-red-500 mt-1">{errors.year_to}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_engine_code">Engine Code *</Label>
              <Input
                id="edit_engine_code"
                value={formData.engine_code}
                onChange={(e) => handleInputChange('engine_code', e.target.value)}
                className={errors.engine_code ? 'border-red-500' : ''}
                placeholder="e.g., K12M"
              />
              {errors.engine_code && (
                <p className="text-sm text-red-500 mt-1">{errors.engine_code}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_engine_displacement">Displacement</Label>
              <Input
                id="edit_engine_displacement"
                value={formData.engine_displacement}
                onChange={(e) => handleInputChange('engine_displacement', e.target.value)}
                placeholder="e.g., 1.2L"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="edit_fuel_type">Fuel Type *</Label>
              <Select value={formData.fuel_type} onValueChange={(value) => handleInputChange('fuel_type', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Petrol">Petrol</SelectItem>
                  <SelectItem value="Diesel">Diesel</SelectItem>
                  <SelectItem value="CNG">CNG</SelectItem>
                  <SelectItem value="Electric">Electric</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_transmission_type">Transmission *</Label>
              <Select value={formData.transmission_type} onValueChange={(value) => handleInputChange('transmission_type', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manual">Manual</SelectItem>
                  <SelectItem value="Automatic">Automatic</SelectItem>
                  <SelectItem value="CVT">CVT</SelectItem>
                  <SelectItem value="AMT">AMT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_transmission_code">Transmission Code</Label>
              <Input
                id="edit_transmission_code"
                value={formData.transmission_code}
                onChange={(e) => handleInputChange('transmission_code', e.target.value)}
                placeholder="e.g., AQ250, CD17"
              />
            </div>

            <div>
              <Label htmlFor="edit_drive_type">Drive Type *</Label>
              <Select value={formData.drive_type} onValueChange={(value) => handleInputChange('drive_type', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FWD">FWD</SelectItem>
                  <SelectItem value="RWD">RWD</SelectItem>
                  <SelectItem value="AWD">AWD</SelectItem>
                  <SelectItem value="4WD">4WD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Engine Oil Specifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive size={18} />
            Engine Oil Specifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_engine_oil_capacity">Capacity (Liters) *</Label>
              <Input
                id="edit_engine_oil_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.engine_oil_capacity}
                onChange={(e) => handleInputChange('engine_oil_capacity', e.target.value)}
                className={errors.engine_oil_capacity ? 'border-red-500' : ''}
                placeholder="4.2"
              />
              {errors.engine_oil_capacity && (
                <p className="text-sm text-red-500 mt-1">{errors.engine_oil_capacity}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_engine_oil_viscosity">Viscosity</Label>
              <Select value={formData.engine_oil_viscosity} onValueChange={(value) => handleInputChange('engine_oil_viscosity', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0W-20">0W-20</SelectItem>
                  <SelectItem value="5W-20">5W-20</SelectItem>
                  <SelectItem value="5W-30">5W-30</SelectItem>
                  <SelectItem value="5W-40">5W-40</SelectItem>
                  <SelectItem value="10W-30">10W-30</SelectItem>
                  <SelectItem value="10W-40">10W-40</SelectItem>
                  <SelectItem value="15W-40">15W-40</SelectItem>
                  <SelectItem value="20W-50">20W-50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_engine_oil_type">Oil Type</Label>
              <Select value={formData.engine_oil_type} onValueChange={(value) => handleInputChange('engine_oil_type', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mineral">Mineral</SelectItem>
                  <SelectItem value="Semi-Synthetic">Semi-Synthetic</SelectItem>
                  <SelectItem value="Fully Synthetic">Fully Synthetic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_engine_oil_brands">Brand Recommendations (comma separated)</Label>
              <Input
                id="edit_engine_oil_brands"
                value={formData.engine_oil_brands}
                onChange={(e) => handleInputChange('engine_oil_brands', e.target.value)}
                placeholder="Castrol GTX, Mobil Super, Shell Helix"
              />
            </div>

            <div>
              <Label htmlFor="edit_engine_oil_interval">Service Interval (km)</Label>
              <Input
                id="edit_engine_oil_interval"
                type="number"
                min="1000"
                value={formData.engine_oil_interval}
                onChange={(e) => handleInputChange('engine_oil_interval', e.target.value)}
                placeholder="10000"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="edit_engine_oil_notes">Notes</Label>
            <Textarea
              id="edit_engine_oil_notes"
              value={formData.engine_oil_notes}
              onChange={(e) => handleInputChange('engine_oil_notes', e.target.value)}
              placeholder="Additional notes about engine oil specifications"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Coolant Specifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap size={18} />
            Coolant Specifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_coolant_capacity">Capacity (Liters) *</Label>
              <Input
                id="edit_coolant_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.coolant_capacity}
                onChange={(e) => handleInputChange('coolant_capacity', e.target.value)}
                className={errors.coolant_capacity ? 'border-red-500' : ''}
                placeholder="6.2"
              />
              {errors.coolant_capacity && (
                <p className="text-sm text-red-500 mt-1">{errors.coolant_capacity}</p>
              )}
            </div>

            <div>
              <Label htmlFor="edit_coolant_color">Color</Label>
              <Select value={formData.coolant_color} onValueChange={(value) => handleInputChange('coolant_color', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Green">Green</SelectItem>
                  <SelectItem value="Blue">Blue</SelectItem>
                  <SelectItem value="Pink">Pink</SelectItem>
                  <SelectItem value="Orange">Orange</SelectItem>
                  <SelectItem value="Red">Red</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_coolant_mixing_ratio">Mixing Ratio</Label>
              <Select value={formData.coolant_mixing_ratio} onValueChange={(value) => handleInputChange('coolant_mixing_ratio', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50:50">50:50</SelectItem>
                  <SelectItem value="60:40">60:40</SelectItem>
                  <SelectItem value="70:30">70:30</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_coolant_brands">Brand Recommendations (comma separated)</Label>
              <Input
                id="edit_coolant_brands"
                value={formData.coolant_brands}
                onChange={(e) => handleInputChange('coolant_brands', e.target.value)}
                placeholder="Castrol Radicool, Shell Radiator Fluid"
              />
            </div>

            <div>
              <Label htmlFor="edit_coolant_interval">Service Interval (km)</Label>
              <Input
                id="edit_coolant_interval"
                type="number"
                min="1000"
                value={formData.coolant_interval}
                onChange={(e) => handleInputChange('coolant_interval', e.target.value)}
                placeholder="40000"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transmission Oil (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 size={18} />
            Transmission Oil (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_trans_oil_capacity">Capacity (Liters)</Label>
              <Input
                id="edit_trans_oil_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.trans_oil_capacity}
                onChange={(e) => handleInputChange('trans_oil_capacity', e.target.value)}
                placeholder="1.8"
              />
            </div>
            <div>
              <Label htmlFor="edit_trans_oil_viscosity">Viscosity</Label>
              <Input
                id="edit_trans_oil_viscosity"
                value={formData.trans_oil_viscosity}
                onChange={(e) => handleInputChange('trans_oil_viscosity', e.target.value)}
                placeholder="80W-90"
              />
            </div>
            <div>
              <Label htmlFor="edit_trans_oil_type">Oil Type</Label>
              <Input
                id="edit_trans_oil_type"
                value={formData.trans_oil_type}
                onChange={(e) => handleInputChange('trans_oil_type', e.target.value)}
                placeholder="Gear Oil"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="edit_trans_oil_brands">Brand Recommendations (comma separated)</Label>
            <Input
              id="edit_trans_oil_brands"
              value={formData.trans_oil_brands}
              onChange={(e) => handleInputChange('trans_oil_brands', e.target.value)}
              placeholder="Castrol GL-4, Shell Spirax"
            />
          </div>
        </CardContent>
      </Card>

      {/* Transfer Case Oil (Optional - 4WD) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench size={18} />
            Transfer Case Oil (Optional - 4WD)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_transfer_case_capacity">Capacity (Liters)</Label>
              <Input
                id="edit_transfer_case_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.transfer_case_capacity}
                onChange={(e) => handleInputChange('transfer_case_capacity', e.target.value)}
                placeholder="1.0"
              />
            </div>
            <div>
              <Label htmlFor="edit_transfer_case_viscosity">Oil Grade</Label>
              <Input
                id="edit_transfer_case_viscosity"
                value={formData.transfer_case_viscosity}
                onChange={(e) => handleInputChange('transfer_case_viscosity', e.target.value)}
                placeholder="75W-85"
              />
            </div>
            <div>
              <Label htmlFor="edit_transfer_case_brands">Brand Recommendations</Label>
              <Input
                id="edit_transfer_case_brands"
                value={formData.transfer_case_brands}
                onChange={(e) => handleInputChange('transfer_case_brands', e.target.value)}
                placeholder="Castrol Syntrax, Toyota Genuine"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Differential Oil (Optional - 4WD/RWD/AWD) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge size={18} />
            Differential Oil (Optional - 4WD/RWD/AWD)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_front_diff_capacity">Front Diff Capacity (Liters)</Label>
              <Input
                id="edit_front_diff_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.front_diff_capacity}
                onChange={(e) => handleInputChange('front_diff_capacity', e.target.value)}
                placeholder="1.2"
              />
            </div>
            <div>
              <Label htmlFor="edit_front_diff_viscosity">Front Diff Oil Grade</Label>
              <Input
                id="edit_front_diff_viscosity"
                value={formData.front_diff_viscosity}
                onChange={(e) => handleInputChange('front_diff_viscosity', e.target.value)}
                placeholder="80W-90"
              />
            </div>
            <div>
              <Label htmlFor="edit_front_diff_brands">Front Diff Brand Recommendations</Label>
              <Input
                id="edit_front_diff_brands"
                value={formData.front_diff_brands}
                onChange={(e) => handleInputChange('front_diff_brands', e.target.value)}
                placeholder="Castrol EPX, Shell Spirax"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_rear_diff_capacity">Rear Diff Capacity (Liters)</Label>
              <Input
                id="edit_rear_diff_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.rear_diff_capacity}
                onChange={(e) => handleInputChange('rear_diff_capacity', e.target.value)}
                placeholder="1.5"
              />
            </div>
            <div>
              <Label htmlFor="edit_rear_diff_viscosity">Rear Diff Oil Grade</Label>
              <Input
                id="edit_rear_diff_viscosity"
                value={formData.rear_diff_viscosity}
                onChange={(e) => handleInputChange('rear_diff_viscosity', e.target.value)}
                placeholder="85W-140"
              />
            </div>
            <div>
              <Label htmlFor="edit_rear_diff_brands">Rear Diff Brand Recommendations</Label>
              <Input
                id="edit_rear_diff_brands"
                value={formData.rear_diff_brands}
                onChange={(e) => handleInputChange('rear_diff_brands', e.target.value)}
                placeholder="Castrol EPX, Shell Spirax"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brake Fluid (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle size={18} />
            Brake Fluid (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_brake_fluid_capacity">Capacity (Liters)</Label>
              <Input
                id="edit_brake_fluid_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.brake_fluid_capacity}
                onChange={(e) => handleInputChange('brake_fluid_capacity', e.target.value)}
                placeholder="0.5"
              />
            </div>
            <div>
              <Label htmlFor="edit_brake_fluid_grade">Grade (DOT Rating)</Label>
              <Select value={formData.brake_fluid_grade} onValueChange={(v) => handleInputChange('brake_fluid_grade', v)}>
                <SelectTrigger id="edit_brake_fluid_grade">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DOT3">DOT3</SelectItem>
                  <SelectItem value="DOT4">DOT4</SelectItem>
                  <SelectItem value="DOT5.1">DOT5.1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_brake_fluid_brands">Brand Recommendations</Label>
              <Input
                id="edit_brake_fluid_brands"
                value={formData.brake_fluid_brands}
                onChange={(e) => handleInputChange('brake_fluid_brands', e.target.value)}
                placeholder="Castrol DOT4, Bosch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Power Steering Fluid (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 size={18} />
            Power Steering Fluid (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit_ps_fluid_capacity">Capacity (Liters)</Label>
              <Input
                id="edit_ps_fluid_capacity"
                type="number"
                step="0.1"
                min="0"
                value={formData.ps_fluid_capacity}
                onChange={(e) => handleInputChange('ps_fluid_capacity', e.target.value)}
                placeholder="1.0"
              />
            </div>
            <div>
              <Label htmlFor="edit_ps_fluid_type">Fluid Type</Label>
              <Input
                id="edit_ps_fluid_type"
                value={formData.ps_fluid_type}
                onChange={(e) => handleInputChange('ps_fluid_type', e.target.value)}
                placeholder="ATF / PSF"
              />
            </div>
            <div>
              <Label htmlFor="edit_ps_fluid_brands">Brand Recommendations</Label>
              <Input
                id="edit_ps_fluid_brands"
                value={formData.ps_fluid_brands}
                onChange={(e) => handleInputChange('ps_fluid_brands', e.target.value)}
                placeholder="Castrol PSF, Dexron III"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter Part Numbers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package size={18} />
            Filter Part Numbers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_engine_oil_filter">Engine Oil Filter</Label>
              <Input
                id="edit_engine_oil_filter"
                value={formData.engine_oil_filter}
                onChange={(e) => handleInputChange('engine_oil_filter', e.target.value)}
                placeholder="e.g., 26300-2E100"
              />
            </div>
            <div>
              <Label htmlFor="edit_air_filter">Air Filter</Label>
              <Input
                id="edit_air_filter"
                value={formData.air_filter}
                onChange={(e) => handleInputChange('air_filter', e.target.value)}
                placeholder="e.g., 28113-S1000"
              />
            </div>
            <div>
              <Label htmlFor="edit_fuel_filter">Fuel Filter</Label>
              <Input
                id="edit_fuel_filter"
                value={formData.fuel_filter}
                onChange={(e) => handleInputChange('fuel_filter', e.target.value)}
                placeholder="e.g., 31112-2E000"
              />
            </div>
            <div>
              <Label htmlFor="edit_cabin_filter">Cabin Filter</Label>
              <Input
                id="edit_cabin_filter"
                value={formData.cabin_filter}
                onChange={(e) => handleInputChange('cabin_filter', e.target.value)}
                placeholder="e.g., 97133-S1000"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button 
          type="submit" 
          disabled={loading}
          className="flex-1"
        >
          {loading ? 'Updating...' : 'Update Vehicle Specification'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

function App() {
  // Initialize auto-uppercase functionality when component mounts
  useEffect(() => {
    initializeAutoUppercase();
    
    // Also initialize for dynamically added inputs
    const observer = new MutationObserver(() => {
      initializeAutoUppercase();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return () => observer.disconnect();
  }, []);

  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AppContent />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

// Salary Section Component
const SalarySection = ({ onUpdate }) => {
  const [employees, setEmployees] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(toLocalISODate(new Date()).slice(0, 7)); // YYYY-MM format
  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentEditMode, setPaymentEditMode] = useState(false);
  const [paymentEditRecordId, setPaymentEditRecordId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'bank_account', // Match expense form pattern
    bank_account_id: '',
    notes: ''
  });
  
  // API-based statistics
  const [salaryStats, setSalaryStats] = useState({
    total_employees: 0,
    paid_salaries: 0,
    pending_salaries: 0,
    total_expense: 0
  });
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadEmployeesAndSalaries();
    loadBankAccounts();
    // Note: Cash balance loading removed for now - can be added later if needed
  }, [selectedMonth]);

  // Add effect to reload when onUpdate is called (when employees are added/deleted)
  useEffect(() => {
    if (onUpdate) {
      loadEmployeesAndSalaries();
    }
  }, [onUpdate]);

  const loadEmployeesAndSalaries = async () => {
    setLoading(true);
    try {
      // Load employees from API
      const employeesResponse = await axios.get(`${backendUrl}/api/employees`);
      const employeesData = employeesResponse.data || [];
      setEmployees(employeesData);

      // Try to load existing salary records for the month
      try {
        const salaryResponse = await axios.get(`${backendUrl}/api/salary/records/month/${selectedMonth}`);
        let existingSalaryRecords = salaryResponse.data || [];
        
        // Filter out salary records for deleted employees
        const activeEmployeeIds = employeesData.map(emp => emp.id);
        existingSalaryRecords = existingSalaryRecords.filter(record => 
          activeEmployeeIds.includes(record.employee_id)
        );
        
        if (existingSalaryRecords.length > 0) {
          // Check for new employees that don't have salary records yet
          const existingRecordEmployeeIds = existingSalaryRecords.map(record => record.employee_id);
          const newEmployees = employeesData.filter(emp => 
            !existingRecordEmployeeIds.includes(emp.id)
          );
          
          if (newEmployees.length > 0) {
            console.log(`Found ${newEmployees.length} new employees, creating salary records...`);
            const newSalaryRecords = await generateSalaryRecordsForEmployees(newEmployees);
            setSalaryRecords([...existingSalaryRecords, ...newSalaryRecords]);
          } else {
            setSalaryRecords(existingSalaryRecords);
          }
        } else {
          // Generate new records for all employees
          await generateSalaryRecordsForMonth(employeesData);
        }
      } catch (error) {
        console.log('No existing salary records, generating new ones');
        await generateSalaryRecordsForMonth(employeesData);
      }

    } catch (error) {
      console.error('Error loading salary data:', error);
      toast.error('Failed to load salary data');
    } finally {
      setLoading(false);
    }
    
    // Load salary statistics for the selected month
    try {
      const statsResponse = await axios.get(`${backendUrl}/api/salary/stats?month=${selectedMonth}`);
      setSalaryStats(statsResponse.data);
    } catch (error) {
      console.error('Error loading salary statistics:', error);
      // Use fallback calculation if API fails
      const fallbackStats = {
        total_employees: salaryRecords.length,
        paid_salaries: salaryRecords.filter(r => r.status === 'paid').length,
        pending_salaries: salaryRecords.filter(r => r.status === 'pending').length,
        total_expense: salaryRecords.reduce((sum, r) => sum + (r.net_salary || 0), 0)
      };
      setSalaryStats(fallbackStats);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/bank-accounts`);
      setBankAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  };

  // Function to clean up salary records for deleted employees
  const cleanupOrphanedRecords = async () => {
    const activeEmployeeIds = employees.map(emp => emp.id);
    const orphanedRecords = salaryRecords.filter(record => 
      !activeEmployeeIds.includes(record.employee_id)
    );
    
    if (orphanedRecords.length === 0) {
      toast.info('No orphaned salary records found');
      return;
    }
    
    if (window.confirm(`Remove ${orphanedRecords.length} salary record(s) for deleted employees?`)) {
      try {
        for (const record of orphanedRecords) {
          await axios.delete(`${backendUrl}/api/salary/records/${record.id}`);
        }
        
        // Update local state
        setSalaryRecords(prev => prev.filter(record => 
          activeEmployeeIds.includes(record.employee_id)
        ));
        
        toast.success(`Removed ${orphanedRecords.length} orphaned salary record(s)`);
      } catch (error) {
        console.error('Error cleaning up orphaned records:', error);
        toast.error('Failed to clean up orphaned records');
      }
    }
  };
  
  // Function to clean up duplicate salary records
  const cleanupDuplicateRecords = async () => {
    setIsCleaningUp(true);
    try {
      const response = await axios.post(`${backendUrl}/api/salary/cleanup-duplicates`);
      toast.success(`Cleanup completed! Removed ${response.data.duplicates_removed} duplicate records`);
      loadEmployeesAndSalaries(); // Reload data to reflect changes
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      toast.error('Failed to cleanup duplicate records');
    } finally {
      setIsCleaningUp(false);
    }
  };

  const generateSalaryRecordsForEmployees = async (employeesToProcess) => {
    // Generate salary records for specific employees only
    const generatedRecords = [];
    
    for (const employee of employeesToProcess) {
      const workingDays = 26;
      const baseSalary = employee.salary || 25000;
      const overtimeRate = baseSalary / 26 / 8 * 1.5; // 1.5x hourly rate
      
      const recordData = {
        employee_id: employee.id,
        month: selectedMonth,
        working_days: workingDays,
        days_worked: workingDays,
        base_salary: baseSalary,
        overtime_hours: 0,
        overtime_rate: overtimeRate,
        bonus: 0,
        deductions: 0,
        notes: ''
      };
      
      try {
        const response = await axios.post(`${backendUrl}/api/salary/records`, recordData);
        generatedRecords.push(response.data);
        console.log(`✅ Created salary record for ${employee.name}`);
      } catch (error) {
        if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
          console.log(`ℹ️ Salary record already exists for ${employee.name} in ${selectedMonth}`);
          // This is expected behavior, not an error
        } else {
          console.error(`❌ Error creating salary record for ${employee.name}:`, error.response?.data?.detail || error.message);
        }
      }
    }
    
    return generatedRecords;
  };

  const generateSalaryRecordsForMonth = async (employeesData) => {
    // Generate salary records for employees who don't have records for this month
    const generatedRecords = [];
    
    for (const employee of employeesData) {
      const workingDays = 26;
      const baseSalary = employee.salary || 25000;
      const overtimeRate = baseSalary / 26 / 8 * 1.5; // 1.5x hourly rate
      
      const recordData = {
        employee_id: employee.id,
        month: selectedMonth,
        working_days: workingDays,
        days_worked: workingDays,
        base_salary: baseSalary,
        overtime_hours: 0,
        overtime_rate: overtimeRate,
        bonus: 0,
        deductions: 0,
        notes: ''
      };
      
      try {
        const response = await axios.post(`${backendUrl}/api/salary/records`, recordData);
        generatedRecords.push(response.data);
        console.log(`✅ Created salary record for ${employee.name}`);
      } catch (error) {
        if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
          console.log(`ℹ️ Salary record already exists for ${employee.name} in ${selectedMonth}`);
          // This is expected behavior, not an error - record already exists, don't add duplicates
        } else {
          console.error(`❌ Error creating salary record for ${employee.name}:`, error.response?.data?.detail || error.message);
        }
      }
    }
    
    setSalaryRecords(generatedRecords);
  };

  const calculateNetSalary = (record) => {
    const overtimePay = record.overtime_hours * record.overtime_rate;
    const grossSalary = record.base_salary + overtimePay + record.bonus;
    const netSalary = grossSalary - record.deductions;
    return { grossSalary, netSalary };
  };

  const handleSalaryUpdate = async (recordId, field, value) => {
    try {
      const numericValue = parseFloat(value) || 0;
      const updateData = { [field]: numericValue };
      
      // Show loading state (optional - can be added later)
      await axios.put(`${backendUrl}/api/salary/records/${recordId}`, updateData);
      
      // Update local state
      setSalaryRecords(prev => prev.map(record => {
        if (record.id === recordId) {
          const updated = { ...record, [field]: numericValue };
          const { grossSalary, netSalary } = calculateNetSalary(updated);
          return { ...updated, gross_salary: grossSalary, net_salary: netSalary };
        }
        return record;
      }));
      
      // If base salary is updated, also update the employee's main record
      if (field === 'base_salary') {
        const record = salaryRecords.find(r => r.id === recordId);
        if (record && record.employee_id) {
          try {
            await axios.put(`${backendUrl}/api/employees/${record.employee_id}`, {
              salary: numericValue
            });
            
            // Update employees state to reflect the change
            setEmployees(prev => prev.map(emp => 
              emp.id === record.employee_id ? { ...emp, salary: numericValue } : emp
            ));
            
            toast.success(`Employee base salary updated to ₹${numericValue.toLocaleString()}`);
          } catch (empError) {
            console.error('Error updating employee record:', empError);
            toast.warning('Salary record updated but employee record update failed');
          }
        }
      }
      
    } catch (error) {
      console.error('Error updating salary:', error);
      toast.error(`Failed to update ${field.replace('_', ' ')}`);
    }
  };

  const handleProcessSalary = (employee) => {
    setSelectedEmployee(employee);
    const record = salaryRecords.find(r => r.employee_id === employee.id);
    setPaymentEditMode(false);
    setPaymentEditRecordId(null);
    setPaymentData({
      amount: record?.net_salary?.toString() || '0',
      payment_method: 'bank_account', // Default to bank account like expense form
      bank_account_id: '',
      notes: ''
    });
    setShowPaymentDialog(true);
  };

  // Edit / correct a previously-paid salary (wrong payment method, amount, etc.)
  const handleEditPayment = (record, employee) => {
    setSelectedEmployee(employee || { id: record.employee_id, name: record.employee_name });
    setPaymentEditMode(true);
    setPaymentEditRecordId(record.id);
    // Pre-fill with current values (so user only edits what's wrong)
    const currentMethod = record.payment_method === 'bank_transfer' ? 'bank_account' : (record.payment_method || 'bank_account');
    setPaymentData({
      amount: record.net_salary?.toString() || '0',
      payment_method: currentMethod,
      bank_account_id: '',
      notes: record.notes || ''
    });
    setShowPaymentDialog(true);
  };

  // Revert a paid salary back to 'pending' (full undo: refunds cash/bank, deletes accounting txn)
  const handleRevertPayment = async () => {
    if (!paymentEditRecordId) {
      toast.error('No salary record selected');
      return;
    }
    const confirmed = window.confirm(
      `Revert this salary back to "pending"?\n\nThis will:\n• Refund the cash/bank balance\n• Delete the accounting transaction\n• Set the record back to unpaid status\n\nAre you sure?`
    );
    if (!confirmed) return;

    try {
      const response = await axios.post(`${backendUrl}/api/salary/records/${paymentEditRecordId}/revert-payment`);
      const msg = response.data?.message || 'Salary payment reverted to pending';
      toast.success(msg);

      // Close dialog & refresh
      setShowPaymentDialog(false);
      setSelectedEmployee(null);
      setPaymentEditMode(false);
      setPaymentEditRecordId(null);
      setPaymentData({ amount: '', payment_method: 'bank_account', bank_account_id: '', notes: '' });
      loadEmployeesAndSalaries();
    } catch (error) {
      console.error('Revert payment error:', error);
      const detail = error.response?.data?.detail || error.message || 'Failed to revert salary payment';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const confirmPayment = async () => {
    if (!selectedEmployee) return;
    
    console.log('Starting payment confirmation...');
    
    // Validate payment data using same pattern as expense form
    if (paymentData.payment_method === 'bank_account' && !paymentData.bank_account_id) {
      toast.error('Please select a bank account for bank transfer');
      return;
    }
    
    // Additional validation for bank account balance
    if (paymentData.payment_method === 'bank_account' && paymentData.bank_account_id) {
      const selectedAccount = bankAccounts.find(acc => acc.id === paymentData.bank_account_id);
      if (selectedAccount && selectedAccount.current_balance < parseFloat(paymentData.amount)) {
        toast.error(`Insufficient balance in selected account. Available: ₹${selectedAccount.current_balance.toLocaleString()}, Required: ₹${parseFloat(paymentData.amount).toLocaleString()}`);
        return;
      }
    }
    
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    // Force close dialog function
    const closeDialogAndReset = () => {
      console.log('Closing dialog and resetting state...');
      setShowPaymentDialog(false);
      setSelectedEmployee(null);
      setPaymentEditMode(false);
      setPaymentEditRecordId(null);
      setPaymentData({ amount: '', payment_method: 'bank_account', bank_account_id: '', notes: '' });
      loadEmployeesAndSalaries(); // Always refresh data
    };
    
    try {
      // In edit mode, we already know the record id; otherwise look up by selected employee
      const record = paymentEditMode
        ? salaryRecords.find(r => r.id === paymentEditRecordId)
        : salaryRecords.find(r => r.employee_id === selectedEmployee.id);
      if (!record) {
        toast.error('Salary record not found');
        closeDialogAndReset();
        return;
      }

      // Show loading state
      const paymentMethodText = paymentData.payment_method === 'bank_account' ? 'bank transfer' : 'cash';
      const verb = paymentEditMode ? 'Correcting' : 'Processing';
      toast.info(`${verb} ${paymentMethodText} payment for ${selectedEmployee.name}...`);

      // Map payment method to backend expected format
      const paymentMethodMap = {
        'bank_account': 'bank_transfer',
        'cash': 'cash'
      };

      const requestData = {
        amount: parseFloat(paymentData.amount),
        payment_method: paymentMethodMap[paymentData.payment_method],
        payment_date: toLocalISODate(new Date()),
        notes: paymentData.notes || ''
      };

      // Add bank account ID for bank transfers
      if (paymentData.payment_method === 'bank_account') {
        requestData.bank_account_id = paymentData.bank_account_id;
      }

      const endpoint = paymentEditMode
        ? `${backendUrl}/api/salary/records/${record.id}/correct-payment`
        : `${backendUrl}/api/salary/records/${record.id}/pay`;

      console.log(`Sending ${paymentEditMode ? 'correction' : 'payment'} request:`, requestData);
      const response = await axios.post(endpoint, requestData);
      console.log('Payment response received:', response.status);

      // Show success message
      const fallbackMsg = paymentEditMode
        ? `Salary payment corrected to ₹${parseFloat(paymentData.amount).toLocaleString()} via ${paymentMethodText} for ${selectedEmployee.name}`
        : `Salary payment of ₹${parseFloat(paymentData.amount).toLocaleString()} processed successfully via ${paymentMethodText} for ${selectedEmployee.name}`;
      const successMessage = response.data?.message || fallbackMsg;
      toast.success(successMessage);

      // Always close dialog on any response (success)
      closeDialogAndReset();
      
    } catch (error) {
      console.error('Payment error caught:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to process payment';
      let shouldCloseDialog = false;
      
      if (error.response?.status === 400) {
        const detail = error.response.data?.detail || '';
        console.log('400 error detail:', detail);
        
        if (detail.includes('Salary already paid')) {
          errorMessage = 'This salary has already been paid';
          shouldCloseDialog = true;
        } else if (detail.includes('Insufficient bank balance') || detail.includes('Insufficient')) {
          errorMessage = 'Insufficient bank account balance. Please choose a different account or add funds.';
        } else if (detail.includes('Bank account ID required')) {
          errorMessage = 'Please select a bank account for bank transfer payments';
        } else {
          errorMessage = detail || 'Invalid payment request';
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Bank account not found or inactive';
        shouldCloseDialog = true; // Close on 404 errors too
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error during payment processing. The payment may have been processed successfully.';
        shouldCloseDialog = true; // Close and let user refresh to check
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection and refresh to verify payment status.';
        shouldCloseDialog = true;
      } else {
        errorMessage = error.response?.data?.detail || error.message || 'Failed to process payment';
        shouldCloseDialog = true; // Close on unknown errors too
      }
      
      toast.error(errorMessage);
      
      // Close dialog for most errors since they indicate completion or unrecoverable states
      if (shouldCloseDialog) {
        console.log('Closing dialog due to error handling logic');
        closeDialogAndReset();
      }
    }
  };

  const totalSalaryExpense = salaryRecords.reduce((sum, record) => sum + (record.net_salary || 0), 0);
  const paidSalaries = salaryRecords.filter(record => record.status === 'paid');
  const pendingSalaries = salaryRecords.filter(record => record.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading salary data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Salary Management</h1>
          <p className="text-gray-600">Manage monthly salaries, overtime, and payments</p>
        </div>
        <div className="flex space-x-2">
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-40"
          />
          <Button 
            variant="outline"
            onClick={loadEmployeesAndSalaries}
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="outline"
            onClick={cleanupOrphanedRecords}
            className="flex items-center text-red-600 border-red-300 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Cleanup
          </Button>
          <Button 
            variant="outline"
            onClick={cleanupDuplicateRecords}
            disabled={isCleaningUp}
            className="flex items-center text-orange-600 border-orange-300 hover:bg-orange-50"
          >
            {isCleaningUp ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            Fix Duplicates
          </Button>
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setShowProcessDialog(true)}
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Process All
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 opacity-80" />
              <div className="ml-4">
                <p className="text-sm opacity-90">Total Employees</p>
                <p className="text-2xl font-bold">{salaryStats.total_employees}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 opacity-80" />
              <div className="ml-4">
                <p className="text-sm opacity-90">Paid Salaries</p>
                <p className="text-2xl font-bold">{salaryStats.paid_salaries}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 opacity-80" />
              <div className="ml-4">
                <p className="text-sm opacity-90">Pending Payments</p>
                <p className="text-2xl font-bold">{salaryStats.pending_salaries}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Calculator className="h-8 w-8 opacity-80" />
              <div className="ml-4">
                <p className="text-sm opacity-90">Total Expense</p>
                <p className="text-2xl font-bold">₹{salaryStats.total_expense.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Salary Records Table */}
      <Card>
        <CardHeader>
          <CardTitle>Salary Records - {new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold">Employee</th>
                  <th className="text-right p-3 font-semibold">Base Salary</th>
                  <th className="text-right p-3 font-semibold">OT Hours</th>
                  <th className="text-right p-3 font-semibold">Bonus</th>
                  <th className="text-right p-3 font-semibold">Deductions</th>
                  <th className="text-right p-3 font-semibold">Net Salary</th>
                  <th className="text-center p-3 font-semibold">Status</th>
                  <th className="text-center p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {salaryRecords.map((record) => {
                  const employee = employees.find(emp => emp.id === record.employee_id);
                  
                  // Skip records for employees that no longer exist
                  if (!employee && !record.employee_name) {
                    return null;
                  }
                  
                  const employeeName = record.employee_name || employee?.name || employee?.full_name || 'Unknown Employee';
                  const employeePosition = record.position || employee?.position || 'Unknown Position';
                  
                  // Add visual indicator for employees that have been deleted
                  const isEmployeeDeleted = !employee;
                  
                  return (
                  <tr key={record.id} className={`border-b hover:bg-gray-50 ${isEmployeeDeleted ? 'bg-red-50' : ''}`}>
                    <td className="p-3">
                      <div className="flex items-center">
                        <div>
                          <p className={`font-medium ${isEmployeeDeleted ? 'text-red-600 line-through' : ''}`}>
                            {employeeName}
                            {isEmployeeDeleted && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Deleted</span>}
                          </p>
                          <p className="text-sm text-gray-600">{employeePosition}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-right p-3">
                      <Input
                        type="number"
                        value={record.base_salary}
                        onChange={(e) => handleSalaryUpdate(record.id, 'base_salary', e.target.value)}
                        className="w-28 text-right font-medium"
                        min="0"
                        step="1000"
                        placeholder="Base Salary"
                      />
                    </td>
                    <td className="text-right p-3">
                      <Input
                        type="number"
                        value={record.overtime_hours}
                        onChange={(e) => handleSalaryUpdate(record.id, 'overtime_hours', e.target.value)}
                        className="w-20 text-right"
                        min="0"
                        step="0.5"
                      />
                    </td>
                    <td className="text-right p-3">
                      <Input
                        type="number"
                        value={record.bonus}
                        onChange={(e) => handleSalaryUpdate(record.id, 'bonus', e.target.value)}
                        className="w-24 text-right"
                        min="0"
                      />
                    </td>
                    <td className="text-right p-3">
                      <Input
                        type="number"
                        value={record.deductions}
                        onChange={(e) => handleSalaryUpdate(record.id, 'deductions', e.target.value)}
                        className="w-24 text-right"
                        min="0"
                      />
                    </td>
                    <td className="text-right p-3 font-semibold">₹{record.net_salary.toLocaleString()}</td>
                    <td className="text-center p-3">
                      <Badge className={
                        record.status === 'paid' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }>
                        {record.status}
                      </Badge>
                    </td>
                    <td className="text-center p-3">
                      {record.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleProcessSalary(employee || { id: record.employee_id, name: employeeName })}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Pay
                        </Button>
                      )}
                      {record.status === 'paid' && (
                        <div className="flex flex-col items-center gap-1">
                          <div className="text-xs text-gray-500">
                            Paid on {record.payment_date}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditPayment(record, employee || { id: record.employee_id, name: employeeName })}
                            className="h-7 px-2 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                            data-testid={`edit-salary-payment-${record.id}`}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit Payment
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {paymentEditMode ? '✏️ Edit / Correct Salary Payment' : 'Process Salary Payment'}
            </DialogTitle>
            <DialogDescription>
              {paymentEditMode
                ? `Correct the payment for ${selectedEmployee?.name || 'Selected Employee'}. The previous payment will be reversed and the new method/amount applied.`
                : `Process salary payment for ${selectedEmployee?.name || selectedEmployee?.full_name || 'Selected Employee'}`}
            </DialogDescription>
          </DialogHeader>
          {paymentEditMode && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
              ⚠️ This will reverse the previous payment from the accounting books (refund the old cash/bank balance) and re-apply the corrected amount/method.
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label htmlFor="amount">Net Salary Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData(prev => ({...prev, amount: e.target.value}))}
                className="text-right"
                readOnly={!paymentEditMode}
                data-testid="salary-payment-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select value={paymentData.payment_method} onValueChange={(value) => setPaymentData(prev => ({...prev, payment_method: value}))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_account">Bank Account</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {paymentData.payment_method === 'bank_account' && (
              <div>
                <Label htmlFor="bank_account_id">Bank Account</Label>
                <Select 
                  value={paymentData.bank_account_id} 
                  onValueChange={(value) => setPaymentData(prev => ({...prev, bank_account_id: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex flex-col">
                          <span>{account.bank_name} - **** {account.account_number.slice(-4)}</span>
                          <span className="text-xs text-gray-500">Balance: ₹{account.current_balance?.toLocaleString()}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Payment Method Details - Match expense form pattern */}
            {paymentData.payment_method === 'cash' && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Cash Payment:</span>
                  <span className="font-semibold text-blue-600">Amount will be deducted from garage cash</span>
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about this payment..."
                value={paymentData.notes}
                onChange={(e) => setPaymentData(prev => ({...prev, notes: e.target.value}))}
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-6">
            <Button 
              variant="outline" 
              onClick={() => {
                console.log('Manual dialog close clicked');
                setShowPaymentDialog(false);
                setSelectedEmployee(null);
                setPaymentData({ amount: '', payment_method: 'bank_account', bank_account_id: '', notes: '' });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmPayment}
              className="bg-green-600 hover:bg-green-700"
            >
              {paymentEditMode ? 'Save Corrected Payment' : 'Confirm Payment'}
            </Button>
          </div>

          {paymentEditMode && (
            <div className="border-t mt-4 pt-3">
              <p className="text-xs text-gray-600 mb-2">
                ❓ Was this salary paid by mistake? Revert it back to "pending" — the cash/bank balance will be refunded.
              </p>
              <Button
                variant="outline"
                onClick={handleRevertPayment}
                className="w-full text-red-700 border-red-300 hover:bg-red-50"
                data-testid="salary-revert-payment-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Revert to Pending (Undo Payment)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Add Supplier Form Component ============
const AddSupplierForm = ({ onSubmit, onCancel }) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm();
  const [categoryValue, setCategoryValue] = useState('');
  const [ratingValue, setRatingValue] = useState('');

  const handleFormSubmit = (data) => {
    // Convert numeric fields
    const formattedData = {
      ...data,
      category: categoryValue || 'other',
      credit_limit: data.credit_limit ? parseFloat(data.credit_limit) : null,
      rating: ratingValue ? parseInt(ratingValue) : null,
    };
    onSubmit(formattedData);
    reset();
    setCategoryValue('');
    setRatingValue('');
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Supplier Name *</Label>
          <Input
            id="name"
            {...register("name", { required: "Supplier name is required" })}
            placeholder="Enter supplier name"
          />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">
              {typeof errors.name.message === 'string' ? errors.name.message : 'This field is required'}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="contact_person">Contact Person</Label>
          <Input
            id="contact_person"
            {...register("contact_person")}
            placeholder="Enter contact person name"
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            {...register("phone")}
            placeholder="Enter phone number"
          />
        </div>
        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            placeholder="Enter email address"
          />
        </div>
      </div>

      {/* Business Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="category">Category</Label>
          <Select value={categoryValue} onValueChange={setCategoryValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="parts">Parts</SelectItem>
              <SelectItem value="tools">Tools</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
              <SelectItem value="lubricants">Lubricants</SelectItem>
              <SelectItem value="electrical">Electrical</SelectItem>
              <SelectItem value="bodywork">Bodywork</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="gst_number">GST Number</Label>
          <Input
            id="gst_number"
            {...register("gst_number")}
            placeholder="Enter GST number"
          />
        </div>
        <div>
          <Label htmlFor="pan_number">PAN Number</Label>
          <Input
            id="pan_number"
            {...register("pan_number")}
            placeholder="Enter PAN number"
          />
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="address_line1">Address Line 1</Label>
          <Input
            id="address_line1"
            {...register("address_line1")}
            placeholder="Enter address line 1"
          />
        </div>
        <div>
          <Label htmlFor="address_line2">Address Line 2</Label>
          <Input
            id="address_line2"
            {...register("address_line2")}
            placeholder="Enter address line 2 (optional)"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              {...register("city")}
              placeholder="Enter city"
            />
          </div>
          <div>
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              {...register("state")}
              placeholder="Enter state"
            />
          </div>
          <div>
            <Label htmlFor="state_code">State Code (GST)</Label>
            <Input
              id="state_code"
              {...register("state_code")}
              placeholder="e.g. 32"
              maxLength={2}
              data-testid="supplier-state-code-input"
            />
          </div>
          <div>
            <Label htmlFor="postal_code">Postal Code</Label>
            <Input
              id="postal_code"
              {...register("postal_code")}
              placeholder="Enter postal code"
            />
          </div>
          <div>
            <Label htmlFor="country">Country</Label>
            <Input
              id="country"
              {...register("country")}
              placeholder="Enter country"
              defaultValue="India"
            />
          </div>
        </div>
      </div>

      {/* Financial Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="payment_terms">Payment Terms</Label>
          <Input
            id="payment_terms"
            {...register("payment_terms")}
            placeholder="e.g., Net 30, COD, etc."
          />
        </div>
        <div>
          <Label htmlFor="credit_limit">Credit Limit (₹)</Label>
          <Input
            id="credit_limit"
            type="number"
            step="0.01"
            {...register("credit_limit")}
            placeholder="Enter credit limit"
          />
        </div>
        <div>
          <Label htmlFor="rating">Rating (1-5)</Label>
          <Select value={ratingValue} onValueChange={setRatingValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 Star</SelectItem>
              <SelectItem value="2">2 Stars</SelectItem>
              <SelectItem value="3">3 Stars</SelectItem>
              <SelectItem value="4">4 Stars</SelectItem>
              <SelectItem value="5">5 Stars</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Additional Information */}
      <div>
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          type="url"
          {...register("website")}
          placeholder="Enter website URL"
        />
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Enter any additional notes about this supplier"
          rows={3}
        />
      </div>

      {/* Submit Buttons */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
          Add Supplier
        </Button>
      </div>
    </form>
  );
};

// ============ Edit Supplier Form Component ============
const EditSupplierForm = ({ supplier, onSubmit, onCancel }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      name: supplier.name || '',
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      website: supplier.website || '',
      address_line1: supplier.address_line1 || '',
      address_line2: supplier.address_line2 || '',
      city: supplier.city || '',
      state: supplier.state || '',
      state_code: supplier.state_code || '',
      postal_code: supplier.postal_code || '',
      country: supplier.country || '',
      gst_number: supplier.gst_number || '',
      pan_number: supplier.pan_number || '',
      payment_terms: supplier.payment_terms || '',
      credit_limit: supplier.credit_limit || '',
      notes: supplier.notes || '',
    }
  });

  const [categoryValue, setCategoryValue] = useState(supplier.category || '');
  const [ratingValue, setRatingValue] = useState(supplier.rating?.toString() || '');

  const handleFormSubmit = (data) => {
    // Convert numeric fields and remove empty strings
    const formattedData = {
      ...data,
      category: categoryValue || 'other',
      credit_limit: data.credit_limit ? parseFloat(data.credit_limit) : null,
      rating: ratingValue ? parseInt(ratingValue) : null,
    };
    
    // Remove empty string values
    Object.keys(formattedData).forEach(key => {
      if (formattedData[key] === '') {
        formattedData[key] = null;
      }
    });
    
    onSubmit(formattedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Supplier Name *</Label>
          <Input
            id="name"
            {...register("name", { required: "Supplier name is required" })}
            placeholder="Enter supplier name"
          />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">
              {typeof errors.name.message === 'string' ? errors.name.message : 'This field is required'}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="contact_person">Contact Person</Label>
          <Input
            id="contact_person"
            {...register("contact_person")}
            placeholder="Enter contact person name"
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            {...register("phone")}
            placeholder="Enter phone number"
          />
        </div>
        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            placeholder="Enter email address"
          />
        </div>
      </div>

      {/* Business Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="category">Category</Label>
          <Select value={categoryValue} onValueChange={setCategoryValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="parts">Parts</SelectItem>
              <SelectItem value="tools">Tools</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
              <SelectItem value="lubricants">Lubricants</SelectItem>
              <SelectItem value="electrical">Electrical</SelectItem>
              <SelectItem value="bodywork">Bodywork</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="gst_number">GST Number</Label>
          <Input
            id="gst_number"
            {...register("gst_number")}
            placeholder="Enter GST number"
          />
        </div>
        <div>
          <Label htmlFor="pan_number">PAN Number</Label>
          <Input
            id="pan_number"
            {...register("pan_number")}
            placeholder="Enter PAN number"
          />
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="address_line1">Address Line 1</Label>
          <Input
            id="address_line1"
            {...register("address_line1")}
            placeholder="Enter address line 1"
          />
        </div>
        <div>
          <Label htmlFor="address_line2">Address Line 2</Label>
          <Input
            id="address_line2"
            {...register("address_line2")}
            placeholder="Enter address line 2 (optional)"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              {...register("city")}
              placeholder="Enter city"
            />
          </div>
          <div>
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              {...register("state")}
              placeholder="Enter state"
            />
          </div>
          <div>
            <Label htmlFor="state_code">State Code (GST)</Label>
            <Input
              id="state_code"
              {...register("state_code")}
              placeholder="e.g. 32"
              maxLength={2}
              data-testid="supplier-state-code-input"
            />
          </div>
          <div>
            <Label htmlFor="postal_code">Postal Code</Label>
            <Input
              id="postal_code"
              {...register("postal_code")}
              placeholder="Enter postal code"
            />
          </div>
          <div>
            <Label htmlFor="country">Country</Label>
            <Input
              id="country"
              {...register("country")}
              placeholder="Enter country"
            />
          </div>
        </div>
      </div>

      {/* Financial Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="payment_terms">Payment Terms</Label>
          <Input
            id="payment_terms"
            {...register("payment_terms")}
            placeholder="e.g., Net 30, COD, etc."
          />
        </div>
        <div>
          <Label htmlFor="credit_limit">Credit Limit (₹)</Label>
          <Input
            id="credit_limit"
            type="number"
            step="0.01"
            {...register("credit_limit")}
            placeholder="Enter credit limit"
          />
        </div>
        <div>
          <Label htmlFor="rating">Rating (1-5)</Label>
          <Select value={ratingValue} onValueChange={setRatingValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 Star</SelectItem>
              <SelectItem value="2">2 Stars</SelectItem>
              <SelectItem value="3">3 Stars</SelectItem>
              <SelectItem value="4">4 Stars</SelectItem>
              <SelectItem value="5">5 Stars</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Additional Information */}
      <div>
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          type="url"
          {...register("website")}
          placeholder="Enter website URL"
        />
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Enter any additional notes about this supplier"
          rows={3}
        />
      </div>

      {/* Submit Buttons */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
          Update Supplier
        </Button>
      </div>
    </form>
  );
};

// ============ Suppliers Section Component ============
const SuppliersSection = ({ onUpdate }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statistics, setStatistics] = useState({});
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  // Supplier ledger (party statement) state
  const [showLedgerDialog, setShowLedgerDialog] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerSupplier, setLedgerSupplier] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showPayPODialog, setShowPayPODialog] = useState(false);
  const [showAgingReport, setShowAgingReport] = useState(false);
  const [ledgerStart, setLedgerStart] = useState(() => {
    const d = new Date();
    return toLocalISODate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [ledgerEnd, setLedgerEnd] = useState(() => toLocalISODate(new Date()));

  const handleViewSupplierLedger = async (supplier, start = ledgerStart, end = ledgerEnd) => {
    if (!supplier) return;
    setLedgerLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      const res = await axios.get(`${API}/suppliers/${supplier.id}/statement?${params.toString()}`);
      setLedgerData(res.data);
      setLedgerSupplier(supplier);
      setLedgerStart(start);
      setLedgerEnd(end);
      setShowLedgerDialog(true);
    } catch (e) {
      console.error('Error loading supplier ledger:', e);
      toast.error('Failed to load supplier ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  // Fetch suppliers data
  const fetchSuppliers = async () => {
    try {
      const response = await apiFetch(`${API}/suppliers`);
      if (response.ok) {
        const suppliersData = await response.json();
        setSuppliers(suppliersData);
        setFilteredSuppliers(suppliersData);
      } else {
        toast.error('Failed to fetch suppliers');
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Error fetching suppliers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch supplier statistics
  const fetchStatistics = async () => {
    try {
      const response = await apiFetch(`${API}/suppliers/stats`);
      if (response.ok) {
        const stats = await response.json();
        setStatistics(stats);
      }
    } catch (error) {
      console.error('Error fetching supplier statistics:', error);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchStatistics();
  }, []);

  // Filter suppliers
  useEffect(() => {
    let filtered = suppliers;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(supplier =>
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(supplier => supplier.category === categoryFilter);
    }

    setFilteredSuppliers(filtered);
  }, [suppliers, searchTerm, categoryFilter]);

  // Handle add supplier
  const handleAddSupplier = async (supplierData) => {
    try {
      const response = await apiFetch(`${API}/suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(supplierData),
      });

      if (response.ok) {
        const newSupplier = await response.json();
        setSuppliers(prev => [...prev, newSupplier]);
        setShowAddDialog(false);
        toast.success(`Supplier "${newSupplier.name}" added successfully`);
        fetchStatistics(); // Refresh statistics
      } else {
        const errorData = await response.json();
        
        // Handle complex error objects (like validation errors)
        let errorMessage = 'Failed to add supplier';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle array of validation errors
            errorMessage = errorData.detail.map(err => {
              if (typeof err === 'string') return err;
              if (err && typeof err === 'object' && err.msg) return err.msg;
              if (err && typeof err === 'object' && err.message) return err.message;
              return 'Validation error';
            }).join(', ');
          } else if (errorData.detail && typeof errorData.detail === 'object') {
            // Handle single validation error object
            if (errorData.detail.msg) {
              errorMessage = errorData.detail.msg;
            } else if (errorData.detail.message) {
              errorMessage = errorData.detail.message;
            } else {
              errorMessage = 'Validation error occurred';
            }
          } else {
            errorMessage = 'Validation error occurred';
          }
        }
        
        console.error('Supplier creation error:', errorData);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error adding supplier:', error);
      toast.error('Error adding supplier');
    }
  };

  // Handle edit supplier
  const handleEditSupplier = async (supplierData) => {
    try {
      const response = await apiFetch(`${API}/suppliers/${selectedSupplier.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(supplierData),
      });

      if (response.ok) {
        const updatedSupplier = await response.json();
        setSuppliers(prev => prev.map(s => s.id === updatedSupplier.id ? updatedSupplier : s));
        setShowEditDialog(false);
        setSelectedSupplier(null);
        toast.success(`Supplier "${updatedSupplier.name}" updated successfully`);
        fetchStatistics(); // Refresh statistics
      } else {
        const errorData = await response.json();
        
        // Handle complex error objects (like validation errors)
        let errorMessage = 'Failed to update supplier';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle array of validation errors
            errorMessage = errorData.detail.map(err => {
              if (typeof err === 'string') return err;
              if (err && typeof err === 'object' && err.msg) return err.msg;
              if (err && typeof err === 'object' && err.message) return err.message;
              return 'Validation error';
            }).join(', ');
          } else if (errorData.detail && typeof errorData.detail === 'object') {
            // Handle single validation error object
            if (errorData.detail.msg) {
              errorMessage = errorData.detail.msg;
            } else if (errorData.detail.message) {
              errorMessage = errorData.detail.message;
            } else {
              errorMessage = 'Validation error occurred';
            }
          } else {
            errorMessage = 'Validation error occurred';
          }
        }
        
        console.error('Supplier update error:', errorData);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      toast.error('Error updating supplier');
    }
  };

  // Handle delete supplier
  const handleDeleteSupplier = async () => {
    try {
      const response = await apiFetch(`${API}/suppliers/${selectedSupplier.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuppliers(prev => prev.filter(s => s.id !== selectedSupplier.id));
        setShowDeleteDialog(false);
        setSelectedSupplier(null);
        toast.success(`Supplier "${selectedSupplier.name}" deleted successfully`);
        fetchStatistics(); // Refresh statistics
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to delete supplier');
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Error deleting supplier');
    }
  };

  // Open edit dialog
  const openEditDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setShowEditDialog(true);
  };

  // Open delete dialog
  const openDeleteDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setShowDeleteDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Suppliers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Suppliers Management</h2>
          <p className="text-muted-foreground">Manage your suppliers and vendor relationships</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAgingReport(true)} data-testid="open-aging-report-btn">
            Aging Report
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Add New Supplier
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Suppliers</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.total_suppliers || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Categories</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Object.keys(statistics.category_breakdown || {}).length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Credits</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.suppliers_with_pending_credits || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.total_transactions || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search suppliers by name, contact person, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="parts">Parts</SelectItem>
              <SelectItem value="tools">Tools</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
              <SelectItem value="lubricants">Lubricants</SelectItem>
              <SelectItem value="electrical">Electrical</SelectItem>
              <SelectItem value="bodywork">Bodywork</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Suppliers List */}
      <div className="grid gap-4">
        {filteredSuppliers.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Truck className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Suppliers Found</h3>
              <p className="text-gray-500 text-center mb-4">
                {searchTerm || categoryFilter !== 'all' 
                  ? "Try adjusting your search or filter criteria"
                  : "Add your first supplier to get started"
                }
              </p>
              {!searchTerm && categoryFilter === 'all' && (
                <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="mr-2 h-4 w-4" />
                  Add First Supplier
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredSuppliers.map((supplier) => (
            <Card key={supplier.id} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-lg font-semibold">{supplier.name}</h3>
                    <Badge variant="outline" className="capitalize">
                      {supplier.category || 'Other'}
                    </Badge>
                    {supplier.rating && (
                      <div className="flex items-center">
                        <span className="text-yellow-400">{'★'.repeat(supplier.rating)}</span>
                        <span className="text-gray-300">{'★'.repeat(5 - supplier.rating)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Contact Person</p>
                      <p className="font-medium">{supplier.contact_person || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Phone</p>
                      <p className="font-medium">{supplier.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Email</p>
                      <p className="font-medium">{supplier.email || 'Not provided'}</p>
                    </div>
                  </div>

                  {supplier.address_line1 && (
                    <div className="mt-3">
                      <p className="text-gray-500 text-sm">Address</p>
                      <p className="text-sm">
                        {supplier.address_line1}
                        {supplier.address_line2 && `, ${supplier.address_line2}`}
                        {supplier.city && `, ${supplier.city}`}
                        {supplier.state && `, ${supplier.state}`}
                        {supplier.postal_code && ` - ${supplier.postal_code}`}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mt-3">
                    {supplier.gst_number && (
                      <div>
                        <p className="text-gray-500">GST Number</p>
                        <p className="font-medium">{supplier.gst_number}</p>
                      </div>
                    )}
                    {supplier.payment_terms && (
                      <div>
                        <p className="text-gray-500">Payment Terms</p>
                        <p className="font-medium">{supplier.payment_terms}</p>
                      </div>
                    )}
                    {supplier.credit_limit && (
                      <div>
                        <p className="text-gray-500">Credit Limit</p>
                        <p className="font-medium">₹{supplier.credit_limit.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewSupplierLedger(supplier)}
                    disabled={ledgerLoading}
                    className="text-blue-600 hover:text-blue-700"
                    data-testid={`supplier-ledger-btn-${supplier.id}`}
                  >
                    <FileText className="h-4 w-4 mr-1" /> Ledger
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(supplier)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeleteDialog(supplier)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Add Supplier Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Supplier</DialogTitle>
          </DialogHeader>
          <AddSupplierForm
            onSubmit={handleAddSupplier}
            onCancel={() => setShowAddDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Supplier Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Supplier</DialogTitle>
          </DialogHeader>
          {selectedSupplier && (
            <EditSupplierForm
              supplier={selectedSupplier}
              onSubmit={handleEditSupplier}
              onCancel={() => {
                setShowEditDialog(false);
                setSelectedSupplier(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* Supplier Ledger Dialog */}
      <Dialog open={showLedgerDialog} onOpenChange={(o) => { if (!o) { setShowLedgerDialog(false); setLedgerData(null); setLedgerSupplier(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Supplier Statement - {ledgerSupplier?.name}</DialogTitle>
            <DialogDescription>Party ledger with running balance, built from this supplier's purchase orders and payments</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end -mt-2 mb-1">
            <Button size="sm" onClick={() => setShowPayPODialog(true)} data-testid="open-pay-po-btn">
              Pay Purchase Orders
            </Button>
          </div>

          {ledgerData && ledgerData.aging && (
            <div className="border rounded-lg p-3 mb-2 bg-amber-50/50" data-testid="supplier-aging-section">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-800">Ageing — Outstanding Payables</h4>
                <span className="text-xs text-gray-500">as of {ledgerData.aging.as_of}</span>
              </div>
              {ledgerData.aging.total > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {[
                      { k: '0-30', label: '0–30 days', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
                      { k: '31-60', label: '31–60 days', cls: 'text-yellow-700 bg-yellow-50 border-yellow-200' },
                      { k: '61-90', label: '61–90 days', cls: 'text-orange-700 bg-orange-50 border-orange-200' },
                      { k: '90+', label: '90+ days', cls: 'text-red-700 bg-red-50 border-red-200' },
                    ].map(b => (
                      <div key={b.k} className={`rounded-md border p-2 text-center ${b.cls}`} data-testid={`aging-bucket-${b.k}`}>
                        <div className="text-[11px] font-medium">{b.label}</div>
                        <div className="text-sm font-bold">₹{(ledgerData.aging.buckets[b.k] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    ))}
                    <div className="rounded-md border p-2 text-center bg-gray-800 text-white" data-testid="aging-bucket-total">
                      <div className="text-[11px] font-medium opacity-90">Total Due</div>
                      <div className="text-sm font-bold">₹{(ledgerData.aging.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  {ledgerData.aging.rows && ledgerData.aging.rows.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto border rounded-md bg-white" data-testid="aging-rows">
                      <div className="grid grid-cols-12 px-2 py-1 text-[11px] font-semibold bg-gray-50 border-b sticky top-0">
                        <span className="col-span-4">Reference</span>
                        <span className="col-span-3">Bill Date</span>
                        <span className="col-span-2 text-right">Age (days)</span>
                        <span className="col-span-3 text-right">Amount</span>
                      </div>
                      {ledgerData.aging.rows.map((r, i) => (
                        <div key={i} className="grid grid-cols-12 px-2 py-1 text-xs border-t items-center">
                          <span className="col-span-4 truncate" title={r.description}>{r.reference}</span>
                          <span className="col-span-3 text-gray-500">{r.bill_date || '—'}</span>
                          <span className={`col-span-2 text-right font-medium ${r.ageing_days > 90 ? 'text-red-600' : r.ageing_days > 60 ? 'text-orange-600' : 'text-gray-700'}`}>{r.ageing_days}</span>
                          <span className="col-span-3 text-right font-medium">₹{(r.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-500">No outstanding payables — nothing to age.</p>
              )}
            </div>
          )}

          {ledgerData && (
            <LedgerStatement
              key={`${ledgerStart}-${ledgerEnd}`}
              statementData={ledgerData}
              title="SUPPLIER STATEMENT"
              partyLabel="Supplier"
              startDate={ledgerStart}
              endDate={ledgerEnd}
              loading={ledgerLoading}
              onApplyFilter={(s, e) => handleViewSupplierLedger(ledgerSupplier, s, e)}
              onClose={() => { setShowLedgerDialog(false); setLedgerData(null); setLedgerSupplier(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {ledgerSupplier && (
        <SupplierPayPODialog
          open={showPayPODialog}
          onOpenChange={setShowPayPODialog}
          supplier={ledgerSupplier}
          onPaid={() => handleViewSupplierLedger(ledgerSupplier, ledgerStart, ledgerEnd)}
        />
      )}

      <SupplierAgingReport open={showAgingReport} onOpenChange={setShowAgingReport} />

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Supplier</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedSupplier?.name}"? This action cannot be undone.
              Any pending transactions with this supplier will be affected.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedSupplier(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSupplier}
            >
              Delete Supplier
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Add Purchase Order Form Component
const EngineProceduresSection = () => {
  const [procedures, setProcedures] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { title: '', engine_code: '', make: '', model: '', series: '', transmission: '', procedure_type: 'rebuild', description: '' }
  });
  const [customPhases, setCustomPhases] = useState([]);
  const [procPhotos, setProcPhotos] = useState([]);
  const [procVideos, setProcVideos] = useState([]);
  const [templateMake, setTemplateMake] = useState('');
  const [templateModel, setTemplateModel] = useState('');
  const [templateSeries, setTemplateSeries] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchProcedures = async () => {
    try {
      const response = await apiFetch(`${backendUrl}/api/engine-procedures`);
      if (response.ok) setProcedures(await response.json());
    } catch (error) {
      console.error('Error fetching procedures:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiFetch(`${backendUrl}/api/engine-procedures/templates`);
      if (response.ok) setTemplates(await response.json());
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    fetchProcedures();
    fetchTemplates();
  }, []);

  const createFromTemplate = async (templateKey) => {
    try {
      const response = await apiFetch(`${backendUrl}/api/engine-procedures/from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_key: templateKey, make: templateMake, model: templateModel, series: templateSeries })
      });
      if (response.ok) {
        const newProc = await response.json();
        toast.success('Procedure created from template!');
        setShowTemplateDialog(false);
        setTemplateMake('');
        setTemplateModel('');
        setTemplateSeries('');
        fetchProcedures();
        setSelectedProcedure(newProc);
      }
    } catch (error) {
      toast.error('Failed to create procedure');
    }
  };

  const createCustomProcedure = async (formData) => {
    // Build phases with IDs for backend
    const phasesPayload = customPhases.map((phase, pIndex) => ({
      name: phase.name,
      order: pIndex + 1,
      tasks: phase.tasks.map((task, tIndex) => ({
        title: task.title,
        description: task.description || '',
        torque_spec: task.torque_spec || '',
        is_critical: task.is_critical || false,
        order: tIndex + 1
      }))
    }));
    try {
      const response = await apiFetch(`${backendUrl}/api/engine-procedures`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, phases: phasesPayload })
      });
      if (response.ok) {
        const newProc = await response.json();
        const procId = newProc.id;

        // Upload photos
        if (procPhotos.length > 0) {
          try {
            const formData = new FormData();
            procPhotos.forEach(f => formData.append('files', f));
            await axios.post(`${backendUrl}/api/engine-procedures/${procId}/upload-photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (err) { console.error('Photo upload failed:', err); }
        }

        // Upload videos
        if (procVideos.length > 0) {
          try {
            toast.info(`Uploading ${procVideos.length} video(s)...`);
            const formData = new FormData();
            procVideos.forEach(f => formData.append('files', f));
            await axios.post(`${backendUrl}/api/engine-procedures/${procId}/upload-videos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (err) { console.error('Video upload failed:', err); }
        }

        const fileCount = procPhotos.length + procVideos.length;
        toast.success(fileCount > 0 ? `Procedure created with ${fileCount} file(s)!` : 'Custom procedure created!');
        setShowCreateDialog(false);
        reset();
        setCustomPhases([]);
        setProcPhotos([]);
        setProcVideos([]);
        fetchProcedures();
        setSelectedProcedure(newProc);
      }
    } catch (error) {
      toast.error('Failed to create procedure');
    }
  };

  // Phase management hooks
  const addPhase = () => {
    setCustomPhases(prev => [...prev, { name: '', tasks: [] }]);
  };

  const updatePhaseName = (phaseIndex, name) => {
    setCustomPhases(prev => prev.map((p, i) => i === phaseIndex ? { ...p, name } : p));
  };

  const removePhase = (phaseIndex) => {
    setCustomPhases(prev => prev.filter((_, i) => i !== phaseIndex));
  };

  // Task management hooks
  const addTask = (phaseIndex) => {
    setCustomPhases(prev => prev.map((p, i) => i === phaseIndex ? { ...p, tasks: [...p.tasks, { title: '', description: '', torque_spec: '', is_critical: false }] } : p));
  };

  const updateTask = (phaseIndex, taskIndex, field, value) => {
    setCustomPhases(prev => prev.map((p, pI) => pI === phaseIndex ? { ...p, tasks: p.tasks.map((t, tI) => tI === taskIndex ? { ...t, [field]: value } : t) } : p));
  };

  const removeTask = (phaseIndex, taskIndex) => {
    setCustomPhases(prev => prev.map((p, i) => i === phaseIndex ? { ...p, tasks: p.tasks.filter((_, tI) => tI !== taskIndex) } : p));
  };

  const deleteProcedure = async (procId) => {
    if (!window.confirm('Are you sure you want to delete this procedure?')) return;
    try {
      await apiFetch(`${backendUrl}/api/engine-procedures/${procId}`, { method: 'DELETE' });
      toast.success('Procedure deleted');
      if (selectedProcedure?.id === procId) setSelectedProcedure(null);
      fetchProcedures();
    } catch (error) {
      toast.error('Failed to delete procedure');
    }
  };

  const toggleTaskStatus = async (procedure, phaseId, taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      const response = await apiFetch(`${backendUrl}/api/engine-procedures/${procedure.id}/task/${phaseId}/${taskId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedProcedure(updated);
        fetchProcedures();
      }
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const getProgress = (proc) => {
    let total = 0, completed = 0;
    (proc.phases || []).forEach(p => (p.tasks || []).forEach(t => { total++; if (t.status === 'completed') completed++; }));
    return { total, completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  // Detail View
  if (selectedProcedure) {
    const progress = getProgress(selectedProcedure);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedProcedure(null)} className="mb-2" data-testid="back-to-procedures">
              <X size={14} className="mr-1" /> Back to Procedures
            </Button>
            <h2 className="text-2xl font-bold tracking-tight">{selectedProcedure.title}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge>{selectedProcedure.engine_code}</Badge>
              <Badge variant="outline">{selectedProcedure.make}</Badge>
              {selectedProcedure.model && <Badge variant="outline">{selectedProcedure.model}</Badge>}
              {selectedProcedure.series && <Badge variant="secondary">{selectedProcedure.series}</Badge>}
              {selectedProcedure.transmission && <Badge variant="secondary">{selectedProcedure.transmission}</Badge>}
            </div>
            {selectedProcedure.description && <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{selectedProcedure.description}</p>}
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{progress.percent}%</div>
            <p className="text-sm text-muted-foreground">{progress.completed}/{progress.total} tasks</p>
            <div className="w-32 h-2 bg-gray-200 rounded-full mt-1">
              <div className="h-2 bg-blue-600 rounded-full transition-all" style={{ width: `${progress.percent}%` }}></div>
            </div>
          </div>
        </div>

        {/* Phases */}
        {(selectedProcedure.phases || []).sort((a, b) => a.order - b.order).map((phase) => {
          const phaseProgress = phase.tasks?.filter(t => t.status === 'completed').length || 0;
          const phaseTotal = phase.tasks?.length || 0;
          return (
            <Card key={phase.id} data-testid={`phase-${phase.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${phaseProgress === phaseTotal && phaseTotal > 0 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {phase.order}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{phase.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{phaseProgress}/{phaseTotal} tasks completed</p>
                    </div>
                  </div>
                  {phaseProgress === phaseTotal && phaseTotal > 0 && (
                    <Badge className="bg-green-100 text-green-700 border-green-300">Complete</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(phase.tasks || []).sort((a, b) => a.order - b.order).map((task) => (
                    <div
                      key={task.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                        task.status === 'completed' ? 'bg-green-50 border-green-200' : task.is_critical ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
                      }`}
                      onClick={() => toggleTaskStatus(selectedProcedure, phase.id, task.id, task.status)}
                      data-testid={`task-${task.id}`}
                    >
                      <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        task.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-gray-300'
                      }`}>
                        {task.status === 'completed' && <Check size={12} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium text-sm ${task.status === 'completed' ? 'line-through text-gray-500' : ''}`}>
                            {task.title}
                          </span>
                          {task.is_critical && <Badge variant="destructive" className="text-xs py-0">Critical</Badge>}
                          {task.torque_spec && (
                            <Badge variant="outline" className="text-xs py-0 bg-amber-50 border-amber-300 text-amber-700">
                              {task.torque_spec}
                            </Badge>
                          )}
                        </div>
                        {task.description && (
                          <p className={`text-xs mt-1 ${task.status === 'completed' ? 'text-gray-400' : 'text-muted-foreground'}`}>
                            {task.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Engine Procedures</h2>
          <p className="text-muted-foreground">Professional step-by-step engine rebuild and service procedures</p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2" data-testid="use-template-btn">
                <ClipboardList size={16} />
                Use Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create from Engine Template</DialogTitle>
                <DialogDescription>Select a professional engine rebuild template. You can customize it after creation.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Make (Optional)</Label>
                    <Input placeholder="e.g., BMW" value={templateMake} onChange={(e) => setTemplateMake(e.target.value.toUpperCase())} />
                  </div>
                  <div>
                    <Label>Model (Optional)</Label>
                    <Input placeholder="e.g., 520d" value={templateModel} onChange={(e) => setTemplateModel(e.target.value.toUpperCase())} />
                  </div>
                  <div>
                    <Label>Series (Optional)</Label>
                    <Input placeholder="e.g., F10" value={templateSeries} onChange={(e) => setTemplateSeries(e.target.value.toUpperCase())} />
                  </div>
                </div>
                <div className="space-y-3">
                  {templates.map(template => (
                    <Card key={template.key} className="cursor-pointer hover:border-blue-400 hover:shadow-md transition-all" onClick={() => createFromTemplate(template.key)} data-testid={`template-${template.key}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">{template.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge>{template.engine_code}</Badge>
                              <Badge variant="outline">{template.make}</Badge>
                              <Badge variant="secondary">{template.transmission}</Badge>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            <div className="text-lg font-bold text-blue-600">{template.phase_count}</div>
                            <p className="text-xs text-muted-foreground">phases</p>
                            <div className="text-lg font-bold text-amber-600">{template.total_tasks}</div>
                            <p className="text-xs text-muted-foreground">tasks</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) { setCustomPhases([]); reset(); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2" data-testid="create-custom-btn">
                <Plus size={16} />
                Custom Procedure
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>Create Custom Procedure</DialogTitle>
                <DialogDescription>Build your own engine procedure with phases and tasks.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(createCustomProcedure)} className="space-y-4 mt-4">
                {/* Basic Info */}
                <div>
                  <Label>Title *</Label>
                  <Input {...register('title', { required: true, setValueAs: v => v.toUpperCase() })} placeholder="e.g., BMW N47 Timing Chain Replacement" data-testid="custom-title" />
                  {errors.title && <p className="text-xs text-red-500 mt-1">Title is required</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Engine Code *</Label>
                    <Input {...register('engine_code', { required: true, setValueAs: v => v.toUpperCase() })} placeholder="e.g., N47" data-testid="custom-engine-code" />
                    {errors.engine_code && <p className="text-xs text-red-500 mt-1">Engine Code is required</p>}
                  </div>
                  <div>
                    <Label>Make *</Label>
                    <Input {...register('make', { required: true, setValueAs: v => v.toUpperCase() })} placeholder="e.g., BMW" data-testid="custom-make" />
                    {errors.make && <p className="text-xs text-red-500 mt-1">Make is required</p>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Model</Label>
                    <Input {...register('model', { setValueAs: v => v.toUpperCase() })} placeholder="e.g., 520d" />
                  </div>
                  <div>
                    <Label>Series</Label>
                    <Input {...register('series', { setValueAs: v => v.toUpperCase() })} placeholder="e.g., F10" />
                  </div>
                  <div>
                    <Label>Transmission</Label>
                    <Input {...register('transmission', { setValueAs: v => v.toUpperCase() })} placeholder="e.g., ZF8HP" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <select
                      {...register('procedure_type')}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="custom-type"
                    >
                      <option value="rebuild">Full Rebuild</option>
                      <option value="timing_chain">Timing Chain</option>
                      <option value="service">Service</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input {...register('description')} placeholder="Brief description..." />
                  </div>
                </div>

                {/* Phases & Tasks Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-sm">Phases & Tasks</h3>
                      <p className="text-xs text-muted-foreground">Add work phases and their step-by-step tasks</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addPhase} data-testid="add-phase-btn">
                      <Plus size={14} className="mr-1" /> Add Phase
                    </Button>
                  </div>

                  {customPhases.length === 0 && (
                    <div className="text-center py-6 border border-dashed rounded-lg text-muted-foreground">
                      <ClipboardList className="mx-auto h-8 w-8 mb-2 opacity-40" />
                      <p className="text-sm">No phases added yet. Click "Add Phase" to start building your procedure.</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {customPhases.map((phase, pIndex) => (
                      <Card key={pIndex} className="border-l-4 border-l-blue-400" data-testid={`custom-phase-${pIndex}`}>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{pIndex + 1}</div>
                            <Input
                              placeholder={`Phase name (e.g., Preparation & Teardown)`}
                              value={phase.name}
                              onChange={(e) => updatePhaseName(pIndex, e.target.value)}
                              className="flex-1 font-medium"
                              data-testid={`phase-name-${pIndex}`}
                            />
                            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600" onClick={() => removePhase(pIndex)} data-testid={`remove-phase-${pIndex}`}>
                              <Trash2 size={14} />
                            </Button>
                          </div>

                          {/* Tasks */}
                          <div className="ml-9 space-y-2">
                            {phase.tasks.map((task, tIndex) => (
                              <div key={tIndex} className="flex items-start gap-2 p-2 bg-gray-50 rounded-md" data-testid={`custom-task-${pIndex}-${tIndex}`}>
                                <div className="flex-1 space-y-2">
                                  <Input
                                    placeholder="Task title (e.g., Remove engine assembly)"
                                    value={task.title}
                                    onChange={(e) => updateTask(pIndex, tIndex, 'title', e.target.value)}
                                    className="text-sm h-8"
                                    data-testid={`task-title-${pIndex}-${tIndex}`}
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      placeholder="Description (optional)"
                                      value={task.description}
                                      onChange={(e) => updateTask(pIndex, tIndex, 'description', e.target.value)}
                                      className="text-xs h-7"
                                    />
                                    <Input
                                      placeholder="Torque spec (e.g., 30 Nm + 90)"
                                      value={task.torque_spec}
                                      onChange={(e) => updateTask(pIndex, tIndex, 'torque_spec', e.target.value)}
                                      className="text-xs h-7"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                                      <input
                                        type="checkbox"
                                        checked={task.is_critical}
                                        onChange={(e) => updateTask(pIndex, tIndex, 'is_critical', e.target.checked)}
                                        className="rounded border-red-300 text-red-600 focus:ring-red-500"
                                      />
                                      <span className="text-red-600 font-medium">Critical Task</span>
                                    </label>
                                  </div>
                                </div>
                                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 flex-shrink-0 mt-0.5" onClick={() => removeTask(pIndex, tIndex)} data-testid={`remove-task-${pIndex}-${tIndex}`}>
                                  <X size={12} />
                                </Button>
                              </div>
                            ))}
                            <Button type="button" variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 h-7 text-xs" onClick={() => addTask(pIndex)} data-testid={`add-task-${pIndex}`}>
                              <Plus size={12} className="mr-1" /> Add Task
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                {customPhases.length > 0 && (
                  <div className="flex items-center gap-4 text-xs text-muted-foreground bg-gray-50 p-2 rounded">
                    <span>{customPhases.length} phase{customPhases.length !== 1 ? 's' : ''}</span>
                    <span>{customPhases.reduce((sum, p) => sum + p.tasks.length, 0)} task{customPhases.reduce((sum, p) => sum + p.tasks.length, 0) !== 1 ? 's' : ''}</span>
                    <span>{customPhases.reduce((sum, p) => sum + p.tasks.filter(t => t.is_critical).length, 0)} critical</span>
                  </div>
                )}

                {/* File Uploads */}
                <div className="space-y-3 border-t pt-4">
                  <Label className="text-base font-medium">Attachments</Label>
                  <div className="border rounded-lg p-3 bg-cyan-50">
                    <div className="flex space-x-2">
                      <Input value={procPhotos.map(f => f.name).join(', ')} placeholder="No photos" readOnly className="flex-1 bg-white text-sm" />
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={el => window.procPhotoInput = el}
                        onChange={(e) => { setProcPhotos(Array.from(e.target.files)); toast.success(`${e.target.files.length} photo(s) selected`); }} />
                      <Button type="button" variant="outline" size="sm" className="bg-green-50 text-green-700 border-green-300" onClick={() => window.procPhotoInput?.click()}>
                        <Camera className="h-3 w-3 mr-1" /> Photos
                      </Button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-3 bg-purple-50">
                    <div className="flex space-x-2">
                      <Input value={procVideos.map(f => f.name).join(', ')} placeholder="No videos" readOnly className="flex-1 bg-white text-sm" />
                      <input type="file" accept="video/mp4,video/mov,video/avi,video/wmv,video/mkv,video/webm" multiple style={{ display: 'none' }} ref={el => window.procVideoInput = el}
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          if (files.length > 10) { toast.error('Max 10 videos'); return; }
                          if (files.some(f => f.size > 100 * 1024 * 1024)) { toast.error('Max 100MB per video'); return; }
                          setProcVideos(files); toast.success(`${files.length} video(s) selected`);
                        }} />
                      <Button type="button" variant="outline" size="sm" className="bg-purple-100 text-purple-700 border-purple-300" onClick={() => window.procVideoInput?.click()}>
                        <Plus className="h-3 w-3 mr-1" /> Videos
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">MP4, MOV, AVI, WMV, MKV, WebM (max 100MB)</p>
                    {procVideos.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {procVideos.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white rounded p-1 border text-xs">
                            <span className="text-purple-700 truncate">{file.name} ({(file.size / (1024 * 1024)).toFixed(1)} MB)</span>
                            <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-500" onClick={() => setProcVideos(prev => prev.filter((_, i) => i !== idx))}><X className="h-3 w-3" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="create-custom-submit">
                  Create Procedure {customPhases.length > 0 && `(${customPhases.length} phases, ${customPhases.reduce((s, p) => s + p.tasks.length, 0)} tasks)`}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Procedures</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{procedures.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Activity className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{procedures.filter(p => { const pr = getProgress(p); return pr.completed > 0 && pr.completed < pr.total; }).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{procedures.filter(p => { const pr = getProgress(p); return pr.total > 0 && pr.completed === pr.total; }).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Templates</CardTitle>
            <Database className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{templates.length}</div>
            <p className="text-xs text-muted-foreground">Available templates</p>
          </CardContent>
        </Card>
      </div>

      {/* Procedures List */}
      {procedures.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ClipboardList className="h-16 w-16 text-muted-foreground mb-4 opacity-30" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Procedures Yet</h3>
            <p className="text-muted-foreground mb-6 text-center max-w-md">
              Create your first engine procedure using a professional template (BMW N47, B47, Mercedes OM651) or start from scratch.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => setShowTemplateDialog(true)}>
                <ClipboardList size={16} className="mr-2" /> Use Template
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(true)}>
                <Plus size={16} className="mr-2" /> Custom Procedure
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {procedures.map(proc => {
            const progress = getProgress(proc);
            return (
              <Card key={proc.id} className="cursor-pointer hover:shadow-lg transition-all hover:border-blue-300" onClick={() => setSelectedProcedure(proc)} data-testid={`procedure-card-${proc.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">{proc.title}</h3>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <Badge className="text-xs">{proc.engine_code}</Badge>
                        <Badge variant="outline" className="text-xs">{proc.make}</Badge>
                        {proc.model && <Badge variant="secondary" className="text-xs">{proc.model}</Badge>}
                        {proc.transmission && <Badge variant="secondary" className="text-xs">{proc.transmission}</Badge>}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 flex-shrink-0" onClick={(e) => { e.stopPropagation(); deleteProcedure(proc.id); }} data-testid={`delete-proc-${proc.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>{progress.completed}/{progress.total} tasks</span>
                      <span className="font-semibold">{progress.percent}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full">
                      <div className={`h-2 rounded-full transition-all ${progress.percent === 100 ? 'bg-green-500' : progress.percent > 0 ? 'bg-blue-500' : 'bg-gray-300'}`} style={{ width: `${progress.percent}%` }}></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{proc.phases?.length || 0} phases</span>
                    <span>{proc.procedure_type}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default App;